import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  VideoCameraIcon
} from "@heroicons/react/24/solid";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactPlayer from "react-player/lazy";
import { useLocation, useNavigate } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/Language/LanguageContext";
import { storeSession } from "../../context/VBLiveAPI/VBLiveAPIAction";
import { SquadPicker } from "../../pages/NewMatch";
import { useAuthStatus } from "../hooks/useAuthStatus";
import EventEditor from "../layout/EventEditor";
import {
  getRotationLabels,
  kRotationSetterPositions,
} from "../utils/Constants";
import { buildDVWMatchText } from "../utils/DVWWriter";
import { generateUUID } from "../utils/Utils";
import DrawAndCodeCourt from "./DrawAndCodeCourt";

/**
 * DVMatchCoding
 * --------------------------------------------------
 * Code a volleyball match using the DataVolley (DVW) shorthand notation.
 *
 * Code format (per touch):
 *   [team][NN][skill][type?][evaluation][zoneFrom?zoneTo?]
 *
 *   team       : "*" = team A (home),  "a" = team B (away)
 *   NN         : 2-digit jersey number  (e.g. 01, 12)
 *   skill      : S=Serve  R=Reception  E=Setter Call  A=Attack  B=Block  D=Dig  F=Freeball
 *   type       : (optional) one char – serve/attack type (H,M,Q,T,U,...)
 *   evaluation : "#"=perfect/kill/ace  "+"=positive  "!"=ok  "-"=negative
 *                "/"=over-pass / blocked  "="=error
 *   zoneFrom?  : (optional) 1 digit  (1-9)
 *   zoneTo?    : (optional) 1 digit  (1-9)
 *
 * Examples
 *   *01SH#       Team A, #1, jump-serve, ACE                         (point A)
 *   a07RH+15     Team B, #7, reception of jump-serve, positive, 1->5
 *   *11AQ#42     Team A, #11, quick attack, KILL, zone 4 -> 2        (point A)
 *   a02A=        Team B, #2, attack error                            (point A)
 *   *13B#        Team A, #13, stuff block                            (point A)
 *
 * Score and rotation are updated automatically on terminal events
 * (#, = on serve / attack / block).
 */

const SKILL_LABELS = {
  S: "Serve",
  R: "Reception",
  E: "Setter Call",
  A: "Attack",
  B: "Block",
  D: "Dig",
  F: "Freeball",
  P: "Point", // manual rally winner
};

// Reception eval -> Serve eval mapping (DataVolley convention).
// A perfect reception means a poor / over-attempted serve, and vice versa.
const R_TO_S_EVAL = {
  "#": "-", // perfect reception => poor serve
  "+": "/",
  "!": "-",
  "-": "!",
  "/": "+",
  "=": "#", // reception error => serve ace
};

const EVAL_LABELS = {
  "#": "Perfect / Kill / Ace",
  "+": "Positive",
  "!": "OK",
  "-": "Negative",
  "/": "Over-pass / Blocked",
  "=": "Error",
};

const EVAL_COLORS = {
  "#": "text-success",
  "+": "text-success/80",
  "!": "text-base-content",
  "-": "text-warning",
  "/": "text-warning",
  "=": "text-error",
};

const SETS_TO_WIN = 3;
const REGULAR_SET_POINTS = 25;
const TIEBREAK_POINTS = 15;

// ---- Compound command support ----------------------------------------------
//
// A "compound" command is a single input string that expands into one or more
// DV events.  Three flavours are supported:
//
//   1. Chained tokens          – several events typed back-to-back, each
//                                starting with a team marker (* or a):
//                                  *01SH+a06RH#*05ET+a04AH#
//
//   2. Two-event shorthand     – after the first event, subsequent events may
//                                omit the team marker, in which case the
//                                previously-used team is inherited:
//                                  a06RH+5ET+   (R by team B player 06,
//                                                followed by E by team B
//                                                player 05)
//
//   3. Macros / aliases        – short keywords that expand to a templated
//                                code before tokenising:
//                                  pa  -> award point to team A
//                                  pb  -> award point to team B
//                                  sa  -> serve ace by current server   (#)
//                                  se  -> serve error by current server (=)
//                                  s+  -> serve "+"  /  s!  -> "!"  etc.
//
// Aliases recognise placeholders <serverA> / <serverB> that are filled with
// the jersey number of the player currently at position 1 of that team.

const SKILL_CHARS = new Set(["S", "R", "E", "A", "B", "D", "F", "P"]);
const EVAL_CHARS = new Set(["#", "+", "!", "-", "/", "="]);
// Type characters — used to detect skill-omitted tokens like "4Q-" (implied attack).
const TYPE_CHARS = new Set(["H", "M", "Q", "T", "U", "N", "O"]);

// ---------------------------------------------------------------------------
// Default Scouting parameters (mirrors DataVolley's "Default Scouting" window)
// ---------------------------------------------------------------------------
// DEFAULT_SKILL : skill assumed when only jersey [+ type/eval] is typed.
// DEFAULT_TYPE  : per-skill type inserted when type is absent.
// DEFAULT_EVAL  : per-skill evaluation inserted when eval is absent.
const DEFAULT_SKILL = "A";
const DEFAULT_TYPE = {
  A: "H",
  S: "Q",
  R: "Q",
  E: "",
  B: "",
  D: "",
  F: "",
  P: "",
};
const DEFAULT_EVAL = {
  A: "+",
  S: "+",
  R: "+",
  E: "+",
  B: "+",
  D: "+",
  F: "+",
  P: "+",
};
// Seconds of video to play *before* an event's timestamp when seeking, so the
// action leading up to the event is visible.
const DEFAULT_VIDEO_LEAD_TIME = 4;
// Substitution defaults (overridable in the Coding defaults modal).
const DEFAULT_MAX_SUBS = 6; // FIVB standard: 6 substitutions per set per team
const DEFAULT_FREE_SUBS = false; // true = ignore FIVB sub-pair / re-entry rules
// Set-scoring defaults (overridable in the Coding defaults modal), so matches
// that don't follow standard FIVB rules (e.g. shortened/practice sets) can
// still have their sets auto-detected as won.
const DEFAULT_SET_WIN_SCORE = REGULAR_SET_POINTS; // points to win sets 1-4
const DEFAULT_FINAL_SET_WIN_SCORE = TIEBREAK_POINTS; // points to win the deciding set
const DEFAULT_WIN_BY_TWO = true; // require a 2-point winning margin
// Maximum players a team may dress for a match (FIVB match-sheet limit). Only
// this "match squad" (chosen in the setup modal) is offered as substitutes;
// more can be pulled from the full roster until the cap is reached.
const MAX_MATCH_SQUAD = 14;
const defaultMatchSquad = (roster) => {
  const withShirt = (roster ?? [])
    .filter((p) => {
      const s = p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber;
      return s != null && String(s).trim() !== "";
    })
    .sort(
      (a, b) =>
        parseInt(a.shirtNumber ?? a.ShirtNumber ?? a.playerNumber ?? 0, 10) -
        parseInt(b.shirtNumber ?? b.ShirtNumber ?? b.playerNumber ?? 0, 10),
    );
  const chosen =
    withShirt.length > MAX_MATCH_SQUAD
      ? withShirt.slice(0, MAX_MATCH_SQUAD)
      : withShirt;
  return new Set(
    chosen.map((p) =>
      String(
        parseInt(String(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber), 10),
      ),
    ),
  );
};

// ---------------------------------------------------------------------------
// Keyboard shortcuts
// ---------------------------------------------------------------------------
const DEFAULT_SHORTCUTS = {
  pointHome: { key: ",", ctrl: true, shift: false, alt: false },
  pointAway: { key: ".", ctrl: true, shift: false, alt: false },
  undoLast: { key: "a", ctrl: true, shift: false, alt: false },
  redoLast: { key: "d", ctrl: true, shift: false, alt: false },
};
const SHORTCUT_LABELS = {
  pointHome: "Point home",
  pointAway: "Point away",
  undoLast: "Undo last event",
  redoLast: "Redo last event",
};

function formatShortcut({ key, ctrl, shift, alt } = {}) {
  if (!key) return "—";
  const parts = [];
  if (ctrl) parts.push("Ctrl");
  if (alt) parts.push("Alt");
  if (shift) parts.push("Shift");
  const display =
    key === " " ? "Space" : key.length === 1 ? key.toUpperCase() : key;
  parts.push(display);
  return parts.join("+");
}

// ---------------------------------------------------------------------------
// DataVolley compound dot-coding support
// ---------------------------------------------------------------------------
//
//   Serve → Reception  (S.R)
//     5SQ1.3-5   →  *05SQ+15  a03RQ-15
//     6SQ.8=     →  *06SQ#    a08RQ=
//     a5S.6      →  a05SQ-    *06RQ+
//
//   Attack → Block  (A.B)
//     a3Q.4#     →  a03AQ/    *04B#
//
// Skill omission (jersey + type/eval → DEFAULT_SKILL inferred):
//   4Q-   →  *04AQ-   (jersey + type + eval)
//   a3-   →  a03AH-   (jersey + eval → default type for skill)
//   14T#  →  *14AT#
//   4Q    →  *04AQ+   (eval absent → DEFAULT_EVAL)

const DOT_REC_TO_SERVE_EVAL = {
  "=": "#", // reception error   → serve ace
  "/": "+", // overpass          → positive serve
  "-": "+", // negative rec      → positive serve
  "!": "!", // ok rec            → ok serve
  "+": "-", // positive rec      → negative serve
  "#": "-", // perfect rec       → negative serve
};

const DOT_BLOCK_TO_ATTACK_EVAL = {
  "#": "/", // block winner  → attack blocked
  "+": "-", // positive block → negative attack
  "!": "!", // ok block       → ok attack
  "-": "+", // negative block → positive attack
  "/": "+", // block touch    → positive attack
  "=": "#", // block miss     → attack winner
};

// Serve → reception dot pair: e.g. 5SQ1.3-5
const COMPOUND_SRV_DOT_RE =
  /([*aA]?)(\d{1,2})([Ss])([HMQhmq]?)(\d[a-dA-D]?|)\.([*aA]?)(?:(\d{1,2})[Rr]?|[Rr])([#+!\-/=]?)(\d[a-dA-D]?|)/g;

// Attack → block dot pair: e.g. a3Q.4#  (no explicit skill letter on attack side)
const COMPOUND_ATK_DOT_RE =
  /([*aA]?)(\d{1,2})([HMQTUNOhmqtuno])(\d[a-dA-D]?|)\.([*aA]?)(\d{1,2})([#+!\-/=]?)(\d[a-dA-D]?|)/g;

// Expand all compound dot tokens into pairs of fully-formed standard codes.
// Two passes: serve→reception first, then attack→block on the result.
function expandCompoundDots(s, { liberoA, liberoB } = {}) {
  // Pass 1: serve → reception
  let result = s.replace(
    COMPOUND_SRV_DOT_RE,
    (
      _m,
      sTeamRaw,
      sJersey,
      _skill,
      sType,
      zFrom,
      rTeamRaw,
      rJersey,
      rEval,
      zTo,
    ) => {
      const sTeam = sTeamRaw === "a" || sTeamRaw === "A" ? "a" : "*";
      const rTeam = rTeamRaw
        ? rTeamRaw === "a" || rTeamRaw === "A"
          ? "a"
          : "*"
        : sTeam === "*"
          ? "a"
          : "*";
      // When jersey is omitted (bare 'R'), use the assigned libero for that team.
      let recJersey = rJersey;
      if (!recJersey) {
        const lib =
          rTeam === "a" ? primaryLibero(liberoB) : primaryLibero(liberoA);
        recJersey = lib != null ? String(lib) : "00";
      }
      const recEval = rEval || "+";
      const serveEval = DOT_REC_TO_SERVE_EVAL[recEval] ?? "!";
      const typeUpper = (sType || "Q").toUpperCase();
      const zoneStr = zFrom.toLowerCase() + zTo.toLowerCase();
      // When the serve is a terminal ace (S#) the reception (R=) would also end
      // the rally via rallyWinnerFor, scoring a second point.  Insert a
      // compound-pair marker (\x01) before the reception code so the tokeniser
      // can flag it and suppress both auto-serve insertion and double-scoring.
      const pairMark = serveEval === "#" ? "\x01" : "";
      return (
        sTeam +
        sJersey.padStart(2, "0") +
        "S" +
        typeUpper +
        serveEval +
        zoneStr +
        pairMark +
        rTeam +
        recJersey.toString().padStart(2, "0") +
        "R" +
        typeUpper +
        recEval +
        zoneStr
      );
    },
  );
  // Pass 2: attack → block
  result = result.replace(
    COMPOUND_ATK_DOT_RE,
    (_m, aTeamRaw, aJersey, aType, zFrom, bTeamRaw, bJersey, bEval, zTo) => {
      const aTeam = aTeamRaw === "a" || aTeamRaw === "A" ? "a" : "*";
      const bTeam = bTeamRaw
        ? bTeamRaw === "a" || bTeamRaw === "A"
          ? "a"
          : "*"
        : aTeam === "*"
          ? "a"
          : "*";
      const blkEval = bEval || "!";
      const atkEval = DOT_BLOCK_TO_ATTACK_EVAL[blkEval] ?? "!";
      const typeUpper = aType.toUpperCase();
      const zoneStr = zFrom.toLowerCase() + zTo.toLowerCase();
      // When block misses (B=) the paired attack is a kill (A#) — both would
      // score, giving double points.  Mark B= as compound-paired.
      const pairMark = blkEval === "=" ? "\x01" : "";
      return (
        aTeam +
        aJersey.padStart(2, "0") +
        "A" +
        typeUpper +
        atkEval +
        zoneStr +
        pairMark +
        bTeam +
        bJersey.padStart(2, "0") +
        "B" +
        blkEval +
        zoneStr
      );
    },
  );
  return result;
}

function sameShirt(a, b) {
  if (a == null || b == null || a === "" || b === "") return false;
  const ia = parseInt(a, 10);
  const ib = parseInt(b, 10);
  return Number.isFinite(ia) && Number.isFinite(ib) && ia === ib;
}

function normalizeLiberoList(liberoValue) {
  const raw = Array.isArray(liberoValue)
    ? liberoValue
    : liberoValue == null || liberoValue === ""
      ? []
      : [liberoValue];
  const out = [];
  for (const val of raw) {
    const n = parseInt(val, 10);
    if (!Number.isFinite(n)) continue;
    if (!out.includes(n)) out.push(n);
    if (out.length >= 2) break;
  }
  return out;
}

function primaryLibero(liberoValue) {
  const list = normalizeLiberoList(liberoValue);
  return list.length > 0 ? list[0] : null;
}

function isLiberoNumber(liberoValue, shirtNumber) {
  const n = parseInt(shirtNumber, 10);
  if (!Number.isFinite(n)) return false;
  return normalizeLiberoList(liberoValue).includes(n);
}

function toggleLiberoNumber(liberoValue, shirtNumber) {
  const n = parseInt(shirtNumber, 10);
  if (!Number.isFinite(n)) return normalizeLiberoList(liberoValue);
  const list = normalizeLiberoList(liberoValue);
  if (list.includes(n)) return list.filter((x) => x !== n);
  if (list.length >= 2) return list;
  return [...list, n];
}

// A player is a libero when their playing-positions array contains 1 (the
// Libero position code — see DVWFile.js / teamsPlayers.js). Handles both the
// parser's `Positions` and the stored `positions` field shapes.
function hasLiberoPosition(player) {
  const arr = Array.isArray(player?.Positions)
    ? player.Positions
    : Array.isArray(player?.positions)
      ? player.positions
      : [];
  return arr.some((v) => parseInt(String(v), 10) === 1);
}
void sameShirt; // exported for potential future use

// Plain text aliases.  Use <serverA>/<serverB> for the current server jersey
// (filled at expand time).  '~' as the skill-eval separator means "let the
// next char be the eval", e.g.  s+ -> *<serverA>S+
const ALIAS_TABLE = [
  // points
  { pattern: /\bpa\b/gi, expand: () => "*--P#" },
  { pattern: /\bpb\b/gi, expand: () => "a--P#" },
  // serve aliases (use current server)
  { pattern: /\bsa\b/gi, expand: (ctx) => buildServeAlias(ctx, "#") },
  { pattern: /\bse\b/gi, expand: (ctx) => buildServeAlias(ctx, "=") },
  // s+ s! s- s/ s#  – serve by current server with given eval
  {
    pattern: /\bs([#+!\-/=])\b/gi,
    expand: (ctx, m) => buildServeAlias(ctx, m[1]),
  },
];

function buildServeAlias(ctx, evalChar) {
  const { servingTeam, lineupA, lineupB } = ctx;
  const lineup = servingTeam === "A" ? lineupA : lineupB;
  const server = lineup?.find((p) => String(p.position) === "1");
  const num = server?.shirtNumber;
  if (num === undefined || num === null) return ""; // strip if unknown
  const padNum = String(num).padStart(2, "0");
  const teamMark = servingTeam === "A" ? "*" : "a";
  return `${teamMark}${padNum}S${evalChar}`;
}

function expandAliases(input, ctx) {
  let out = input;
  for (const a of ALIAS_TABLE) {
    out = out.replace(a.pattern, (...args) => {
      // the regex `match` is the first arg; capture groups follow
      const expansion = a.expand(ctx, args);
      return expansion ?? "";
    });
  }
  return out;
}

// Split a compacted (whitespace/semicolon-stripped) string into raw event
// tokens. Expands dot-pairs (S.R, A.B) first, then parses with:
//   • skill omission  (jersey + type/eval → DEFAULT_SKILL inferred as attack)
//   • type omission   → DEFAULT_TYPE[skill]
//   • eval omission   → DEFAULT_EVAL[skill]
//   • sub-zone support (e.g. 5b, 5b1a)
//   • no team marker  → always defaults to home team (*)
function splitCompound(input, defs = {}) {
  const dSkill = defs.skill ?? DEFAULT_SKILL;
  const dType = defs.type ?? DEFAULT_TYPE;
  const dEval = defs.eval ?? DEFAULT_EVAL;
  // Expand dot-pairs BEFORE stripping whitespace so spaces act as natural
  // boundaries preventing the dot-regex from consuming too greedily.
  const expanded = expandCompoundDots(input, {
    liberoA: defs.liberoA,
    liberoB: defs.liberoB,
  });
  const s = expanded.replace(/[\s;]+/g, "");
  const tokens = [];
  let pos = 0;

  while (pos < s.length) {
    // Compound-pair marker inserted by expandCompoundDots: this token is the
    // second half of a terminal dot-pair and must not auto-insert a serve or
    // score an additional point (the first token already handled both).
    let isCompoundPaired = false;
    if (s[pos] === "\x01") {
      isCompoundPaired = true;
      pos++;
      if (pos >= s.length) break;
    }
    const start = pos;
    let team;
    const ch = s[pos];
    if (ch === "*" || ch === "a" || ch === "A") {
      team = ch === "*" ? "*" : "a";
      pos++;
    } else {
      team = "*"; // no marker → default to home team
    }

    // Player number: 1 or 2 digits (or '--' for manual point macro)
    let numStr = "";
    if (s[pos] === "-" && s[pos + 1] === "-") {
      numStr = "--";
      pos += 2;
    } else {
      while (pos < s.length && /\d/.test(s[pos]) && numStr.length < 2) {
        numStr += s[pos];
        pos++;
      }
      if (numStr.length === 0) {
        // Allow omitting the jersey number for reception (R) — default to the
        // team's first assigned libero.
        const nextCh = s[pos]?.toUpperCase();
        if (nextCh === "R") {
          const lib =
            team === "*"
              ? primaryLibero(defs.liberoA)
              : primaryLibero(defs.liberoB);
          if (lib != null) {
            numStr = String(lib);
          } else {
            return { error: `No libero assigned — enter a jersey number` };
          }
        } else {
          return {
            error: `Expected jersey number after team marker at position ${start + 1}`,
          };
        }
      }
    }

    // If we hit a token boundary immediately after the jersey (end-of-string
    // or next team marker), apply ALL defaults and emit the token.
    if (pos >= s.length || s[pos] === "*") {
      const skill = dSkill;
      const typeChar = dType[skill] || "";
      const evalChar = dEval[skill] || "+";
      const padded = numStr === "--" ? "--" : numStr.padStart(2, "0");
      tokens.push({
        raw: team + padded + skill + typeChar + evalChar,
        team,
        compoundPaired: isCompoundPaired,
      });
      while (pos < s.length && s[pos] === ".") pos++;
      continue;
    }

    // Skill may be omitted — if the char after the jersey is a type char or
    // eval char, infer DEFAULT_SKILL and do NOT advance pos.
    let skill;
    const skillCh = s[pos].toUpperCase();
    if (SKILL_CHARS.has(skillCh)) {
      skill = skillCh;
      pos++;
    } else if (TYPE_CHARS.has(skillCh) || EVAL_CHARS.has(s[pos])) {
      skill = dSkill; // implied by context (default attack)
    } else {
      return { error: `Invalid skill '${s[pos]}' at position ${pos + 1}` };
    }

    // Type: use explicit char if present, otherwise fill in DEFAULT_TYPE[skill].
    let typeChar = "";
    if (pos < s.length && /[A-Za-z]/.test(s[pos])) {
      typeChar = s[pos].toUpperCase();
      pos++;
    } else {
      typeChar = dType[skill] || "";
    }

    // Eval: use explicit char if present, otherwise fill in DEFAULT_EVAL[skill].
    let evalChar;
    if (pos < s.length && EVAL_CHARS.has(s[pos])) {
      evalChar = s[pos];
      pos++;
    } else {
      evalChar = dEval[skill] || "+";
    }

    // Zone section: each slot is a digit plus an optional sub-zone letter (a-d).
    // Try to consume the longest candidate whose trailing char is a valid token
    // boundary (* a A . or end-of-string).
    // IMPORTANT: 'a'/'A' is only a valid boundary when the char AFTER it is a
    // digit or '-', confirming it is a team marker (e.g. "a06") rather than a
    // skill/type letter (e.g. "AH" in "06AH#").
    const ZONE_SEC_RE = /^(\d[a-dA-D]?){1,2}$/;
    const validTailCh = (tc, afterTc) => {
      // \x01 is the compound-pair marker — treat it as a valid token boundary.
      if (tc === undefined || tc === "*" || tc === "." || tc === "\x01")
        return true;
      if (tc === "a" || tc === "A") {
        // Only a real team marker when followed by a digit or '--'
        return afterTc !== undefined && (/\d/.test(afterTc) || afterTc === "-");
      }
      return false;
    };
    let zoneStr = "";
    const maxLen = Math.min(4, s.length - pos);
    for (let len = maxLen; len >= 0; len--) {
      const candidate = s.slice(pos, pos + len);
      if (len > 0 && !ZONE_SEC_RE.test(candidate)) continue;
      const tailCh = s[pos + len];
      const afterTailCh = s[pos + len + 1];
      if (!validTailCh(tailCh, afterTailCh)) continue;
      zoneStr = candidate.toLowerCase();
      pos += len;
      break;
    }

    const padded = numStr === "--" ? "--" : numStr.padStart(2, "0");
    tokens.push({
      raw: team + padded + skill + typeChar + evalChar + zoneStr,
      team,
      compoundPaired: isCompoundPaired,
    });
    void start;
    // Skip dot separators between tokens.
    while (pos < s.length && s[pos] === ".") pos++;
  }
  return { tokens };
}

function parseDVCode(raw) {
  if (!raw) return { ok: false, error: "Empty code" };
  const trimmed = raw.trim();
  if (trimmed.length < 3)
    return { ok: false, error: "Too short (e.g. 1S# or *01SH#)" };

  // Optional team marker. No marker → default to home team (*).
  const first = trimmed[0];
  let teamChar;
  let hasMarker;
  if (first === "*") {
    teamChar = "*";
    hasMarker = true;
  } else if (first === "a" || first === "A") {
    teamChar = "a";
    hasMarker = true;
  } else {
    teamChar = "*";
    hasMarker = false;
  }
  const code =
    teamChar + (hasMarker ? trimmed.substring(1) : trimmed).toUpperCase();

  // Player number: 1 or 2 digits, OR '--' for the manual-point macro.
  let i = 1;
  let numStr = "";
  if (code[1] === "-" && code[2] === "-") {
    numStr = "--";
    i = 3;
  } else {
    while (i < code.length && /\d/.test(code[i]) && numStr.length < 2) {
      numStr += code[i];
      i++;
    }
    if (numStr.length === 0)
      return { ok: false, error: "Player number required (1 or 2 digits)" };
  }

  const skill = code[i];
  if (!SKILL_LABELS[skill])
    return { ok: false, error: "Skill must be one of S R E A B D F P" };
  i++;

  let type = null;
  if (/[A-Z]/.test(code[i] ?? "")) {
    type = code[i];
    i++;
  }

  const evalChar = code[i];
  if (!EVAL_LABELS[evalChar])
    return {
      ok: false,
      error:
        "Evaluation required: # (kill/ace), + (positive), ! (ok), - (negative), / (overpass), = (error)",
    };
  i++;

  // Each zone slot is a digit plus an optional sub-zone letter (a/b/c/d).
  let zoneFrom = null;
  let subzoneFrom = null;
  let zoneTo = null;
  let subzoneTo = null;
  if (code[i] && /\d/.test(code[i])) {
    zoneFrom = code[i];
    i++;
    if (code[i] && /[A-D]/.test(code[i])) {
      subzoneFrom = code[i].toLowerCase();
      i++;
    }
    if (code[i] && /\d/.test(code[i])) {
      zoneTo = code[i];
      i++;
      if (code[i] && /[A-D]/.test(code[i])) {
        subzoneTo = code[i].toLowerCase();
        i++;
      }
    }
  }

  const padNum = numStr === "--" ? "--" : numStr.padStart(2, "0");
  const normalised =
    teamChar +
    padNum +
    skill +
    (type ?? "") +
    evalChar +
    (zoneFrom ?? "") +
    (subzoneFrom ?? "") +
    (zoneTo ?? "") +
    (subzoneTo ?? "");

  return {
    ok: true,
    event: {
      id: generateUUID(),
      raw: normalised,
      team: teamChar === "*" ? "A" : "B",
      shirtNumber: padNum,
      skill,
      skillLabel: SKILL_LABELS[skill],
      type,
      evaluation: evalChar,
      evaluationLabel: EVAL_LABELS[evalChar],
      zoneFrom,
      subzoneFrom,
      zoneTo,
      subzoneTo,
    },
  };
}

function rallyWinnerFor(ev) {
  const { skill, team } = ev;
  const evaluation = ev.evaluation ?? ev.DVGrade ?? null;
  const opp = team === "A" ? "B" : "A";
  if (skill === "P" && evaluation === "#") return team; // manual point
  if (evaluation === "=") return opp;
  if (evaluation === "#") {
    if (skill === "S" || skill === "A" || skill === "B") return team;
  }
  return null;
}

function emptySet(setNumber) {
  return {
    setNumber,
    pointsA: 0,
    pointsB: 0,
    winner: null,
    events: [],
  };
}

// Given a lineup (array of {shirtNumber/playerNumber, position/currentPosition})
// and the setter's shirt number, returns which of the 6 canonical rotations
// (1-6, matching Constants.js's `kRotationSetterPositions` = [1,6,5,4,3,2] —
// i.e. R1=setter@1, R2=setter@6, ... R6=setter@2) this lineup is currently in,
// or null if the setter can't be found on court. This is what keeps
// rotationA/B in sync with wherever the setter actually starts — see the
// MatchSetupModal onSave callback for why this matters.
function rotationForSetterInLineup(lineup, setterShirt) {
  if (setterShirt == null) return null;
  const setterNum = String(parseInt(String(setterShirt), 10));
  const entry = (lineup ?? []).find((p) => {
    const shirt = p?.shirtNumber ?? p?.playerNumber ?? p?.ShirtNumber;
    return String(parseInt(String(shirt ?? ""), 10)) === setterNum;
  });
  const pos = parseInt(
    String(entry?.position ?? entry?.currentPosition ?? ""),
    10,
  );
  if (!Number.isFinite(pos)) return null;
  const idx = kRotationSetterPositions.indexOf(pos);
  return idx >= 0 ? idx + 1 : null;
}

const EVENT_TYPE_BY_SKILL = {
  S: 1,
  R: 2,
  E: 3,
  A: 4,
  B: 5,
  D: 6,
  F: 7,
  C: 8,
  P: 0,
};

const EVENT_GRADE_BY_EVAL = {
  "=": 0,
  "/": 0.5,
  "-": 1,
  "!": 2,
  "+": 3,
  "#": 3,
};

function isSetWon(set, isTiebreak, winScore, finalSetWinScore, winByTwo) {
  const cap = isTiebreak
    ? (finalSetWinScore ?? TIEBREAK_POINTS)
    : (winScore ?? REGULAR_SET_POINTS);
  const margin = winByTwo === false ? 1 : 2;
  const { pointsA, pointsB } = set;
  if (pointsA >= cap && pointsA - pointsB >= margin) return "A";
  if (pointsB >= cap && pointsB - pointsA >= margin) return "B";
  return null;
}

function DVMatchCoding() {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { currentUser } = useAuthStatus();

  const initialState = location.state ?? {};
  const teamA = initialState.teamA ??
    initialState.match?.teamA ?? { Name: "Team A", colour: "lime" };
  const teamB = initialState.teamB ??
    initialState.match?.teamB ?? { Name: "Team B", colour: "magenta" };

  let teamAName = teamA.Name ?? teamA.name ?? "Team A";
  teamAName = teamAName.length > 16 ? teamAName.substring(0, 20) + "…" : teamAName;
  teamAName = teamAName.toUpperCase();
  let teamBName = teamB.Name ?? teamB.name ?? "Team B";
  teamBName = teamBName.length > 16 ? teamBName.substring(0, 20) + "…" : teamBName;
  teamBName = teamBName.toUpperCase();

  // Team colours are held in state (not just read off the plain `teamA`/`teamB`
  // objects) so they can be changed live from the Match Setup modal and have
  // that change immediately reflected in the on-court diagram.
  const [colourA, setColourA] = useState(teamA.colour ?? "lime");
  const [colourB, setColourB] = useState(teamB.colour ?? "magenta");

  const [sets, setSets] = useState(() => [emptySet(1)]);
  const [currentSetIdx, setCurrentSetIdx] = useState(0);
  const [servingTeam, setServingTeam] = useState("A");
  const [rotationA, setRotationA] = useState(teamA.rotation ?? 1);
  const [rotationB, setRotationB] = useState(teamB.rotation ?? 1);
  const [lineupA, setLineupA] = useState(
    teamA.currentLineup ?? teamA.lineup ?? [],
  );
  const [lineupB, setLineupB] = useState(
    teamB.currentLineup ?? teamB.lineup ?? [],
  );
  const [codeInput, setCodeInput] = useState("");
  const [parseError, setParseError] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  // Court orientation + which team is on which side. Like `drawMode`, these are
  // display-only preferences that aren't reconstructed from the saved match, so
  // they're persisted to GLOBAL "last used" localStorage keys (not the per-match
  // autosave draft, which isn't reliably matched on a resume-from-DB) so a
  // resumed match opens with the teams on the correct side and the orientation
  // the coder last used.
  const [orientation, setOrientation] = useState(() => {
    try {
      const v = localStorage.getItem("VBLiveOrientation");
      return v === "horizontal" || v === "vertical" ? v : "vertical";
    } catch (_) {
      return "vertical";
    }
  }); // "vertical" | "horizontal"
  const [swapSides, setSwapSides] = useState(() => {
    try {
      return localStorage.getItem("VBLiveSwapSides") === "true";
    } catch (_) {
      return false;
    }
  }); // swap which team is top/bottom (vertical) or left/right (horizontal)
  const [tcMode, setTcMode] = useState(false); // TouchCoding mode
  // Draw & Code mode (replaces the court diagram). Initialised from a GLOBAL
  // "last used mode" flag (not the per-match autosave draft) so it survives a
  // save + resume-from-DB, where the draft key isn't reliably matched — draw
  // mode is otherwise the ONLY coding-surface state not reconstructed from the
  // saved match, so without this it always resumed in the DV-code court.
  const [drawMode, setDrawMode] = useState(() => {
    try {
      return localStorage.getItem("VBLiveDrawMode") === "true";
    } catch (_) {
      return false;
    }
  });
  const [selectedEventId, setSelectedEventId] = useState(null);
  // True once the user has explicitly picked an event row to review/edit
  // (as opposed to just being at the live/most-recent event) — see
  // `isViewingLiveEvent`. Needed because the LAST event in the set is both
  // "the live event" AND a perfectly normal row a user can click to inspect
  // its ball path; without this flag, clicking that last row was
  // indistinguishable from "still live", so in Draw mode it kept showing the
  // live drawing canvas instead of the court diagram (with the selected
  // event's path) like every other row.
  const [reviewingEvent, setReviewingEvent] = useState(false);
  const [leftWidth, setLeftWidth] = useState(320); // px, resizable
  const [scoutingDefaults, setScoutingDefaults] = useState(null);
  const [shortcutDefs, setShortcutDefs] = useState(null);
  const matchIdRef = useRef(
    initialState.match?.guid ?? initialState.guid ?? generateUUID(),
  );
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const settingsDialogRef = useRef(null);
  const lineupDialogRef = useRef(null);
  const matchSetupDialogRef = useRef(null);
  const nominateSetterDialogRef = useRef(null);
  // Which team ("A"/"B") the nominate-setter modal is currently prompting
  // for, or null when not needed — see the effect near `setSetterA`/
  // `setSetterB` that opens this whenever a configured team's setter is null.
  const [nominateSetterTeam, setNominateSetterTeam] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [lineupEditTeam, setLineupEditTeam] = useState("A");
  // Track the latest lineup for each set so the editor can offer "Use prev set".
  const setStartLineupsRef = useRef([]);
  // Lineup at the very start of the match; used by computeStateAtEvent to replay.
  const initialLineupsRef = useRef({
    A: teamA.currentLineup ?? teamA.lineup ?? [],
    B: teamB.currentLineup ?? teamB.lineup ?? [],
  });
  // Stable "who started at what position" snapshot for Draw & Code's role
  // labels (S/OPP/OH1/OH2/MB1/MB2/L1/L2) — captured ONCE, the first time each
  // team's on-court lineup actually has real shirt numbers (covers both a
  // brand-new match, where the lineup is only filled in via the Match Setup
  // modal AFTER mount, and a loaded match). Never updated again afterwards,
  // even if players rotate/substitute.
  const startingLineupsRef = useRef({ A: [], B: [] });
  const startingLineupsCapturedRef = useRef(false);
  // Full team rosters (all players, not just the 6 on court).
  const rosterARef = useRef(
    (teamA.players?.length ? teamA.players : null) ?? teamA.lineup ?? [],
  );
  const rosterBRef = useRef(
    (teamB.players?.length ? teamB.players : null) ?? teamB.lineup ?? [],
  );
  // Setter jersey number for each team (initialised from a previously saved
  // match's `setterA`/`setterB`, falling back to role1 === "S" in the
  // lineup). Checking `initialState.match` first matters for a match that
  // was saved before any events were coded (see the "save new match as soon
  // as lineups are filled" flow) — the "load match" effect below only runs
  // when the match already has `sets`, so this mount-time fallback is what
  // restores the setter for a lineup-only save.
  const [setterA, setSetterA] = useState(
    () =>
      initialState.match?.setterA ??
      (teamA.currentLineup ?? teamA.lineup ?? []).find((p) => p.role1 === "S")
        ?.shirtNumber ??
      null,
  );
  const [setterB, setSetterB] = useState(
    () =>
      initialState.match?.setterB ??
      (teamB.currentLineup ?? teamB.lineup ?? []).find((p) => p.role1 === "S")
        ?.shirtNumber ??
      null,
  );
  // "Opposition passing" formation override, per team — used only by Draw &
  // Code's on-court diagram (`DrawAndCodeCourt`, via `formationFor`) to pick
  // the `recposopp` reception formation instead of the default `recp`os`.
  // Toggled from a checkbox on each team's lineup panel (see `renderLineup`).
  const [isOppPassingA, setIsOppPassingA] = useState(!!teamA.isOppPassing);
  const [isOppPassingB, setIsOppPassingB] = useState(!!teamB.isOppPassing);
  const initialRotationARef = useRef(teamA.rotation ?? 1);
  const initialRotationBRef = useRef(teamB.rotation ?? 1);
  const initialLiberosForTeam = (team) =>
    normalizeLiberoList(
      [...(team.players ?? []), ...(team.currentLineup ?? team.lineup ?? [])]
        .filter((p) => p.role1 === "L" || hasLiberoPosition(p))
        .map((p) => p.shirtNumber),
    );
  const [liberoA, setLiberoA] = useState(() => initialLiberosForTeam(teamA));
  const [liberoB, setLiberoB] = useState(() => initialLiberosForTeam(teamB));
  // Substitution pair tracking.  subPairs[setIdx] = { A: [{p1,p2,count},...], B: [...] }
  const [subPairs, setSubPairs] = useState(() =>
    Array(6)
      .fill(null)
      .map(() => ({ A: [], B: [] })),
  );
  // The match squad — the players SELECTED for this match (via the setup
  // modal's Playing-squad picker). Only these (plus anyone who has already
  // played) are offered as substitutes; the sub popover's "full roster" option
  // can pull in others until MAX_MATCH_SQUAD is reached. Stored as Sets of
  // normalised (int-string) shirt numbers.
  const [matchSquadA, setMatchSquadA] = useState(() =>
    defaultMatchSquad(rosterARef.current),
  );
  const [matchSquadB, setMatchSquadB] = useState(() =>
    defaultMatchSquad(rosterBRef.current),
  );
  // Video player
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoRate, setVideoRate] = useState(1);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [isSeekBarDragging, setIsSeekBarDragging] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const videoWrapRef = useRef(null); // wraps the ReactPlayer — mousewheel/drag scrub target
  const videoDragRef = useRef({ active: false, lastX: 0 });
  const playerReadyRef = useRef(false); // true once ReactPlayer fires onReady
  // Guards the onReady initial seek so it runs EXACTLY ONCE per loaded video.
  // react-player re-fires onReady on internal (re)inits (YouTube especially),
  // and the handler seeks to the currently-selected event — which is the
  // most-recently-coded one. Without this one-shot guard, every re-fire (and
  // every progress tick that triggered a re-init) snapped the video straight
  // back to that event's position, overriding any wheel/drag/click seek. Reset
  // whenever the video source changes (see the effect below).
  const initialVideoSeekDoneRef = useRef(false);
  const [isSubs, setIsSubs] = useState(false);
  // Inline substitution popover — opened by tapping a player's number in the
  // lineups panel. `subPopup` = { team, position, shirt } | null; `showAll`
  // toggles the candidate list between the FIVB-available subset and the whole
  // team roster.
  const [subPopup, setSubPopup] = useState(null);
  const [subPopupShowAll, setSubPopupShowAll] = useState(false);
  // Dismiss the substitution popover on any outside tap. Taps/scrolls that land
  // INSIDE the popover (`[data-subpopup]`) are ignored — relying on React's
  // stopPropagation was unreliable on touch (a scroll/select touch could close
  // the popover before it registered).
  useEffect(() => {
    if (!subPopup) return;
    const close = (e) => {
      if (e?.target?.closest?.("[data-subpopup]")) return;
      setSubPopup(null);
      setSubPopupShowAll(false);
    };
    const id = setTimeout(() => {
      document.addEventListener("mousedown", close);
      document.addEventListener("touchstart", close);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [subPopup]);
  // Substitution-history popover — opened by tapping the "Subs n/m" chip on a
  // team's lineup panel. `subHistoryPopup` = { team, x, y } | null.
  const [subHistoryPopup, setSubHistoryPopup] = useState(null);
  useEffect(() => {
    if (!subHistoryPopup) return;
    const close = (e) => {
      if (e?.target?.closest?.("[data-subhistory]")) return;
      setSubHistoryPopup(null);
    };
    const id = setTimeout(() => {
      document.addEventListener("mousedown", close);
      document.addEventListener("touchstart", close);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [subHistoryPopup]);
  const [eventsWidth, setEventsWidth] = useState(240); // px – width of events list alongside video
  const draggingEventsRef = useRef(false);
  const videoRowRef = useRef(null);
  const selectedRowRef = useRef(null); // selected event row, kept scrolled into view
  // Actual rendered width of the events panel (differs from eventsWidth when the
  // video is hidden and the list spans the full container).
  const [eventsPanelWidth, setEventsPanelWidth] = useState(240);
  const eventsPanelObserverRef = useRef(null);
  const eventsPanelRef = useCallback((node) => {
    if (eventsPanelObserverRef.current) {
      eventsPanelObserverRef.current.disconnect();
      eventsPanelObserverRef.current = null;
    }
    if (node) {
      setEventsPanelWidth(node.getBoundingClientRect().width);
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setEventsPanelWidth(entry.contentRect.width);
        }
      });
      ro.observe(node);
      eventsPanelObserverRef.current = ro;
    }
  }, []);
  const playerRef = useRef(null);
  const courtSvgRef = useRef(null); // SVG element for getBoundingClientRect

  // Compute the video position (seconds) for an event.
  // Video scrub controls (mousewheel / drag-pan / swipe / toolbar buttons) —
  // same interaction model as the Coding Page's own video player, but scoped
  // to this panel's wrapper (a non-passive wheel listener, since React's
  // onWheel can't preventDefault and would otherwise scroll the page).
  const seekVideo = (delta) => {
    const p = playerRef.current;
    if (!p || typeof p.getCurrentTime !== "function") return;
    const cur = p.getCurrentTime() ?? 0;
    const dur = videoDuration || Infinity;
    const next = Math.max(0, Math.min(dur, cur + delta));
    p.seekTo(next, "seconds");
    setVideoCurrentTime(next);
  };

  useEffect(() => {
    const el = videoWrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!playerRef.current) return;
      e.preventDefault();
      setVideoPlaying(false);
      // Divided down further than the drag/toolbar seeks so a single wheel
      // notch only nudges the video a fraction of a second — coarse scrubs
      // are still available via drag or the ±1s/±10s toolbar buttons.
      seekVideo(-e.deltaY / 300);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fullscreen toggle (the wrapper div, so the capture overlay + toolbar stay
  // usable) + keep isVideoFullscreen in sync with ESC / browser UI exits.
  const toggleVideoFullscreen = () => {
    const el = videoWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  useEffect(() => {
    const onFsChange = () =>
      setIsVideoFullscreen(
        !!document.fullscreenElement &&
          document.fullscreenElement === videoWrapRef.current,
      );
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // iPad / iOS Safari: `100vh` measures the *largest* viewport (with the browser
  // chrome hidden), so the coding page renders taller than the visible area —
  // leaving a blank strip above the app and allowing the whole page to scroll
  // vertically. While the coding screen is mounted, pin <html>/<body> to the
  // dynamic viewport height and hide overflow so the entire UI fits with no
  // phantom top margin and no page-level scrolling.
  useEffect(() => {
    document.documentElement.classList.add("coding-locked");
    document.body.classList.add("coding-locked");
    return () => {
      document.documentElement.classList.remove("coding-locked");
      document.body.classList.remove("coding-locked");
    };
  }, []);

  // A new video source should get its one initial seek-to-selected-event; reset
  // the guard so the NEXT onReady (for this new URL) performs it exactly once.
  useEffect(() => {
    initialVideoSeekDoneRef.current = false;
  }, [videoUrl]);

  // Persist the current coding surface (Draw & Code vs DV-code court) to a
  // GLOBAL "last used mode" flag so the next match/resume opens in the same
  // mode. Kept separate from the per-match autosave draft (which isn't matched
  // on a resume-from-DB) so draw mode survives save + resume.
  useEffect(() => {
    try {
      localStorage.setItem("VBLiveDrawMode", String(drawMode));
    } catch (_) {
      /* ignore storage failures */
    }
  }, [drawMode]);

  // Persist court orientation + team side (same GLOBAL "last used" approach as
  // draw mode) so a resumed match opens with the teams on the correct side and
  // in the last-used orientation.
  useEffect(() => {
    try {
      localStorage.setItem("VBLiveOrientation", orientation);
    } catch (_) {
      /* ignore storage failures */
    }
  }, [orientation]);
  useEffect(() => {
    try {
      localStorage.setItem("VBLiveSwapSides", String(swapSides));
    } catch (_) {
      /* ignore storage failures */
    }
  }, [swapSides]);

  const onVideoPointerDown = (e) => {
    const p = playerRef.current;
    const cur =
      p && typeof p.getCurrentTime === "function" ? (p.getCurrentTime() ?? 0) : 0;
    videoDragRef.current = {
      active: true,
      lastX: e.clientX,
      moved: 0,
      target: cur,
      lastSeek: 0,
    };
  };
  const onVideoPointerMove = (e) => {
    const d = videoDragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.lastX;
    d.moved = (d.moved || 0) + Math.abs(dx);
    if (Math.abs(dx) < 2) return;
    d.lastX = e.clientX;
    // Accumulate a TARGET time rather than reading getCurrentTime() each move
    // (which lags behind an in-flight seek) and only actually seek the player
    // at most ~10×/sec. Seeking on every pointermove re-buffers an online
    // (HLS/YouTube) source each time, which is what makes iPad scrubbing jerky.
    const dur = videoDuration || Infinity;
    d.target = Math.max(0, Math.min(dur, (d.target ?? 0) + dx / 40));
    setVideoPlaying(false);
    setVideoCurrentTime(d.target); // scrub bar updates smoothly every move
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - (d.lastSeek || 0) >= 100) {
      d.lastSeek = now;
      const p = playerRef.current;
      if (p && typeof p.seekTo === "function") p.seekTo(d.target, "seconds");
    }
  };
  const onVideoPointerUp = () => {
    const d = videoDragRef.current;
    // A tap (negligible movement) toggles play/pause — the native player
    // controls are disabled (unreachable under the capture overlay above),
    // so this restores basic click-to-play/pause.
    if (d.active && (d.moved || 0) < 6) {
      setVideoPlaying((p) => !p);
    } else if (d.active) {
      // Final precise seek to the last drag target (throttling above may have
      // skipped the most recent position).
      const p = playerRef.current;
      if (p && typeof p.seekTo === "function") p.seekTo(d.target ?? 0, "seconds");
    }
    d.active = false;
  };

  const videoSwipeHandlers = useSwipeable({
    onSwipedLeft: () => seekVideo(-2.5),
    onSwipedRight: () => seekVideo(2.5),
    trackMouse: false,
  });

  // PRIORITY: `VideoPosition`/`timestamp` are captured LIVE from the actual
  // player (see getTimestamp()) at the moment the event was coded in THIS
  // app, so they are always accurate for the video currently loaded — prefer
  // them first. Only fall back to the TimeStamp + match
  // videoStartTimeSeconds/videoOffset calibration (mirrors VideoAnalysis)
  // when no such live-captured position exists (e.g. events imported from a
  // file with no coding-time video). That calibration can otherwise be STALE
  // (left over from a previous session or a different video) and, combined
  // with a wall-clock TimeStamp, produce a wildly wrong/negative position
  // that clamps to 0 — this was the cause of "selecting an event always
  // jumps to the start of the video".
  const computeEventVideoPosition = (ev) => {
    if (!ev) return null;
    const leadTime = activeDefaults?.videoLeadTime ?? DEFAULT_VIDEO_LEAD_TIME;
    if (typeof ev.VideoPosition === "number" && ev.VideoPosition >= 0) {
      return Math.max(0, ev.VideoPosition - leadTime);
    }
    const startSecs = initialState.match?.videoStartTimeSeconds;
    const offset = initialState.match?.videoOffset;
    if (
      startSecs != null &&
      startSecs !== -1 &&
      offset != null &&
      offset !== -1 &&
      ev.TimeStamp
    ) {
      const ts =
        ev.TimeStamp instanceof Date ? ev.TimeStamp : new Date(ev.TimeStamp);
      if (!isNaN(ts.getTime())) {
        return Math.max(0, ts.getTime() / 1000 - startSecs + offset - leadTime);
      }
    }
    if (typeof ev.timestamp === "number" && ev.timestamp > 0) {
      return Math.max(0, ev.timestamp / 100 - leadTime);
    }
    return null;
  };
  const dragStartRef = useRef(null); // internal {x,y} of drag start; null = not dragging
  const [dragLine, setDragLine] = useState(null); // {x1,y1,x2,y2} internal coords

  // Stable refs so the keyboard handler never captures stale closures.
  const awardPointRef = useRef(null);
  const undoRef = useRef(null);
  const redoRef = useRef(null);

  // ---------- autosave / discard ----------
  // The coding session is autosaved to localStorage (keyed by match id) as the
  // user codes, so accidental navigation/remounts don't lose work. We also keep
  // a snapshot of the initial state (the originally loaded match, or an empty
  // match for a brand-new one) so the user can discard all changes and revert.
  const initialSnapshotRef = useRef(null); // state to revert to on "Discard"
  const didInitRef = useRef(false); // initial snapshot captured + draft restored
  const loadAppliedRef = useRef(false); // the incoming match has been applied
  const saveTimerRef = useRef(null); // debounce timer for autosave writes
  const autosaveKey = () => `VBLiveCodingDraft_${matchIdRef.current}`;

  const activeDefaults = scoutingDefaults ?? {
    skill: DEFAULT_SKILL,
    type: DEFAULT_TYPE,
    eval: DEFAULT_EVAL,
    videoLeadTime: DEFAULT_VIDEO_LEAD_TIME,
    maxSubs: DEFAULT_MAX_SUBS,
    freeSubstitutions: DEFAULT_FREE_SUBS,
    setWinScore: DEFAULT_SET_WIN_SCORE,
    finalSetWinScore: DEFAULT_FINAL_SET_WIN_SCORE,
    winByTwo: DEFAULT_WIN_BY_TWO,
  };
  const activeShortcuts = shortcutDefs ?? DEFAULT_SHORTCUTS;
  // Effective substitution rules (fall back to defaults for older drafts that
  // predate these settings).
  const effMaxSubs = activeDefaults.maxSubs ?? DEFAULT_MAX_SUBS;
  const freeSubstitutions = !!activeDefaults.freeSubstitutions;
  // Effective set-scoring rules (fall back to standard FIVB rules for older
  // drafts that predate these settings).
  const effSetWinScore = activeDefaults.setWinScore ?? DEFAULT_SET_WIN_SCORE;
  const effFinalSetWinScore =
    activeDefaults.finalSetWinScore ?? DEFAULT_FINAL_SET_WIN_SCORE;
  const effWinByTwo = activeDefaults.winByTwo ?? DEFAULT_WIN_BY_TWO;

  const currentSet = sets[currentSetIdx];
  const isTiebreak = currentSetIdx === 4;
  const setsWonA = sets.filter((s) => s.winner === "A").length;
  const setsWonB = sets.filter((s) => s.winner === "B").length;
  const matchOver = setsWonA === SETS_TO_WIN || setsWonB === SETS_TO_WIN;
  const matchMeta = {
    app: "DataVolley",
    source: "DVMatchCoding",
    Guid: matchIdRef.current,
    guid: matchIdRef.current,
    dvstring: initialState.match?.dvstring ?? matchIdRef.current,
    teamA: teamAName,
    teamB: teamBName,
  };

  const DV_GRADE_BY_INDEX = ["=", "/", "!", "-", "+", "#"];
  const SKILL_BY_EVENT_TYPE = {
    1: "S",
    2: "R",
    3: "E",
    4: "A",
    5: "B",
    6: "D",
    7: "F",
    8: "C",
    20: "E", // setter call → set skill (otherwise defaults to "A" = attack on save)
    250: "C",
    40: "P", // manual point (VBLive-only EventType, see DVWFile kSkillManualPoint)
  };
  const EVENT_TYPE_BY_SKILL_CHAR = {
    S: 1,
    R: 2,
    E: 3,
    A: 4,
    B: 5,
    D: 6,
    F: 7,
    C: 8,
  };

  // Returns the first value that is not null/undefined and not an empty/whitespace
  // string.  Plain `??` does NOT fall through empty strings (a DB NickName of ""
  // would block the fallback to FirstName), so name resolution must use this.
  const firstNonEmpty = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== "") return v;
    }
    return "";
  };

  // Safe deep-clone that breaks circular references introduced by player.statsItems.
  // Deep-clone a value, handling cycles and shared references efficiently.
  // Uses TWO tracking structures, not one:
  //   - `completed`: val -> finished clone, for objects that have ALREADY been
  //     fully walked. Reusing these lets a shared (non-cyclic) sub-tree that's
  //     referenced from multiple places (e.g. event.drill -> set -> match,
  //     shared across many events) be cloned once and reused everywhere,
  //     avoiding the O(N²) re-walk / crash the single-pass version used to hit
  //     on large edited matches.
  //   - `ancestors`: val currently being walked RIGHT NOW (i.e. an object on
  //     the active recursion stack). If we hit one of these again it's a TRUE
  //     cycle (e.g. player.statsItems[i].player pointing back to the same
  //     player) — returning the in-progress clone here (as a single `visited`
  //     Map used to) would embed that still-under-construction object into
  //     itself, so the CLONE would be exactly as circular as the original and
  //     JSON.stringify would still throw "Converting circular structure to
  //     JSON" despite safeClone having "succeeded". So a true cycle is instead
  //     broken by substituting `undefined` (dropped from the clone) — shared
  //     sibling references are still fully preserved via `completed`.
  const safeClone = (value) => {
    const completed = new Map();
    const ancestors = new Set();
    const walk = (val) => {
      if (val === null || typeof val !== "object") return val;
      if (val instanceof Date) return new Date(val.getTime());
      if (completed.has(val)) return completed.get(val);
      if (ancestors.has(val)) return undefined; // true cycle — break it
      ancestors.add(val);
      let out;
      if (Array.isArray(val)) {
        out = val.map(walk);
      } else {
        out = {};
        for (const k of Object.keys(val)) {
          // `statsItems` is a heavy per-set stats aggregation array attached
          // to every roster player by DVWFile.js/PSVBFile.js (one full
          // StatsItem per set — see StatsItem.js). It's what caused the
          // player<->statsItem circular reference fixed above, and cloning
          // it is unnecessary here: nothing in this component (autosave,
          // rotation bookkeeping, DrawAndCodeCourt) reads a player's
          // statsItems, only their shirt/name/role/position. Since EVERY
          // event embeds a full (non-shared, once JSON-serialized) copy of
          // its player, keeping this array multiplies the autosave draft's
          // size by (events referencing that player) x (statsItems payload)
          // — easily blowing past localStorage's quota on a real match.
          if (k === "statsItems") continue;
          // `dvstring` (lowercase — NOT the small per-event `dvString`/
          // `event.dvString`, which is kept) is the FULL RAW DVW scout text
          // of an imported/resumed match (`DVWFile.js` sets `match.dvstring
          // = s` to the entire parsed file's source text — can be hundreds
          // of KB to several MB). `hydrateEventForReports` embeds it into
          // EVERY event via `Drill.match` (`matchMeta.dvstring`), so once
          // JSON-serialized (which can't share references the way in-memory
          // objects do) it gets duplicated in full for EACH event — for a
          // resumed match with hundreds of events this alone is what blows
          // way past localStorage's quota (far more than `statsItems` ever
          // did). Not needed to resume live coding — the match is rebuilt
          // fresh from the current sets/events on every save anyway (see
          // `buildDVWMatchText`), never read back from this old string.
          if (k === "dvstring") continue;
          out[k] = walk(val[k]);
        }
      }
      ancestors.delete(val);
      completed.set(val, out);
      return out;
    };
    return walk(value);
  };

  const normalizePlayerShape = (player) => {
    if (!player) return null;
    const firstName = firstNonEmpty(
      player.FirstName,
      player.firstName,
      player.playerFirstName,
    );
    const lastName = firstNonEmpty(
      player.LastName,
      player.lastName,
      player.playerLastName,
    );
    return {
      ...player,
      Guid: player.Guid ?? player.guid ?? null,
      guid: player.guid ?? player.Guid ?? null,
      ShirtNumber:
        player.ShirtNumber ?? player.shirtNumber ?? player.playerNumber ?? null,
      shirtNumber:
        player.shirtNumber ?? player.ShirtNumber ?? player.playerNumber ?? null,
      FirstName: firstName,
      LastName: lastName,
      // When no real nickname exists, fall back to LAST name (surname),
      // matching the display convention used everywhere else (e.g.
      // EventEditor.jsx's own `playerName` helper renders "F. LASTNAME",
      // Dashboard.jsx renders "F. LastName") — NOT first name. This matters
      // because `EventEditor.jsx`'s `doPlayerChanged` only supplies
      // `{FirstName, LastName, ShirtNumber}` (no NickName) when a player is
      // picked from its dropdown, so every player selected via the Event
      // Editor relies on THIS fallback; putting `firstName` before
      // `lastName` here caused the events list to show the player's first
      // name instead of their last name specifically after changing the
      // player in the Event Editor (other code paths that resolve players
      // from the roster/lineup usually already carry a real `NickName`, so
      // they never hit this fallback and looked correct).
      NickName: firstNonEmpty(
        player.NickName,
        player.nickName,
        lastName,
        firstName,
      ),
    };
  };

  const skillCharFromEventType = (eventType) =>
    SKILL_BY_EVENT_TYPE[eventType] ?? "A";

  const eventTypeFromSkillChar = (skillChar) =>
    EVENT_TYPE_BY_SKILL_CHAR[skillChar] ?? 4;

  const eventStringFromEventType = (eventType) => {
    switch (eventType) {
      case 1:
        return "Serve ";
      case 2:
        return "Pass ";
      case 3:
        return "Setter Calls ";
      case 4:
        return "Spike ";
      case 5:
        return "Block ";
      case 6:
        return "Dig ";
      case 7:
        return "Cover";
      case 20:
        return "Setter Calls ";
      case 250:
        return "Substitution ";
      default:
        return "";
    }
  };

  const resolveEventTeam = (event) => {
    if (event.team === "A" || event.team === "B") return event.team;
    if (event.homePlayer != null) return event.homePlayer ? "A" : "B";
    const player = event.Player ?? event.player ?? null;
    const playerGuid = player?.Guid ?? player?.guid ?? null;
    const playerNumber = String(
      player?.ShirtNumber ?? player?.shirtNumber ?? "",
    );
    const teamAPlayers = [
      ...(teamA.players ?? []),
      ...(teamA.currentLineup ?? []),
    ];
    const teamBPlayers = [
      ...(teamB.players ?? []),
      ...(teamB.currentLineup ?? []),
    ];
    if (playerGuid) {
      if (teamAPlayers.some((p) => (p.Guid ?? p.guid) === playerGuid))
        return "A";
      if (teamBPlayers.some((p) => (p.Guid ?? p.guid) === playerGuid))
        return "B";
    }
    if (playerNumber) {
      if (
        teamAPlayers.some(
          (p) =>
            String(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? "") ===
            playerNumber,
        )
      )
        return "A";
      if (
        teamBPlayers.some(
          (p) =>
            String(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? "") ===
            playerNumber,
        )
      )
        return "B";
    }
    return "A";
  };

  const inferPlayerByNumber = (teamKey, shirtNumber) => {
    if (shirtNumber == null) return null;
    const roster =
      teamKey === "A"
        ? [...(teamA.players ?? []), ...(teamA.currentLineup ?? [])]
        : [...(teamB.players ?? []), ...(teamB.currentLineup ?? [])];
    const normalized = String(parseInt(String(shirtNumber), 10));
    const found = roster.find(
      (p) =>
        String(
          parseInt(
            String(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? ""),
            10,
          ),
        ) === normalized,
    );
    return found ? normalizePlayerShape(found) : null;
  };

  const parseLineupTailFromDVCode = (dvCode) => {
    if (typeof dvCode !== "string" || dvCode.length === 0) return null;
    const fields = dvCode.split(";").filter((t) => t.trim() !== "");
    if (fields.length < 12) return null;
    const last12 = fields.slice(-12).map((value) => {
      const num = Number.parseInt(String(value ?? "").trim(), 10);
      return Number.isFinite(num) && num > 0 ? num : null;
    });
    const teamA = last12.slice(0, 6);
    const teamB = last12.slice(6, 12);
    if (!teamA.some((n) => n != null) && !teamB.some((n) => n != null))
      return null;
    return { teamA, teamB };
  };

  const buildLineupFromNumbers = (numbers, roster, fallbackLineup = []) => {
    if (!Array.isArray(numbers) || numbers.length === 0) return [];
    const pool = [...(roster ?? []), ...(fallbackLineup ?? [])];
    const lineup = [];
    for (let idx = 0; idx < numbers.length; idx++) {
      const num = numbers[idx];
      if (num == null) continue;
      const pos = String(idx + 1);
      const found = pool.find((p) => {
        const pnum = p?.shirtNumber ?? p?.playerNumber ?? p?.ShirtNumber;
        // `numbers` from a LineupSnapshot are STRINGS ("5") while a roster
        // player's shirt parses to a NUMBER (5) — compare both as numbers so
        // the real player is actually found. When this lookup wrongly failed
        // (`5 === "5"` is false) every reconstructed slot fell back to a bare
        // placeholder that lost the player's identity/role data.
        return (
          Number.parseInt(String(pnum), 10) === Number.parseInt(String(num), 10)
        );
      });
      if (found) {
        const normalized = normalizePlayerShape(found);
        lineup.push({
          ...normalized,
          shirtNumber: normalized?.shirtNumber ?? num,
          ShirtNumber: normalized?.ShirtNumber ?? num,
          playerNumber:
            normalized?.shirtNumber ?? normalized?.ShirtNumber ?? num,
          position: pos,
          currentPosition: pos,
        });
      } else {
        lineup.push({
          shirtNumber: num,
          ShirtNumber: num,
          playerNumber: num,
          position: pos,
          currentPosition: pos,
        });
      }
    }
    return lineup;
  };

  const parseScoutStateFromDVCode = (dvCode) => {
    if (typeof dvCode !== "string" || dvCode.length === 0) return null;
    const fields = dvCode.split(";");
    if (fields.length > 0 && fields[fields.length - 1] === "") fields.pop();
    if (fields.length < 12) return null;

    const toNum = (v) => {
      const n = Number.parseInt(String(v ?? "").trim(), 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const lineupFields =
      fields.length >= 26 ? fields.slice(14, 26) : fields.slice(-12);
    if (!Array.isArray(lineupFields) || lineupFields.length < 12) return null;

    const numsA = lineupFields.slice(0, 6).map(toNum);
    const numsB = lineupFields.slice(6, 12).map(toNum);
    if (!numsA.some((n) => n != null) && !numsB.some((n) => n != null))
      return null;

    const lineupAFromScout = buildLineupFromNumbers(
      numsA,
      rosterARef.current ?? [],
      lineupA ?? [],
    );
    const lineupBFromScout = buildLineupFromNumbers(
      numsB,
      rosterBRef.current ?? [],
      lineupB ?? [],
    );
    const setterPosA = toNum(fields[9]);
    const setterPosB = toNum(fields[10]);
    const setterAFromScout =
      setterPosA != null
        ? (lineupAFromScout.find(
            (p) =>
              String(p.position ?? p.currentPosition ?? "") ===
              String(setterPosA),
          )?.shirtNumber ?? null)
        : null;
    const setterBFromScout =
      setterPosB != null
        ? (lineupBFromScout.find(
            (p) =>
              String(p.position ?? p.currentPosition ?? "") ===
              String(setterPosB),
          )?.shirtNumber ?? null)
        : null;

    return {
      lineupA: lineupAFromScout,
      lineupB: lineupBFromScout,
      setterPosA,
      setterPosB,
      setterA: setterAFromScout,
      setterB: setterBFromScout,
    };
  };

  const rebuildEventStrings = (event) => {
    const player = event.Player ?? event.player ?? null;
    const playerNumber = String(
      player?.ShirtNumber ?? player?.shirtNumber ?? event.shirtNumber ?? "00",
    ).padStart(2, "0");
    const teamMark = event.team === "B" ? "a" : "*";
    const skill = event.skill ?? skillCharFromEventType(event.EventType);
    const typeChar = event.ExtraCode1 ?? event.type ?? "";
    const evalChar = event.DVGrade ?? event.evaluation ?? "+";
    const zonePart = `${event.zoneFrom ?? ""}${event.subzoneFrom ?? ""}${event.zoneTo ?? ""}${event.subzoneTo ?? ""}`;
    return {
      raw: `${teamMark}${playerNumber}${skill}${typeChar}${evalChar}${zonePart}`,
      dvString: `${teamMark}${playerNumber}${skill}${typeChar}${evalChar}${zonePart}`,
    };
  };

  const patchSelectedEvent = (patch) => {
    if (!selectedEventId) return;
    pushHistory();
    setSets((prev) =>
      prev.map((s, i) => {
        if (i !== currentSetIdx) return s;
        const nextEvents = s.events.map((ev) => {
          if (ev.id !== selectedEventId) return ev;
          const merged = { ...ev, ...patch };
          const rebuilt = rebuildEventStrings(merged);
          return {
            ...merged,
            Player: normalizePlayerShape(
              merged.Player ?? merged.player ?? null,
            ),
            player: normalizePlayerShape(
              merged.player ?? merged.Player ?? null,
            ),
            raw: patch.raw ?? rebuilt.raw,
            dvString: patch.dvString ?? rebuilt.dvString,
          };
        });
        return { ...s, events: nextEvents };
      }),
    );
  };

  // Auto-show setup modal when navigating from NewMatch
  useEffect(() => {
    if (initialState.showSetupModal) {
      // Pre-populate the video from the loaded match so the coder can watch it
      // while entering lineups in the (large) Match Setup modal.
      if (!videoUrl) {
        const matchUrl = initialState.match?.videoOnlineUrl;
        if (
          matchUrl &&
          matchUrl !== "?" &&
          matchUrl !== "-1" &&
          matchUrl !== -1
        ) {
          playerReadyRef.current = false;
          setVideoUrl(matchUrl);
        }
      }
      matchSetupDialogRef.current?.showModal();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // team.setter should never be null once a team's lineup is actually
  // configured (a null setter means "setter call" events get created with no
  // player — see `makeEvent`'s "00" fallback in DrawAndCodeCourt.jsx). If
  // that ever happens, force the user to nominate one via a dedicated modal
  // rather than silently coding events against a missing player.
  useEffect(() => {
    if (nominateSetterTeam) return; // already prompting for one team
    const hasConfiguredLineup = (lineup) =>
      (lineup ?? []).some((p) => p?.shirtNumber);
    if (setterA == null && hasConfiguredLineup(lineupA)) {
      setNominateSetterTeam("A");
    } else if (setterB == null && hasConfiguredLineup(lineupB)) {
      setNominateSetterTeam("B");
    }
  }, [setterA, setterB, lineupA, lineupB, nominateSetterTeam]);

  useEffect(() => {
    if (nominateSetterTeam && !nominateSetterDialogRef.current?.open) {
      nominateSetterDialogRef.current?.showModal();
    }
  }, [nominateSetterTeam]);

  useEffect(() => {
    if (!initialState.match?.sets || initialState.match.sets.length === 0) {
      loadAppliedRef.current = true;
      return;
    }
    let importedTailLineupA = null;
    let importedTailLineupB = null;
    const normalizeLoadedEvent = (event, setObj) => {
      const player = normalizePlayerShape(
        event.Player ??
          event.player ??
          inferPlayerByNumber(
            resolveEventTeam(event),
            event.shirtNumber ??
              event.Player?.ShirtNumber ??
              event.player?.ShirtNumber,
          ),
      );
      const teamKey = resolveEventTeam({ ...event, Player: player, player });
      const eventType =
        event.EventType ??
        eventTypeFromSkillChar(
          event.skill ?? skillCharFromEventType(event.EventType),
        );
      const skill = event.skill ?? skillCharFromEventType(eventType);
      const grade = event.EventGrade ?? 0;
      const timestamp =
        event.TimeStamp instanceof Date
          ? event.TimeStamp
          : event.TimeStamp
            ? new Date(event.TimeStamp)
            : event.timestamp
              ? new Date(event.timestamp * 10)
              : new Date();
      const codes = rebuildEventStrings({
        ...event,
        Player: player,
        player,
        team: teamKey,
        skill,
        EventType: eventType,
      });
      const sourceDVCode = event.dvString ?? event.raw ?? "";
      const lineupTail = parseLineupTailFromDVCode(sourceDVCode);
      if (lineupTail) {
        importedTailLineupA = lineupTail.teamA;
        importedTailLineupB = lineupTail.teamB;
      }
      // Derive zone fields for DVWFile-imported events where zoneFrom/zoneTo are not set.
      // DVWFile DV code layout: [team][NN][skill][type][eval][attackCombo(2)][?][startZone][endZone][subEndZone?]
      // Zones sit at character positions 9 and 10 of the first semicolon-delimited token.
      const rawDVCode0 = sourceDVCode.split(";")[0];
      // Position 4 is the subtype slot (H, Q, M, T, O…). Eval chars occupy that slot when
      // there is no subtype, so filter them out.
      const DV_EVAL_CHARS = new Set(["#", "+", "!", "-", "/", "="]);
      const rawTypeChar = rawDVCode0[4] ?? "";
      const derivedType =
        event.type ??
        (!DV_EVAL_CHARS.has(rawTypeChar) && rawTypeChar.trim() !== ""
          ? rawTypeChar
          : null);
      // Positions 6-7 = attack combo code (e.g. "V1", "X6") — only present for S/A with
      // full-length codes (≥11 chars).
      const rawCombo =
        rawDVCode0.length >= 11 ? rawDVCode0.substring(6, 8) : "";
      const derivedAttackCombo =
        event.attackCombo && event.attackCombo.trim()
          ? event.attackCombo
          : rawCombo.trim() !== ""
            ? rawCombo
            : "";
      const derivedZoneFrom =
        event.zoneFrom ??
        (/\d/.test(rawDVCode0[9] ?? "") ? rawDVCode0[9] : null);
      const derivedZoneTo =
        event.zoneTo ??
        (/\d/.test(rawDVCode0[10] ?? "") ? rawDVCode0[10] : null);
      const derivedSubzoneTo =
        event.subzoneTo ??
        (/[a-dA-D]/.test(rawDVCode0[11] ?? "")
          ? rawDVCode0[11].toLowerCase()
          : null);
      const derivedSubzoneFrom = event.subzoneFrom ?? null;
      return {
        ...event,
        id: event.id ?? event.EventId ?? generateUUID(),
        EventId: event.EventId ?? event.id ?? generateUUID(),
        team: teamKey,
        homePlayer: teamKey === "A",
        Player: player,
        player,
        PlayerGuid: player?.Guid ?? player?.guid ?? null,
        // Top-level shirt number is what DVWWriter.eventCode reads on save;
        // without it every serialised event loses its player (writes "00").
        shirtNumber:
          (event.shirtNumber != null && String(event.shirtNumber).trim() !== ""
            ? event.shirtNumber
            : null) ??
          player?.ShirtNumber ??
          player?.shirtNumber ??
          null,
        EventType: eventType,
        skill,
        skillLabel: SKILL_LABELS[skill] ?? skill,
        EventString: event.EventString ?? eventStringFromEventType(eventType),
        EventGrade: grade,
        DVGrade:
          event.DVGrade ?? event.evaluation ?? DV_GRADE_BY_INDEX[grade] ?? null,
        evaluation:
          event.evaluation ?? event.DVGrade ?? DV_GRADE_BY_INDEX[grade] ?? null,
        passingGrade:
          event.passingGrade ??
          (eventType === 2
            ? grade === 0
              ? 0
              : grade === 1
                ? 0.5
                : grade === 2
                  ? 1
                  : 3
            : 0),
        ErrorType:
          event.ErrorType ??
          (event.DVGrade === "=" || event.DVGrade === "/" ? 1 : 0),
        SubEvent: event.SubEvent ?? null,
        SubEvent2: event.SubEvent2 ?? null,
        type: derivedType,
        zoneFrom: derivedZoneFrom,
        subzoneFrom: derivedSubzoneFrom,
        zoneTo: derivedZoneTo,
        subzoneTo: derivedSubzoneTo,
        BallStartString:
          event.BallStartString ??
          `${derivedZoneFrom ?? ""}${derivedSubzoneFrom ?? ""}`,
        BallEndString:
          event.BallEndString ??
          `${derivedZoneTo ?? ""}${derivedSubzoneTo ?? ""}`,
        ExtraCode1: event.ExtraCode1 ?? event.type ?? "",
        ExtraCode2: event.ExtraCode2 ?? "",
        ExtraCode3: event.ExtraCode3 ?? "",
        settersCall: event.settersCall ?? event.setterCall ?? "",
        setterCall: event.setterCall ?? event.settersCall ?? "",
        attackCombo: derivedAttackCombo,
        NumberOfBlocks: event.NumberOfBlocks ?? "",
        Row: event.Row ?? 0,
        TeamScore: event.TeamScore ?? setObj?.pointsA ?? 0,
        OppositionScore: event.OppositionScore ?? setObj?.pointsB ?? 0,
        // Absolute per-event scores (A=home, B=away). DVW: TeamScore=homescore,
        // OppositionScore=awayscore (always home/away regardless of event team).
        // VIS: scoreA/scoreB set from rally r.score_a/r.score_b.
        scoreA: event.scoreA ?? event.TeamScore ?? 0,
        scoreB: event.scoreB ?? event.OppositionScore ?? 0,
        importedLineupA: lineupTail?.teamA ?? null,
        importedLineupB: lineupTail?.teamB ?? null,
        // Per-event on-court lineups. Reconstruct from the DV scout-line tail
        // (sm[14..25]) so the lineup panels reflect the selected event and a
        // re-save (DVWWriter.snapshotTwelve) keeps both teams' lineups — edits
        // rebuild dvString as a short code without the tail, so the snapshot is
        // the durable source of truth.
        LineupSnapshot:
          event.LineupSnapshot &&
          Array.isArray(event.LineupSnapshot.A) &&
          Array.isArray(event.LineupSnapshot.B)
            ? event.LineupSnapshot
            : lineupTail &&
                (lineupTail.teamA.some((n) => n != null) ||
                  lineupTail.teamB.some((n) => n != null))
              ? { A: lineupTail.teamA, B: lineupTail.teamB }
              : null,
        TimeStamp: timestamp,
        dvString: event.dvString ?? event.raw ?? codes.dvString,
        raw: event.raw ?? event.dvString ?? codes.raw,
      };
    };

    const normalizeLoadedSet = (setObj, idx) => {
      const normalizedSet = {
        ...setObj,
        setNumber: setObj.setNumber ?? setObj.GameNumber ?? idx + 1,
        pointsA: setObj.pointsA ?? setObj.HomeScore ?? 0,
        pointsB: setObj.pointsB ?? setObj.AwayScore ?? 0,
        winner: setObj.winner ?? null,
        events: [],
      };
      normalizedSet.events = (setObj.events ?? []).map((ev) =>
        normalizeLoadedEvent(ev, normalizedSet),
      );
      return normalizedSet;
    };

    const loadedSets = initialState.match.sets.map((setObj, idx) =>
      normalizeLoadedSet(setObj, idx),
    );
    if (loadedSets.length === 0) {
      loadAppliedRef.current = true;
      return;
    }
    setSets(loadedSets);
    // Resume at the LIVE point — the last coded event of the last set that has
    // any events — so the coder (and the video's onReady seek) picks up where
    // they left off instead of at the very first event. A restored autosave
    // draft may further refine this via `applySessionSnapshot`.
    let liveSetIdx = 0;
    for (let i = loadedSets.length - 1; i >= 0; i--) {
      if ((loadedSets[i]?.events ?? []).length > 0) {
        liveSetIdx = i;
        break;
      }
    }
    const liveEvents = loadedSets[liveSetIdx]?.events ?? [];
    setCurrentSetIdx(liveSetIdx);
    setSelectedEventId(
      liveEvents.length ? liveEvents[liveEvents.length - 1].id : null,
    );
    // Derive the live rotation counters + serving team straight from the loaded
    // events so the Draw & Code formation is correct on resume even without an
    // autosave draft to restore them (the load path sets the lineups but the
    // rotation counter would otherwise stay at its default of 1, skewing the
    // on-court formation). Mirrors `computeStateAtEvent`'s replay: physical
    // rotation = the starting rotation (from the FIRST event's scout-line setter
    // position, before any sub has happened) + the side-out count, which is
    // substitution-independent and therefore correct across (double) subs.
    {
      let serving = "A";
      let rotA = 1;
      let rotB = 1;
      const first = loadedSets?.[0]?.events?.[0];
      if (first) {
        const fs = parseScoutStateFromDVCode(first.dvString ?? first.raw ?? "");
        if (fs) {
          if (fs.setterPosA != null) {
            const r = kRotationSetterPositions.indexOf(fs.setterPosA) + 1;
            if (r > 0) rotA = r;
          }
          if (fs.setterPosB != null) {
            const r = kRotationSetterPositions.indexOf(fs.setterPosB) + 1;
            if (r > 0) rotB = r;
          }
        }
      }
      for (const s of loadedSets) {
        for (const ev of s.events ?? []) {
          if (ev.skill === "S" && !ev.compoundPaired) serving = ev.team;
          const winner = ev.compoundPaired ? null : rallyWinnerFor(ev);
          if (winner && serving !== winner) {
            if (winner === "A") rotA = (rotA % 6) + 1;
            else rotB = (rotB % 6) + 1;
            serving = winner;
          }
        }
      }
      setRotationA(rotA);
      setRotationB(rotB);
      setServingTeam(serving);
    }
    const initialLineupA = importedTailLineupA
      ? buildLineupFromNumbers(
          importedTailLineupA,
          teamA.players ?? teamA.lineup ?? [],
          teamA.currentLineup ?? teamA.lineup ?? [],
        )
      : (teamA.currentLineup ?? teamA.lineup ?? []);
    const initialLineupB = importedTailLineupB
      ? buildLineupFromNumbers(
          importedTailLineupB,
          teamB.players ?? teamB.lineup ?? [],
          teamB.currentLineup ?? teamB.lineup ?? [],
        )
      : (teamB.currentLineup ?? teamB.lineup ?? []);
    setLineupA(initialLineupA);
    setLineupB(initialLineupB);
    initialLineupsRef.current = { A: initialLineupA, B: initialLineupB };
    // Restore libero assignments persisted in the saved match object.
    if (initialState.match.liberoA != null)
      setLiberoA(normalizeLiberoList(initialState.match.liberoA));
    if (initialState.match.liberoB != null)
      setLiberoB(normalizeLiberoList(initialState.match.liberoB));
    // Restore each team's nominated setter so resuming a saved match doesn't
    // re-prompt the "nominate setter" modal (see the `nominateSetterTeam`
    // effect, which only fires when `setterA`/`setterB` is still null).
    if (initialState.match.setterA != null)
      setSetterA(initialState.match.setterA);
    if (initialState.match.setterB != null)
      setSetterB(initialState.match.setterB);
    // Restore the match squad + substitution-pair tracking persisted alongside
    // the buffer (the DVW scout format can't represent them). Guards against a
    // missing/legacy field so pre-existing matches keep their default squad.
    const normSquad = (arr) =>
      new Set(
        (arr ?? [])
          .map((v) => String(parseInt(String(v), 10)))
          .filter((v) => v !== "NaN"),
      );
    if (Array.isArray(initialState.match.matchSquadA) && initialState.match.matchSquadA.length)
      setMatchSquadA(normSquad(initialState.match.matchSquadA));
    if (Array.isArray(initialState.match.matchSquadB) && initialState.match.matchSquadB.length)
      setMatchSquadB(normSquad(initialState.match.matchSquadB));
    if (Array.isArray(initialState.match.subPairs))
      setSubPairs(initialState.match.subPairs);
    setHistory([]);
    setRedoStack([]);
    loadAppliedRef.current = true;
    // Loaded match initialization only needs to run when the incoming match changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState.match]);

  // Serialise the codeable session state into a plain object for autosave.
  const getSessionSnapshot = () => ({
    v: 1,
    matchId: matchIdRef.current,
    savedAt: Date.now(),
    sets: safeClone(sets),
    currentSetIdx,
    servingTeam,
    rotationA,
    rotationB,
    lineupA: safeClone(lineupA),
    lineupB: safeClone(lineupB),
    setterA,
    setterB,
    liberoA,
    liberoB,
    subPairs: safeClone(subPairs),
    drawMode,
    colourA,
    colourB,
    matchSquadA: [...(matchSquadA ?? [])],
    matchSquadB: [...(matchSquadB ?? [])],
  });

  // Re-hydrate Date fields that JSON serialisation flattened to strings.
  const reviveSets = (loadedSets) =>
    (loadedSets ?? []).map((s) => ({
      ...s,
      events: (s.events ?? []).map((ev) => ({
        ...ev,
        TimeStamp: ev.TimeStamp ? new Date(ev.TimeStamp) : new Date(),
      })),
    }));

  // Restore a previously serialised snapshot into component state.
  const applySessionSnapshot = (s) => {
    if (!s) return;
    if (Array.isArray(s.sets) && s.sets.length > 0) setSets(reviveSets(s.sets));
    if (
      typeof s.currentSetIdx === "number" &&
      Array.isArray(s.sets) &&
      s.sets.length > 0
    ) {
      setCurrentSetIdx(s.currentSetIdx);
    }
    if (s.servingTeam) setServingTeam(s.servingTeam);
    if (s.rotationA != null) setRotationA(s.rotationA);
    if (s.rotationB != null) setRotationB(s.rotationB);
    if (Array.isArray(s.lineupA) && s.lineupA.length > 0) setLineupA(s.lineupA);
    if (Array.isArray(s.lineupB) && s.lineupB.length > 0) setLineupB(s.lineupB);
    setSetterA(s.setterA ?? null);
    setSetterB(s.setterB ?? null);
    // Restore the last editing MODE (Draw & Code vs the DV-code court) so
    // resuming a match drops the coder straight back into the surface they
    // left in.
    if (typeof s.drawMode === "boolean") setDrawMode(s.drawMode);
    // Only restore liberos when the snapshot has a non-empty list so that an
    // older draft (saved before libero support) or a pre-setup snapshot doesn't
    // wipe libero assignments that were loaded from the database.
    const draftLiberosA = normalizeLiberoList(s.liberoA);
    const draftLiberosB = normalizeLiberoList(s.liberoB);
    if (draftLiberosA.length > 0) setLiberoA(draftLiberosA);
    if (draftLiberosB.length > 0) setLiberoB(draftLiberosB);
    if (Array.isArray(s.subPairs)) setSubPairs(s.subPairs);
    if (Array.isArray(s.matchSquadA)) setMatchSquadA(new Set(s.matchSquadA));
    if (Array.isArray(s.matchSquadB)) setMatchSquadB(new Set(s.matchSquadB));
    // Restore team colours (they aren't reconstructed from the DV scout tail).
    if (s.colourA) setColourA(s.colourA);
    if (s.colourB) setColourB(s.colourB);
    setHistory([]);
    setRedoStack([]);
    const curIdx = typeof s.currentSetIdx === "number" ? s.currentSetIdx : 0;
    const evs =
      Array.isArray(s.sets) && s.sets.length > 0
        ? (s.sets?.[curIdx]?.events ?? [])
        : [];
    setSelectedEventId(evs.length ? evs[evs.length - 1].id : null);
  };

  // Autosave bootstrap + writer. On the first settled render we capture the
  // initial snapshot (what "Discard" reverts to) and restore any autosaved
  // draft; thereafter every change is debounced-written to localStorage.
  useEffect(() => {
    if (!didInitRef.current) {
      const hasIncoming = !!initialState.match?.sets?.length;
      // Wait until the incoming match has been applied to state.
      if (hasIncoming && !loadAppliedRef.current) return;

      initialSnapshotRef.current = getSessionSnapshot();
      didInitRef.current = true;

      try {
        const raw = localStorage.getItem(autosaveKey());
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && Array.isArray(draft.sets)) {
            const hasIncoming = !!initialState.match?.sets?.length;
            const hasDraftEvents = draft.sets.some(
              (setObj) =>
                Array.isArray(setObj?.events) && setObj.events.length > 0,
            );
            // For loaded matches, ignore stale/empty drafts that can wipe
            // imported lineups/events. New matches still restore empty-event drafts.
            if (!hasIncoming || hasDraftEvents) {
              applySessionSnapshot(draft);
              toast.info(t("Resumed autosaved coding"));
            }
          }
        }
      } catch (e) {
        console.log("Failed to restore autosaved coding", e);
      }
      // On RESUME of an existing match (loaded from the DB — NOT a brand-new
      // match, which uses the setup modal), auto-open the video panel when the
      // match has a playable URL so the coder can watch it straight away; the
      // one-shot onReady seek then jumps it to the currently-selected event
      // (the last coded one — see the load effect / draft restore), ready to
      // resume live. Draw mode, if it was on when leaving, is restored from the
      // autosave draft by `applySessionSnapshot` above.
      if (!initialState.showSetupModal) {
        const matchUrl = initialState.match?.videoOnlineUrl;
        if (
          matchUrl &&
          matchUrl !== "?" &&
          matchUrl !== "-1" &&
          matchUrl !== -1
        ) {
          playerReadyRef.current = false;
          initialVideoSeekDoneRef.current = false;
          setVideoUrl(matchUrl);
          setShowVideo(true);
        }
      }
      return; // don't autosave on the bootstrap pass
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const payload = JSON.stringify(getSessionSnapshot());
      try {
        localStorage.setItem(autosaveKey(), payload);
      } catch (e) {
        if (e?.name === "QuotaExceededError" || e?.code === 22) {
          // localStorage may still be full of STALE drafts from other
          // matches (nothing else ever cleans these up) — free them and
          // retry once before giving up.
          try {
            Object.keys(localStorage)
              .filter(
                (k) =>
                  k.startsWith("VBLiveCodingDraft_") && k !== autosaveKey(),
              )
              .forEach((k) => localStorage.removeItem(k));
            localStorage.setItem(autosaveKey(), payload);
            return;
          } catch (e2) {
            console.log(
              "Failed to autosave coding (after clearing old drafts)",
              e2,
            );
            return;
          }
        }
        console.log("Failed to autosave coding", e);
      }
    }, 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sets,
    currentSetIdx,
    servingTeam,
    rotationA,
    rotationB,
    lineupA,
    lineupB,
    setterA,
    setterB,
    liberoA,
    liberoB,
    subPairs,
  ]);

  // Flush any pending autosave on unmount.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (didInitRef.current) {
        try {
          localStorage.setItem(
            autosaveKey(),
            JSON.stringify(getSessionSnapshot()),
          );
        } catch (e) {
          console.log("Failed to flush autosave on unmount", e);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revert all changes back to the initial state captured on entry.
  const discardChanges = () => {
    if (!initialSnapshotRef.current) return;
    if (
      !window.confirm(
        t("Discard all changes and revert to the starting point?"),
      )
    ) {
      return;
    }
    applySessionSnapshot(initialSnapshotRef.current);
    try {
      localStorage.setItem(
        autosaveKey(),
        JSON.stringify(initialSnapshotRef.current),
      );
    } catch (e) {
      console.log("Failed to reset autosave", e);
    }
    toast.info(t("Changes discarded"));
  };

  // ---------- save to database ----------
  // Serialise the coded match to DataVolley scout text and upload it so it
  // appears in the Matches List (and can be reopened for analysis/editing).
  const [saving, setSaving] = useState(false);
  const savedMatchRef = useRef(false); // guards the one-shot auto-save on match end

  const buildMatchForSave = () => {
    // Per-set starting lineups (fall back to current lineups for the live set).
    const setsForSave = sets.map((set, idx) => {
      const start = setStartLineupsRef.current[idx];
      const luA = idx === currentSetIdx ? lineupA : (start?.A ?? lineupA);
      const luB = idx === currentSetIdx ? lineupB : (start?.B ?? lineupB);
      return {
        pointsA: set.pointsA ?? 0,
        pointsB: set.pointsB ?? 0,
        events: set.events ?? [],
        lineupA: luA,
        lineupB: luB,
      };
    });

    const buffer = buildDVWMatchText({
      teamAName,
      teamBName,
      // Team colours are round-tripped through the DVW [3TEAMS] section so
      // they survive a save + resume-from-DB (otherwise they reverted to the
      // lime/magenta defaults on reload).
      colourA,
      colourB,
      tournamentName:
        initialState.tournamentName ?? initialState.match?.tournamentName ?? "",
      date: initialState.match?.TrainingDate ?? new Date(),
      rosterA: rosterARef.current,
      rosterB: rosterBRef.current,
      setterA,
      setterB,
      liberoA,
      liberoB,
      guid: matchIdRef.current,
      sets: setsForSave,
    });
    return {
      guid: matchIdRef.current,
      teamA: { Name: teamAName, colour: colourA },
      teamB: { Name: teamBName, colour: colourB },
      origin: "New",
      TrainingDate: initialState.match?.TrainingDate
        ? new Date(initialState.match.TrainingDate)
        : new Date(),
      tournamentName:
        initialState.tournamentName ?? initialState.match?.tournamentName ?? "",
      videoOnlineUrl: initialState.match?.videoOnlineUrl ?? videoUrl ?? null,
      liberoA,
      liberoB,
      // Live-coding state the DVW buffer can't represent, persisted so a
      // resume-from-DB restores the chosen match squad + substitution pairs
      // (not just the fragile localStorage autosave draft).
      matchSquadA: [...(matchSquadA ?? [])],
      matchSquadB: [...(matchSquadB ?? [])],
      subPairs: safeClone(subPairs),
      sets: setsForSave.map((s) => ({
        HomeScore: s.pointsA,
        AwayScore: s.pointsB,
      })),
      buffer,
    };
  };

  // `allowEmpty` lets the one-shot "lineups filled" auto-save (below) create
  // the match doc before any events are coded. `silent` suppresses the
  // success toast for that same background save. `markSaved` defaults to
  // true; passing false keeps `savedMatchRef` clear so the real "match over"
  // auto-save still fires later (a lineups-only save is not a full save).
  const saveMatchToDatabase = async (isAuto = false, opts = {}) => {
    const { allowEmpty = false, silent = false, markSaved = true } = opts;
    if (saving) return;
    if (!currentUser?.email) {
      if (!isAuto) toast.error(t("Please sign in to save this match."));
      return;
    }
    const hasEvents = sets.some((s) => (s.events ?? []).length > 0);
    if (!hasEvents && !allowEmpty) {
      if (!isAuto) toast.info(t("Nothing to save yet."));
      return;
    }
    setSaving(true);
    try {
      const match = buildMatchForSave();
      const ret = await storeSession(match, currentUser);
      if (ret) {
        if (!silent) toast.success(t("Match saved"));
        if (markSaved) savedMatchRef.current = true;
      } else if (!isAuto) {
        toast.error(t("Could not save the match. Please try again."));
      }
    } catch (e) {
      console.log("Failed to save match", e);
      if (!isAuto)
        toast.error(t("Could not save the match. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  // Auto-save once when the match is decided.
  useEffect(() => {
    if (matchOver && !savedMatchRef.current && currentUser?.email) {
      saveMatchToDatabase(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchOver, currentUser]);

  // Auto-save a brand-new match (from NewMatch's "Match Setup" flow) to the
  // database as soon as BOTH teams' lineups are filled in — so it appears in
  // the Matches List and, crucially, its nominated setters/lineups are
  // persisted (round-tripped via the DVW buffer) even before the first point
  // is coded, instead of only being saved once the match ends/is coded and
  // manually saved.
  const initialLineupSavedRef = useRef(false);
  useEffect(() => {
    if (!initialState.showSetupModal) return; // only for brand-new matches
    if (initialLineupSavedRef.current || !currentUser?.email) return;
    const hasShirts = (lu) =>
      (lu ?? []).some((p) => {
        const n = p?.shirtNumber ?? p?.ShirtNumber ?? p?.playerNumber;
        return n != null && String(n).trim() !== "";
      });
    if (hasShirts(lineupA) && hasShirts(lineupB)) {
      initialLineupSavedRef.current = true;
      saveMatchToDatabase(true, {
        allowEmpty: true,
        silent: true,
        markSaved: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineupA, lineupB, currentUser]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [currentSetIdx]);

  // Keep a snapshot of each set's lineup so LineupEditorModal can offer
  // "Use prev set". Updated whenever the set changes or lineup is edited.
  useEffect(() => {
    setStartLineupsRef.current[currentSetIdx] = {
      A: safeClone(lineupA),
      B: safeClone(lineupB),
    };
  }, [currentSetIdx, lineupA, lineupB]);

  // Capture Draw & Code's stable "starting lineup" once both teams' on-court
  // lineups have real shirt numbers (see startingLineupsRef above).
  useEffect(() => {
    if (startingLineupsCapturedRef.current) return;
    const hasShirts = (lu) =>
      (lu ?? []).some((p) => {
        const n = p?.shirtNumber ?? p?.ShirtNumber ?? p?.playerNumber;
        return n != null && String(n).trim() !== "";
      });
    if (hasShirts(lineupA) && hasShirts(lineupB)) {
      startingLineupsRef.current = {
        A: safeClone(lineupA),
        B: safeClone(lineupB),
      };
      startingLineupsCapturedRef.current = true;
    }
  }, [lineupA, lineupB]);

  // ---------- left/right column resizer ----------
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const next = Math.max(220, Math.min(rect.width - 280, x));
      setLeftWidth(next);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const startResize = () => {
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // ---------- events list / video resizer ----------
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingEventsRef.current || !videoRowRef.current) return;
      const rect = videoRowRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const next = Math.max(260, Math.min(rect.width - 200, rect.width - x));
      setEventsWidth(next);
    };
    const onUp = () => {
      draggingEventsRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const startEventsResize = () => {
    draggingEventsRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Keep stable refs pointing to the latest versions of these callbacks.
  // This allows the keyboard handler (registered once) to call the current
  // implementation without capturing a stale closure.
  const snapshot = () => ({
    sets: safeClone(sets),
    currentSetIdx,
    servingTeam,
    rotationA,
    rotationB,
    lineupA: safeClone(lineupA),
    lineupB: safeClone(lineupB),
    // Substitution-affected state. A substitution (MatchSetupModal onSave)
    // pushes a snapshot then mutates the setter/libero/subPairs/colours (and
    // the starting-lineup snapshot for a fresh lineup), but NONE of these used
    // to be captured here. Draw & Code derives each player's role (and thus
    // which back-row slot the libero substitutes into / which disc is drawn)
    // from setterA/B + liberoA/B, so undoing across a sub restored the lineup
    // but left the setter/libero stale — the on-court discs "messed up",
    // especially when new events were coded after an undo and then undone past
    // that point. Capture them so undo/redo fully round-trips.
    setterA,
    setterB,
    liberoA: safeClone(liberoA),
    liberoB: safeClone(liberoB),
    subPairs: safeClone(subPairs),
    colourA,
    colourB,
    startingLineups: safeClone(startingLineupsRef.current),
  });

  // Pushes the current state onto the undo history and clears the redo
  // stack (a new action invalidates any previously-undone redo entries).
  // Plain function (not useCallback) so it always reads the live state.
  const pushHistory = () => {
    setHistory((h) => [...h, snapshot()].slice(-200));
    setRedoStack([]);
  };

  // Plain functions (not useCallback) — they read `history`/`redoStack`
  // directly so they're always up to date; `undoRef`/`redoRef` are
  // reassigned every render so the keyboard handler always calls the latest
  // version without needing to be re-subscribed.
  //
  // Restores the substitution-affected state captured by `snapshot()`
  // (setter/libero/subPairs/colours/starting-lineup snapshot). Guarded per
  // key so a snapshot taken before these fields existed can't clobber live
  // state with `undefined`. `startingLineups` is a ref (not React state) so
  // it's assigned directly.
  const restoreSubState = (snap) => {
    const has = (k) => Object.prototype.hasOwnProperty.call(snap, k);
    if (has("setterA")) setSetterA(snap.setterA);
    if (has("setterB")) setSetterB(snap.setterB);
    if (has("liberoA")) setLiberoA(snap.liberoA);
    if (has("liberoB")) setLiberoB(snap.liberoB);
    if (has("subPairs")) setSubPairs(snap.subPairs);
    if (has("colourA")) setColourA(snap.colourA);
    if (has("colourB")) setColourB(snap.colourB);
    if (has("startingLineups") && snap.startingLineups) {
      startingLineupsRef.current = snap.startingLineups;
    }
  };

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setRedoStack((r) => [...r, snapshot()].slice(-200));
    setHistory((h) => h.slice(0, -1));
    setSets(last.sets);
    setCurrentSetIdx(last.currentSetIdx);
    setServingTeam(last.servingTeam);
    setRotationA(last.rotationA);
    setRotationB(last.rotationB);
    setLineupA(last.lineupA);
    setLineupB(last.lineupB);
    restoreSubState(last);
    // Undo/redo restore a PAST `sets` snapshot, but `selectedEventId` is
    // separate React state and isn't part of that snapshot — left alone, it
    // keeps pointing at whatever event was selected before the undo, which
    // is usually the just-undone (now removed) last event. Since it no
    // longer matches the new last event, `isViewingLiveEvent` (which
    // requires `selectedEventId === lastEvent.id`) turns false and the left
    // column silently swaps out of the live Draw & Code surface into the
    // read-only court diagram — exactly the "court view changed, should
    // remain in Draw Live mode" bug. Re-point selection at the (new) last
    // event of the restored set, same as `goLive`, so the live view sticks.
    const restoredSet = last.sets[last.currentSetIdx];
    const restoredEvents = restoredSet?.events ?? [];
    setSelectedEventId(
      restoredEvents.length
        ? restoredEvents[restoredEvents.length - 1].id
        : null,
    );
    setReviewingEvent(false);
  };
  undoRef.current = undo;

  const redo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setHistory((h) => [...h, snapshot()].slice(-200));
    setRedoStack((r) => r.slice(0, -1));
    setSets(next.sets);
    setCurrentSetIdx(next.currentSetIdx);
    setServingTeam(next.servingTeam);
    setRotationA(next.rotationA);
    setRotationB(next.rotationB);
    setLineupA(next.lineupA);
    setLineupB(next.lineupB);
    restoreSubState(next);
    // Same stale-`selectedEventId` fix as `undo` (see its comment) — keep
    // selection pinned to the restored set's last event so the live Draw &
    // Code view doesn't silently drop back to the read-only diagram.
    const restoredSet = next.sets[next.currentSetIdx];
    const restoredEvents = restoredSet?.events ?? [];
    setSelectedEventId(
      restoredEvents.length
        ? restoredEvents[restoredEvents.length - 1].id
        : null,
    );
    setReviewingEvent(false);
  };
  redoRef.current = redo;

  const resolvePlayerForEvent = (
    teamKey,
    shirtNumber,
    lineupAState,
    lineupBState,
  ) => {
    if (shirtNumber == null || shirtNumber === "--") return null;
    const normalizedNumber = String(parseInt(String(shirtNumber), 10));
    if (!normalizedNumber || normalizedNumber === "NaN") return null;
    const lineup = teamKey === "A" ? lineupAState : lineupBState;
    const roster = teamKey === "A" ? rosterARef.current : rosterBRef.current;
    const searchPool = [...(lineup ?? []), ...(roster ?? [])];
    const matches = searchPool.filter((player) => {
      const playerNumber =
        player?.shirtNumber ?? player?.playerNumber ?? player?.ShirtNumber;
      return String(parseInt(String(playerNumber), 10)) === normalizedNumber;
    });
    if (matches.length === 0) return null;
    // A lineup can hold an unnamed placeholder for a shirt number; prefer a
    // candidate that actually carries a name so the event never records a blank.
    const hasName = (p) =>
      !!firstNonEmpty(
        p?.NickName,
        p?.nickName,
        p?.FirstName,
        p?.firstName,
        p?.LastName,
        p?.lastName,
        p?.playerFirstName,
        p?.playerLastName,
      );
    const best = matches.find(hasName) ?? matches[0];
    return normalizePlayerShape(best);
  };

  const positionForPlayer = (player, lineup) => {
    if (!player || !lineup) return 0;
    const playerNumber = String(
      parseInt(
        String(
          player.shirtNumber ?? player.playerNumber ?? player.ShirtNumber ?? "",
        ),
        10,
      ),
    );
    const found = lineup.find((candidate) => {
      const candidateNumber =
        candidate?.shirtNumber ??
        candidate?.playerNumber ??
        candidate?.ShirtNumber;
      return String(parseInt(String(candidateNumber), 10)) === playerNumber;
    });
    const position = parseInt(
      found?.position ?? found?.currentPosition ?? "0",
      10,
    );
    return Number.isFinite(position) ? position : 0;
  };

  // Snapshot a lineup as a 6-slot array of shirt numbers ordered by court
  // position 1..6.  Reflects whatever rotations/substitutions the live lineup
  // currently encodes (positions are updated on rotation; players are swapped
  // on substitution).  Empty slots are returned as "".
  const lineupPositionNumbers = (lineup) => {
    const slots = ["", "", "", "", "", ""];
    (lineup ?? []).forEach((p) => {
      const pos = parseInt(String(p?.position ?? p?.currentPosition ?? ""), 10);
      const num = p?.shirtNumber ?? p?.playerNumber ?? p?.ShirtNumber;
      if (Number.isFinite(pos) && pos >= 1 && pos <= 6 && num != null) {
        slots[pos - 1] = String(parseInt(String(num), 10));
      }
    });
    return slots;
  };

  const hydrateEventForReports = (
    ev,
    setObj,
    lineupAState,
    lineupBState,
    eventWallTimeMs,
    timeOffsetCs = 0,
    scoreAOverride,
    scoreBOverride,
    rotationASnapshot,
    rotationBSnapshot,
  ) => {
    const lineup = ev.team === "A" ? lineupAState : lineupBState;
    const player = resolvePlayerForEvent(
      ev.team,
      ev.shirtNumber,
      lineupAState,
      lineupBState,
    );
    // Scores to record on the event. Default to the set's live running score,
    // but applyEvents passes the PRE-rally score (the score as it stood BEFORE
    // this rally's point) so a scoring event shows that pre-point score rather
    // than the score its own point produced — matching imported matches and
    // what the user expects.
    const displayPointsA = scoreAOverride ?? setObj.pointsA;
    const displayPointsB = scoreBOverride ?? setObj.pointsB;
    const teamScore = ev.team === "A" ? displayPointsA : displayPointsB;
    const oppositionScore = ev.team === "A" ? displayPointsB : displayPointsA;
    // `grade` (0, 0.5, 1, 2, 3) is a passing/hitting EFFICIENCY value used only
    // for `passingGrade` stats — it is NOT the same scale as `EventGrade`,
    // which EventEditor.jsx (and CodingPage.jsx) treat as an ARRAY INDEX into
    // `["=", "/", "!", "-", "+", "#"]` (see `DV_GRADE_BY_INDEX`). Keep both.
    const grade = EVENT_GRADE_BY_EVAL[ev.evaluation] ?? null;
    const eventGradeIndexLookup = DV_GRADE_BY_INDEX.indexOf(ev.evaluation);
    const eventGradeIndex =
      eventGradeIndexLookup >= 0 ? eventGradeIndexLookup : null;
    const eventType = EVENT_TYPE_BY_SKILL[ev.skill] ?? 0;
    const timestampMs = eventWallTimeMs - timeOffsetCs * 10;
    const eventString = `${SKILL_LABELS[ev.skill] ?? ev.skill ?? ""}${ev.skill ? " " : ""}`;
    const ballStartString = ev.zoneFrom
      ? `${ev.zoneFrom}${ev.subzoneFrom ?? ""}`
      : "";
    const ballEndString = ev.zoneTo ? `${ev.zoneTo}${ev.subzoneTo ?? ""}` : "";
    const passingGrade = eventType === 2 ? grade : 0;
    const eventId = ev.EventId ?? String(ev.id ?? generateUUID());
    Object.assign(ev, {
      id: ev.id ?? eventId,
      EventId: eventId,
      Player: player,
      player,
      PlayerGuid: player?.Guid ?? player?.guid ?? null,
      // Stable primitive copy of the resolved name/number captured while the
      // roster is available.  Used as a serialization-proof display fallback.
      PlayerName: firstNonEmpty(
        player?.NickName,
        player?.nickName,
        player?.FirstName,
        player?.firstName,
        player?.playerFirstName,
        player?.LastName,
        player?.lastName,
        player?.playerLastName,
        ev.PlayerName,
      ),
      EventType: ev.eventTypeOverride ?? eventType,
      EventString: ev.eventStringOverride ?? eventString,
      EventGrade: eventGradeIndex,
      DVGrade: ev.evaluation ?? null,
      SubEvent: ev.subEvent ?? null,
      SubEvent2: ev.subEvent2 ?? null,
      ErrorType: ev.evaluation === "=" || ev.evaluation === "/" ? 1 : 0,
      VideoPosition:
        showVideo && videoUrl && typeof ev.timestamp === "number"
          ? ev.timestamp / 100
          : ev.VideoPosition,
      BallStartString: ballStartString,
      BallEndString: ballEndString,
      ExtraCode1: ev.type ?? "",
      ExtraCode2: "",
      ExtraCode3: "",
      setterCall: ev.setterCall ?? "",
      settersCall: ev.settersCall ?? ev.setterCall ?? "",
      attackCombo: ev.attackCombo ?? "",
      NumberOfBlocks: ev.NumberOfBlocks ?? "",
      Row: positionForPlayer(player, lineup),
      Drill: {
        GameNumber: setObj.setNumber ?? currentSetIdx + 1,
        HomeScore: displayPointsA,
        AwayScore: displayPointsB,
        match: matchMeta,
      },
      TeamScore: teamScore,
      OppositionScore: oppositionScore,
      scoreA: displayPointsA,
      scoreB: displayPointsB,
      passingGrade,
      substitution: ev.substitution ?? null,
      setter: ev.setter ?? null,
      dvString: ev.dvString ?? ev.raw ?? "",
      TimeStamp: new Date(timestampMs),
      homePlayer: ev.team === "A",
      // On-court lineups (by position 1..6) at the moment of this event,
      // reflecting rotations and substitutions made up to this point.
      LineupSnapshot: {
        A: lineupPositionNumbers(lineupAState),
        B: lineupPositionNumbers(lineupBState),
      },
      // The team ROTATION counters at the moment of this event, captured
      // ALONGSIDE LineupSnapshot so any later state reconstruction
      // (`computeStateAtEvent` -> event-row clicks / set nav / "return to
      // live") restores the lineup POSITIONS and the rotation COUNTER from the
      // SAME moment. Previously the lineup came from LineupSnapshot but the
      // rotation from a separate rotateTeamSafely replay (which starts from a
      // hardcoded serving team and doesn't apply substitutions) — when the two
      // disagreed, `selectEventAndRestoreSnapshot` wrote a DESYNCED
      // (lineup, rotation) pair into live state, which then got baked into
      // undo snapshots and showed as "discs in the wrong slots after several
      // undos". Only set when the caller supplies them (draw/typed coding);
      // omitted for paths that don't, so `hasOwnProperty` guards downstream
      // fall back to the legacy replay for older events.
      ...(rotationASnapshot != null && rotationBSnapshot != null
        ? { RotationSnapshot: { A: rotationASnapshot, B: rotationBSnapshot } }
        : {}),
    });
    return ev;
  };

  const validatePlayer = (ev) => {
    if (ev.shirtNumber === "--") return true; // manual / macro placeholder
    const num = String(parseInt(ev.shirtNumber, 10));
    // Always accept designated liberos — they substitute in/out and may not
    // appear in the current court lineup.
    const liberos = ev.team === "A" ? liberoA : liberoB;
    if (isLiberoNumber(liberos, num)) return true;
    const lineup = ev.team === "A" ? lineupA : lineupB;
    if (!lineup || lineup.length === 0) return true;
    const found = lineup.find((p) => String(p.shirtNumber) === num);
    return !!found;
  };

  // Rotate a team clockwise (volleyball side-out rotation):
  //   2 -> 1 (becomes the new server)
  //   1 -> 6
  //   6 -> 5
  //   5 -> 4
  //   4 -> 3
  //   3 -> 2
  // Returns a new immutable lineup array and the next rotation index (1..6).
  const ROTATE_NEXT = { 1: "6", 2: "1", 3: "2", 4: "3", 5: "4", 6: "5" };
  const rotateTeamSafely = (team, lineup, rotation) => {
    const newLineup = (lineup || []).map((p) => {
      const cur = String(p.position ?? p.currentPosition ?? "");
      const nxt = ROTATE_NEXT[cur] ?? cur;
      return { ...p, position: nxt, currentPosition: nxt };
    });
    const nextRotation = (rotation % 6) + 1;
    return { lineup: newLineup, rotation: nextRotation };
  };

  // Build a synthetic auto-serve event for a reception that begins a new rally.
  // Uses the player at position "1" (server) of the currently serving team.
  const buildAutoServeEvent = (recEv, currentServingTeam, lineupOverride) => {
    if (recEv.team === currentServingTeam) return null; // R must be by receiving team
    const lineup =
      lineupOverride ?? (currentServingTeam === "A" ? lineupA : lineupB);
    const server = lineup?.find((p) => String(p.position) === "1");
    const num = server?.shirtNumber;
    if (!num && num !== 0) return null;
    const padNum = String(num).padStart(2, "0");
    const evalChar = R_TO_S_EVAL[recEv.evaluation] ?? "!";
    const teamMark = currentServingTeam === "A" ? "*" : "a";
    return {
      id: generateUUID(),
      raw: `${teamMark}${padNum}S${recEv.type ?? ""}${evalChar} (auto)`,
      team: currentServingTeam,
      shirtNumber: padNum,
      skill: "S",
      skillLabel: SKILL_LABELS["S"],
      type: recEv.type ?? null,
      evaluation: evalChar,
      evaluationLabel: EVAL_LABELS[evalChar],
      zoneFrom: null,
      zoneTo: recEv.zoneFrom ?? null,
      auto: true,
    };
  };

  // Determine if the next event would begin a new rally
  // (current set has no events, or the last event is a rally-ender).
  const isStartOfRally = (setObj) => {
    if (!setObj || setObj.events.length === 0) return true;
    // Substitutions (skill "C") are administrative and don't belong to the
    // rally flow — skip any trailing ones so a sub made between rallies
    // doesn't make it look like we're still mid-rally (which would drop the
    // Draw & Code court out of the serving phase into "attack").
    let i = setObj.events.length - 1;
    while (i >= 0 && setObj.events[i]?.skill === "C") i--;
    if (i < 0) return true;
    return rallyWinnerFor(setObj.events[i]) !== null;
  };

  // Apply a sequence of events atomically using local working state so that
  // chained events (e.g. auto-serve + reception) all see each other's effects.
  const applyEvents = (incoming) => {
    if (!incoming || incoming.length === 0) return;
    console.log(
      "[applyEvents] events:",
      incoming.map(
        (e) =>
          `${e.team}${e.skill}${e.evaluation}${e.compoundPaired ? "(cp)" : ""}`,
      ),
      "| serving:",
      servingTeam,
      "| lineupB positions:",
      lineupB.map(
        (p) =>
          `#${p.shirtNumber ?? p.playerNumber}@${p.position ?? p.currentPosition ?? "?"}`,
      ),
    );
    pushHistory();

    let workingSets = sets.map((s) => ({ ...s, events: [...s.events] }));
    let workingSetIdx = currentSetIdx;
    let workingServing = servingTeam;
    let workingLineupA = lineupA;
    let workingLineupB = lineupB;
    let workingRotA = rotationA;
    let workingRotB = rotationB;
    let matchEnded = false;
    let lastAppendedEventId = null;
    const lastTimestamp = incoming[incoming.length - 1]?.timestamp ?? null;
    const lastWallTime = Date.now();
    // The pre-point score of the most recent SCORING event in this batch, so a
    // compoundPaired partner (same rally, no point of its own) shows the same
    // pre-rally score as its scorer instead of the already-incremented one.
    let rallyPreA = null;
    let rallyPreB = null;

    for (let idx = 0; idx < incoming.length; idx++) {
      const ev = incoming[idx];
      if (matchEnded) break;
      const set = workingSets[workingSetIdx];
      const currentWallOffset =
        lastTimestamp !== null && typeof ev.timestamp === "number"
          ? lastTimestamp - ev.timestamp
          : (incoming.length - 1 - idx) * 50;

      // Compound-paired tokens (e.g. R= from a compound S#.R=) must not score
      // an additional point — the partnered serve token already scored.
      const winner = ev.compoundPaired ? null : rallyWinnerFor(ev);

      // Score to DISPLAY on this event = the score BEFORE this rally's point.
      // A scorer reads the set's current (pre-increment) score; its
      // compoundPaired partner reuses the scorer's captured pre-point score.
      const dispA =
        ev.compoundPaired && rallyPreA != null ? rallyPreA : set.pointsA;
      const dispB =
        ev.compoundPaired && rallyPreB != null ? rallyPreB : set.pointsB;

      if (winner) {
        rallyPreA = set.pointsA;
        rallyPreB = set.pointsB;
        if (winner === "A") set.pointsA += 1;
        else set.pointsB += 1;

        if (workingServing !== winner) {
          // side-out: rotate the new serving team
          if (winner === "A") {
            const r = rotateTeamSafely(teamA, workingLineupA, workingRotA);
            console.log(
              "[rotation] Team A rotated. Before:",
              workingLineupA.map((p) => `#${p.shirtNumber}@${p.position}`),
              "After:",
              r.lineup.map((p) => `#${p.shirtNumber}@${p.position}`),
            );
            workingLineupA = r.lineup;
            workingRotA = r.rotation;
          } else {
            const r = rotateTeamSafely(teamB, workingLineupB, workingRotB);
            console.log(
              "[rotation] Team B rotated. Before:",
              workingLineupB.map((p) => `#${p.shirtNumber}@${p.position}`),
              "After:",
              r.lineup.map((p) => `#${p.shirtNumber}@${p.position}`),
            );
            workingLineupB = r.lineup;
            workingRotB = r.rotation;
          }
          workingServing = winner;
        }

        const setWinner = isSetWon(
          set,
          workingSetIdx === 4,
          effSetWinScore,
          effFinalSetWinScore,
          effWinByTwo,
        );
        if (setWinner) {
          set.winner = setWinner;
          const wonA = workingSets.filter((s) => s.winner === "A").length;
          const wonB = workingSets.filter((s) => s.winner === "B").length;
          if (wonA < SETS_TO_WIN && wonB < SETS_TO_WIN) {
            workingSets.push(emptySet(workingSets.length + 1));
            workingSetIdx = workingSets.length - 1;
            workingServing = workingServing === "A" ? "B" : "A";
          } else {
            toast.success(
              `Match won by ${setWinner === "A" ? teamAName : teamBName}`,
            );
            matchEnded = true;
          }
        }
      }
      hydrateEventForReports(
        ev,
        set,
        workingLineupA,
        workingLineupB,
        lastWallTime - currentWallOffset * 10,
        0,
        dispA,
        dispB,
        workingRotA,
        workingRotB,
      );
      set.events.push(ev);
      lastAppendedEventId = ev.id ?? null;
    }

    setSets(workingSets);
    setCurrentSetIdx(workingSetIdx);
    setServingTeam(workingServing);
    setLineupA(workingLineupA);
    setLineupB(workingLineupB);
    setRotationA(workingRotA);
    setRotationB(workingRotB);
    if (lastAppendedEventId) {
      setSelectedEventId(lastAppendedEventId);
      setReviewingEvent(false);
    }
  };

  const applyEvent = (ev) => applyEvents([ev]);

  // Manual rally winner button: synthesize a "Point" terminal event.
  const awardPoint = (team) => {
    if (matchOver) {
      toast.info("Match is already over.");
      return;
    }
    const teamMark = team === "A" ? "*" : "a";
    const ev = {
      id: generateUUID(),
      raw: `${teamMark}--P# (manual)`,
      team,
      shirtNumber: "00",
      skill: "P",
      skillLabel: "Point",
      type: null,
      evaluation: "#",
      evaluationLabel: "Manual point",
      zoneFrom: null,
      subzoneFrom: null,
      zoneTo: null,
      subzoneTo: null,
      manual: true,
      timestamp: getTimestamp(),
    };
    applyEvent(ev);
  };
  awardPointRef.current = awardPoint;

  // Manually ends the current set at its current score, overriding the
  // automatic win-score/win-by-two detection (isSetWon) — lets the coder
  // close a set at any score for matches that don't follow standard rules
  // (shortened sets, no-advantage finishes, custom caps, etc.).
  const closeCurrentSet = () => {
    if (matchOver) {
      toast.info("Match is already over.");
      return;
    }
    const set = sets[currentSetIdx];
    if (!set || set.pointsA === set.pointsB) {
      toast.error(
        "Scores are tied — award a point to break the tie before closing the set.",
      );
      return;
    }
    const winner = set.pointsA > set.pointsB ? "A" : "B";
    const workingSets = sets.map((s, i) =>
      i === currentSetIdx ? { ...s, winner } : s,
    );
    const wonA = workingSets.filter((s) => s.winner === "A").length;
    const wonB = workingSets.filter((s) => s.winner === "B").length;
    if (wonA < SETS_TO_WIN && wonB < SETS_TO_WIN) {
      workingSets.push(emptySet(workingSets.length + 1));
      setSets(workingSets);
      setCurrentSetIdx(workingSets.length - 1);
      setServingTeam((prev) => (prev === "A" ? "B" : "A"));
    } else {
      setSets(workingSets);
      toast.success(`Match won by ${winner === "A" ? teamAName : teamBName}`);
    }
  };

  // Global keyboard shortcuts.
  // Modified shortcuts (Ctrl/Alt/Shift) fire even when the input has focus.
  useEffect(() => {
    const handler = (e) => {
      const sc = activeShortcuts;
      const inputFocused = document.activeElement === inputRef.current;
      const matches = (def) =>
        e.key.toLowerCase() === def.key.toLowerCase() &&
        !!e.ctrlKey === !!def.ctrl &&
        !!e.shiftKey === !!def.shift &&
        !!e.altKey === !!def.alt;
      // Unmodified single-char shortcuts pass through to the input.
      const isModified = (def) => def.ctrl || def.shift || def.alt;
      if (matches(sc.pointHome)) {
        if (!isModified(sc.pointHome) && inputFocused) return;
        e.preventDefault();
        awardPointRef.current?.("A");
      } else if (matches(sc.pointAway)) {
        if (!isModified(sc.pointAway) && inputFocused) return;
        e.preventDefault();
        awardPointRef.current?.("B");
      } else if (matches(sc.undoLast)) {
        if (!isModified(sc.undoLast) && inputFocused) return;
        e.preventDefault();
        undoRef.current?.();
      } else if (matches(sc.redoLast)) {
        if (!isModified(sc.redoLast) && inputFocused) return;
        e.preventDefault();
        redoRef.current?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShortcuts]);

  // Returns the current timestamp in centiseconds.
  // When a video is loaded, returns the video position * 100.
  // Otherwise returns wall-clock centiseconds (Date.now / 10).
  const getTimestamp = () => {
    if (showVideo && videoUrl && playerRef.current) {
      return Math.round(playerRef.current.getCurrentTime() * 100);
    }
    return Math.round(Date.now() / 10);
  };

  // Applies a batch of events produced by the "Draw & Code" court (drag-drawn
  // ball paths). DVMatchCoding's own engine (applyEvents/rallyWinnerFor) still
  // owns scoring, rotation, and set boundaries — DrawAndCodeCourt only decides
  // who touched the ball, what skill/result, and the DV zones.
  const handleDrawCodeEvents = (events) => {
    if (!events || events.length === 0) return;
    if (matchOver) {
      toast.info("Match is already over.");
      return;
    }
    const baseTs = getTimestamp();
    events.forEach((ev, i) => {
      ev.timestamp = baseTs - (events.length - 1 - i) * 50;
    });
    applyEvents(events);
  };

  const submitCode = (overrideCode = null) => {
    if (matchOver) {
      toast.info("Match is already over.");
      return;
    }
    const trimmed = (overrideCode ?? codeInput).trim();
    if (!trimmed) return;

    // 1. Expand aliases / macros (pa, pb, sa, se, s+, s!, etc.)
    const expanded = expandAliases(trimmed, {
      servingTeam,
      lineupA,
      lineupB,
    });

    // 2. Split into raw event tokens (handles chaining, dot-pairs, skill/type/eval
    //    omission, sub-zones, and team-marker inheritance).
    const split = splitCompound(expanded, {
      ...activeDefaults,
      liberoA,
      liberoB,
    });
    if (split.error) {
      setParseError(split.error);
      return;
    }
    const tokens = split.tokens; // keep full objects; .raw used below
    if (tokens.length === 0) return;

    // Build a flat list of events to apply atomically (so auto-serve sees the
    // same working state as the reception that triggered it).
    const toApply = [];
    let simulatedServing = servingTeam;
    let simulatedLineupA = lineupA;
    let simulatedLineupB = lineupB;
    let simulatedRotA = rotationA;
    let simulatedRotB = rotationB;
    let simulatedSet = {
      events: [...(currentSet?.events ?? [])],
    };

    for (const tok of tokens) {
      const result = parseDVCode(tok.raw);
      if (!result.ok) {
        setParseError(`"${tok.raw}": ${result.error}`);
        return;
      }
      if (!validatePlayer(result.event)) {
        const pn = result.event.shirtNumber;
        setParseError(
          `"${tok.raw}": player #${pn === "--" ? pn : parseInt(pn, 10)} not in lineup of team ${
            result.event.team
          }`,
        );
        return;
      }

      const ev = result.event;
      // Carry the compound-pair flag onto the event so applyEvents skips scoring.
      if (tok.compoundPaired) ev.compoundPaired = true;

      // Auto-insert serve when an R starts a new rally for the receiving team.
      // Skip for compound-paired receptions — the serve was already explicitly
      // coded as the first half of the dot-pair.
      if (
        !tok.compoundPaired &&
        ev.skill === "R" &&
        isStartOfRally(simulatedSet) &&
        ev.team !== simulatedServing
      ) {
        const serveLineup =
          simulatedServing === "A" ? simulatedLineupA : simulatedLineupB;
        const serveEv = buildAutoServeEvent(ev, simulatedServing, serveLineup);
        if (serveEv) {
          toApply.push(serveEv);
          simulatedSet.events.push(serveEv);
          // When this synthetic serve is terminal (e.g. R= -> S#), mark the
          // paired reception as compoundPaired so only one rally point is scored.
          if (rallyWinnerFor(serveEv) !== null) {
            ev.compoundPaired = true;
          }
        }
      }

      toApply.push(ev);
      simulatedSet.events.push(ev);

      // Update simulated serving team / lineups if this event ends the rally.
      // Compound-paired tokens don't end the rally (their partner already did).
      const w = ev.compoundPaired ? null : rallyWinnerFor(ev);
      if (w) {
        if (w !== simulatedServing) {
          // side-out: rotate the new serving team
          if (w === "A") {
            const r = rotateTeamSafely(teamA, simulatedLineupA, simulatedRotA);
            simulatedLineupA = r.lineup;
            simulatedRotA = r.rotation;
          } else {
            const r = rotateTeamSafely(teamB, simulatedLineupB, simulatedRotB);
            simulatedLineupB = r.lineup;
            simulatedRotB = r.rotation;
          }
          simulatedServing = w;
        }
        simulatedSet = { events: [] }; // new rally
      }
    }

    // Stamp timestamps: last event = now, each prior event is 50cs (0.5s) earlier.
    const baseTs = getTimestamp();
    toApply.forEach((ev, i) => {
      ev.timestamp = baseTs - (toApply.length - 1 - i) * 50;
    });

    applyEvents(toApply);

    setCodeInput("");
    setParseError(null);
  };

  const onInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitCode();
    } else if (e.key === "Escape") {
      setCodeInput("");
      setParseError(null);
    }
  };

  // ---------- TouchCoding (TC) helpers ------------------------------------

  // Extract the last evaluation character from the current input string.
  const tcLastEval = codeInput.match(/[#+!\-/=](?=[^#+!\-/=]*$)/)?.[0] ?? null;

  // Derive the current TC rally stage from input + set state.
  const tcStage = (() => {
    if (!tcMode) return null;
    const input = codeInput.trim();
    if (input) {
      // If the last token (after final dot) contains an R skill → awaiting attack chain
      const tail = input.split(".").pop() ?? "";
      if (/^[*a]?\d+R/i.test(tail)) return "attack";
      return "eval";
    }
    return isStartOfRally(currentSet) ? "receive" : "attack";
  })();

  // Called when a player cell is tapped in TC mode.
  const onTCPlayerTap = (team, shirtNumber) => {
    const n = parseInt(String(shirtNumber), 10);
    if (!Number.isFinite(n)) return;
    const teamMark = team === "A" ? "*" : "a";
    const jersey = String(n).padStart(2, "0");
    const input = codeInput.trim();

    if (!input && isStartOfRally(currentSet)) {
      // Start of rally: only the receiving team may receive
      const receivingTeam = servingTeam === "A" ? "B" : "A";
      if (team !== receivingTeam) return;
      const dType = activeDefaults.type?.R ?? DEFAULT_TYPE.R;
      const dEval = activeDefaults.eval?.R ?? DEFAULT_EVAL.R;
      setCodeInput(`${teamMark}${jersey}R${dType}${dEval}`);
    } else if (!input) {
      // Input is empty, mid-rally (previous compound was just submitted).
      // If the last event was a blocked attack (/), the next tap on the
      // opposing (blocking) team produces a block kill (B#).
      const lastEv = currentSet?.events?.[currentSet.events.length - 1];
      const lastEval = lastEv?.evaluation ?? lastEv?.DVGrade ?? null;
      if (lastEv?.skill === "A" && lastEval === "/" && lastEv.team !== team) {
        // Block kill: add directly as a compoundPaired event so it is recorded
        // as a stat without triggering a second point (the A/ already ended the rally).
        const bResult = parseDVCode(`${teamMark}${jersey}B#`);
        if (bResult.ok) {
          const bEv = bResult.event;
          bEv.compoundPaired = true;
          bEv.timestamp = getTimestamp();
          applyEvents([bEv]);
        }
        inputRef.current?.focus();
        return;
      }
      // Otherwise start a plain attack
      const dType = activeDefaults.type?.A ?? DEFAULT_TYPE.A;
      const dEval = activeDefaults.eval?.A ?? DEFAULT_EVAL.A;
      setCodeInput(`${teamMark}${jersey}A${dType}${dEval}`);
    } else {
      // Input already has content: append attack as compound dot chain.
      // In transition, if the previous action in the chain was also an attack
      // by the same team, the ball stayed on their side — recode it as a dig (D).
      const dType = activeDefaults.type?.A ?? DEFAULT_TYPE.A;
      const dEval = activeDefaults.eval?.A ?? DEFAULT_EVAL.A;
      let base = input;
      const lastDot = input.lastIndexOf(".");
      const tail = lastDot >= 0 ? input.slice(lastDot + 1) : input;
      // Same-team attack token: [*a]\d{2}A{type}{eval} → recode skill A→D, drop type char
      if (tail[0] === teamMark && /^[*a]\d{2}A./i.test(tail)) {
        const fixedTail = tail.replace(/^([*a]\d{2})A(.)/, "$1D");
        base =
          lastDot >= 0 ? input.slice(0, lastDot + 1) + fixedTail : fixedTail;
      }
      const prefix = base ? `${base}.` : "";
      setCodeInput(`${prefix}${teamMark}${jersey}A${dType}${dEval}`);
    }
    inputRef.current?.focus();
  };

  // Called when an evaluation button is tapped in TC mode.
  // Replaces the last eval char in the input then immediately submits.
  const onTCEvalTap = (evalChar) => {
    const input = codeInput.trim();
    if (!input) return;
    const newCode = input.replace(/[#+!\-/=](?=[^#+!\-/=]*$)/, evalChar);
    if (newCode === input) return;
    // When the last coded action is a reception (tcStage === "attack" means
    // a reception is drafted and we're awaiting the next attack), just update
    // the eval in the input bar so the user can chain the attack next.
    // Exception: "=" (error) terminates the rally immediately.
    if (tcStage === "attack" && evalChar !== "=") {
      setCodeInput(newCode);
    } else {
      submitCode(newCode);
    }
  };

  const selectedEvent =
    currentSet?.events.find((ev) => ev.id === selectedEventId) ?? null;
  const selectedEventTeamObj = selectedEvent?.team === "B" ? teamB : teamA;
  const selectedEventLineup = selectedEvent?.team === "B" ? lineupB : lineupA;
  const selectedEventEditorTeam = {
    ...(selectedEventTeamObj ?? {}),
    currentLineup: (selectedEventLineup ?? []).map((p) => ({
      ...p,
      playerNumber: p.playerNumber ?? p.shirtNumber ?? p.ShirtNumber,
      shirtNumber: p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber,
    })),
    players: (selectedEventTeamObj?.players ?? []).map((p) =>
      normalizePlayerShape(p),
    ),
  };
  const selectedEventEditorValue = selectedEvent
    ? {
        ...selectedEvent,
        player: {
          ...(selectedEvent.player ?? selectedEvent.Player ?? {}),
          ShirtNumber:
            selectedEvent?.player?.ShirtNumber ??
            selectedEvent?.player?.shirtNumber ??
            selectedEvent?.Player?.ShirtNumber ??
            selectedEvent?.Player?.shirtNumber ??
            selectedEvent?.shirtNumber,
        },
      }
    : null;

  // Keep the selected event row visible whenever it changes, the panels are
  // resized, or the event editor's height changes for a different event type.
  useEffect(() => {
    if (!selectedEventId) return;
    const id = requestAnimationFrame(() => {
      selectedRowRef.current?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [selectedEventId, eventsPanelWidth, selectedEvent?.EventType]);

  // Keep the event editor a CONSTANT height regardless of the skill being
  // edited. The Attack editor is the tallest (type-of-hit + 3 extra codes +
  // attack combos), so track the greatest height the editor content has ever
  // rendered at and pin the panel to it via min-height — shorter skills then
  // simply leave blank space below instead of shrinking the panel and making
  // the layout jump.
  const editorContentRef = useRef(null);
  const [editorMinHeight, setEditorMinHeight] = useState(0);
  useLayoutEffect(() => {
    const el = editorContentRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) setEditorMinHeight((prev) => (h > prev ? h : prev));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => ro?.disconnect();
  }, [selectedEvent?.id, selectedEvent?.EventType, eventsPanelWidth]);

  const onSelectedEventPlayerChanged = (player) => {
    const normalized = normalizePlayerShape(player);
    const nextRow =
      (selectedEventLineup ?? []).find(
        (p) =>
          String(
            parseInt(
              String(p.shirtNumber ?? p.playerNumber ?? p.ShirtNumber ?? ""),
              10,
            ),
          ) ===
          String(
            parseInt(
              String(normalized?.ShirtNumber ?? normalized?.shirtNumber ?? ""),
              10,
            ),
          ),
      )?.position ?? 0;
    patchSelectedEvent({
      Player: normalized,
      player: normalized,
      PlayerGuid: normalized?.Guid ?? normalized?.guid ?? null,
      shirtNumber: String(
        normalized?.ShirtNumber ?? normalized?.shirtNumber ?? "",
      ).padStart(2, "0"),
      Row: nextRow,
    });
  };

  const onSelectedEventSkillChanged = (eventType) => {
    const skill = skillCharFromEventType(eventType);
    patchSelectedEvent({
      EventType: eventType,
      skill,
      EventString: eventStringFromEventType(eventType),
      eventTypeOverride: undefined,
      eventStringOverride: undefined,
    });
  };

  const onSelectedEventSubskillChanged = (subEvent) => {
    patchSelectedEvent({ SubEvent: subEvent });
  };

  const onSelectedEventSetterCallChanged = (setterCall) => {
    patchSelectedEvent({ setterCall, settersCall: setterCall });
  };

  const onSelectedEventAttackComboChanged = (attackCombo) => {
    patchSelectedEvent({ attackCombo });
  };

  const onSelectedEventTypeChanged = (type) => {
    patchSelectedEvent({ type });
  };

  const onSelectedEventBlocksChanged = (NumberOfBlocks) => {
    patchSelectedEvent({ NumberOfBlocks });
  };

  const onSelectedEventExtCode1Changed = (val) => {
    patchSelectedEvent({ ExtraCode1: val });
  };

  const onSelectedEventExtCode2Changed = (val) => {
    patchSelectedEvent({ ExtraCode2: val });
  };

  const onSelectedEventExtCode3Changed = (val) => {
    patchSelectedEvent({ ExtraCode3: val });
  };

  const onSelectedEventGradeChanged = (grade) => {
    patchSelectedEvent({
      EventGrade: grade,
      DVGrade: DV_GRADE_BY_INDEX[grade] ?? null,
      // `EVAL_COLORS[ev.evaluation]`/`ev.evaluation` (not `DVGrade`) is what the
      // Events List row actually renders — without this, editing the result
      // in Event Editor left the Events List showing the old grade.
      evaluation: DV_GRADE_BY_INDEX[grade] ?? null,
      ErrorType: grade <= 1 ? 1 : 0,
      passingGrade:
        selectedEvent?.EventType === 2
          ? grade === 0
            ? 0
            : grade === 1
              ? 0.5
              : grade === 2
                ? 1
                : 3
          : 0,
    });
  };

  // Position layout, viewed from team A's perspective with the net at the
  // top (vertical) or right (horizontal). Team B is rendered rotated 180°
  // so the two front rows face each other across the net.
  //
  // Vertical (3 cols):  front=4,3,2  back=5,6,1   → A: [4,3,2,5,6,1]
  // Horizontal (2 cols): back|front per row       → A: [5,4, 6,3, 1,2]
  const POS_A_VERTICAL = ["4", "3", "2", "5", "6", "1"];
  const POS_A_HORIZONTAL = ["5", "4", "6", "3", "1", "2"];

  // ── Inline substitutions (tap a number in the lineups panel) ──────────────
  // Count of substitutions used by a team this set = number of substitution
  // ("C") events (robust for both live and resumed matches, unlike summing
  // `subPairs` which isn't rebuilt on resume).
  const subsUsedForTeam = (team) => {
    let n = 0;
    (currentSet?.events ?? []).forEach((ev) => {
      if (ev?.team === team && (ev?.skill === "C" || ev?.EventType === 250))
        n += 1;
    });
    return n;
  };

  // The most recent substitution that put `currentShirt` on court for this team
  // with NO rally event coded since (only trailing "C" events after it). Lets a
  // just-made sub be corrected/undone by re-subbing the same position without
  // counting as a new substitution. Returns { id, outNum, inNum } or null.
  const findUndoableSub = (team, currentShirt) => {
    const events = currentSet?.events ?? [];
    const curNum = String(parseInt(String(currentShirt), 10));
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      const isSub = ev?.skill === "C" || ev?.EventType === 250;
      if (!isSub) break; // a real (rally) event since → not undoable
      if (ev?.team !== team) continue;
      const code = String(ev?.substitution ?? ev?.dvString ?? ev?.raw ?? "");
      const m = code.match(/c\s*(\d+)\s*:\s*(\d+)/i);
      if (!m) continue;
      const inNum = String(parseInt(m[2], 10));
      if (inNum === curNum) {
        return { id: ev.id, outNum: String(parseInt(m[1], 10)), inNum };
      }
    }
    return null;
  };

  // Remove one exchange of the {a,b} substitution pair (decrement its count, or
  // drop the pair when it reaches zero).
  const removeSubPairFrom = (pairs, a, b) => {
    const na = String(parseInt(String(a), 10));
    const nb = String(parseInt(String(b), 10));
    const idx = (pairs ?? []).findIndex((p) => {
      const p1 = String(parseInt(String(p.p1), 10));
      const p2 = String(parseInt(String(p.p2), 10));
      return (p1 === na && p2 === nb) || (p1 === nb && p2 === na);
    });
    if (idx < 0) return pairs ?? [];
    const next = [...pairs];
    const cur = next[idx];
    if ((cur.count ?? 1) <= 1) next.splice(idx, 1);
    else next[idx] = { ...cur, count: cur.count - 1 };
    return next;
  };

  // Substitution history for a team this set: [{ out, in, score }] where score
  // is the team-relative score (this team's points - opponent's) when it was
  // made. Built from the set's "C" events.
  const subHistoryForTeam = (team) => {
    const rows = [];
    (currentSet?.events ?? []).forEach((ev) => {
      if (ev?.team !== team) return;
      if (ev?.skill !== "C" && ev?.EventType !== 250) return;
      const code = String(ev?.substitution ?? ev?.dvString ?? ev?.raw ?? "");
      const m = code.match(/c\s*(\d+)\s*:\s*(\d+)/i);
      if (!m) return;
      let score = "";
      if (ev.TeamScore != null && ev.OppositionScore != null)
        score = `${ev.TeamScore}-${ev.OppositionScore}`;
      else if (ev.scoreA != null && ev.scoreB != null)
        score = `${ev.scoreA}-${ev.scoreB}`;
      rows.push({
        out: String(parseInt(m[1], 10)),
        in: String(parseInt(m[2], 10)),
        score,
      });
    });
    return rows;
  };

  const openSubHistory = (team, anchorEl) => {
    const rect = anchorEl?.getBoundingClientRect?.();
    setSubHistoryPopup({
      team,
      x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
      y: rect ? rect.bottom + 4 : window.innerHeight / 2,
    });
  };

  const onCourtShirtSet = (team) => {
    const lineup = team === "A" ? lineupA : lineupB;
    return new Set(
      (lineup ?? [])
        .map((p) =>
          String(
            parseInt(
              String(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? ""),
              10,
            ),
          ),
        )
        .filter((n) => n !== "NaN"),
    );
  };

  // Shirt numbers already involved in a substitution this set (subbed IN or
  // OUT). They can't be picked from the sub list again — a subbed-out starter's
  // only legal return is the AUTOMATIC re-entry when their own replacement is
  // tapped (see `autoSwapPartner`). Built from BOTH the live `subPairs` AND the
  // set's substitution ("C") events, so it also works on a resumed match (where
  // `subPairs` isn't rebuilt) and regardless of which path made the sub.
  // Normalised int-strings.
  const usedSubShirts = (team) => {
    const s = new Set();
    (subPairs[currentSetIdx]?.[team] ?? []).forEach((pr) => {
      [pr.p1, pr.p2].forEach((v) => {
        const n = String(parseInt(String(v), 10));
        if (n !== "NaN") s.add(n);
      });
    });
    (currentSet?.events ?? []).forEach((ev) => {
      if (ev?.team !== team) return;
      if (ev?.skill !== "C" && ev?.EventType !== 250) return;
      const code = String(ev?.substitution ?? ev?.dvString ?? ev?.raw ?? "");
      const m = code.match(/c\s*(\d+)\s*:\s*(\d+)/i);
      if (m) {
        s.add(String(parseInt(m[1], 10)));
        s.add(String(parseInt(m[2], 10)));
      }
    });
    return s;
  };

  // A roster player is a libero if they're in the team's designated libero list
  // (chosen in the setup popup) OR carry the libero role tag.
  const isLiberoPlayer = (team, p, num) => {
    const liberoSet = new Set(
      normalizeLiberoList(team === "A" ? liberoA : liberoB).map((n) =>
        String(parseInt(String(n), 10)),
      ),
    );
    if (liberoSet.has(num)) return true;
    const role = String(p?.role1 ?? p?.Role1 ?? p?.role ?? "").toUpperCase();
    return (
      role === "L" || role === "LIB" || role === "LIBERO" || hasLiberoPosition(p)
    );
  };

  // Players who may be substituted IN for `outShirt`: match-squad members who
  // are NOT on court, NOT a libero, and have NOT already been subbed in/out
  // this set. `showAll` (full roster) also offers roster players outside the
  // squad, but only while it has room (MAX_MATCH_SQUAD); picking one adds them
  // to the match. FIVB pair/cap rules (via `checkSub`) still apply unless free
  // subs are on.
  const subCandidates = (team, outShirt, showAll) => {
    const roster = team === "A" ? rosterARef.current : rosterBRef.current;
    const pairs = subPairs[currentSetIdx]?.[team] ?? [];
    const squad = team === "A" ? matchSquadA : matchSquadB;
    const canAddNew = (squad?.size ?? 0) < MAX_MATCH_SQUAD;
    const onCourt = onCourtShirtSet(team);
    const used = usedSubShirts(team);
    const seen = new Set();
    const out = [];
    (roster ?? []).forEach((p) => {
      const raw = p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber;
      if (raw == null || String(raw).trim() === "") return;
      const num = String(parseInt(String(raw), 10));
      if (num === "NaN" || seen.has(num)) return;
      // Exclude on-court players, liberos, and anyone already subbed this set.
      if (onCourt.has(num) || used.has(num) || isLiberoPlayer(team, p, num))
        return;
      const inSquad = squad ? squad.has(num) : true;
      // Non-squad players only surface via "full roster" and only when there's
      // still room in the squad to add them.
      if (!inSquad && (!showAll || !canAddNew)) return;
      if (!freeSubstitutions) {
        const chk = checkSub(outShirt, raw, pairs, effMaxSubs);
        if (!chk.ok) return;
      }
      seen.add(num);
      out.push(normalizePlayerShape({ ...p, shirtNumber: raw }));
    });
    return out.sort(
      (a, b) =>
        parseInt(a.shirtNumber || 0, 10) - parseInt(b.shirtNumber || 0, 10),
    );
  };

  // When the clicked (on-court) player is a substitute who came in for a
  // starter, return that starter's shirt (their only FIVB-legal re-entry
  // partner, currently off court) so the swap can be applied automatically.
  const autoSwapPartner = (team, outEntry) => {
    const pairs = subPairs[currentSetIdx]?.[team] ?? [];
    const onCourt = onCourtShirtSet(team);
    const outNum = String(parseInt(String(outEntry.shirtNumber), 10));
    for (const pr of pairs) {
      if ((pr.count ?? 0) >= 2) continue;
      const p1 = String(parseInt(String(pr.p1), 10));
      const p2 = String(parseInt(String(pr.p2), 10));
      if (p1 === outNum || p2 === outNum) {
        const partnerRaw = p1 === outNum ? pr.p2 : pr.p1;
        const partnerNum = String(parseInt(String(partnerRaw), 10));
        if (!onCourt.has(partnerNum)) return partnerRaw;
      }
    }
    return null;
  };

  // Perform the substitution: swap the on-court player at `position` for the
  // incoming player, append a "C" substitution event, record the sub pair, and
  // move any setter designation to the incoming player.
  //
  // CORRECTION CASE: if the current player at `position` was subbed in with no
  // rally coded since (see `findUndoableSub`), this action corrects that sub
  // instead of adding a new one — the prior "C" event and its pair are removed
  // first, and a fresh event is only created if the incoming player differs
  // from the one who was there BEFORE the mistaken sub (a pure restore adds
  // nothing, so the sub count is unchanged).
  const applyInlineSub = (team, position, outShirtRaw, inShirtRaw, inPlayerRaw) => {
    const teamChar = team === "A" ? "" : "a";
    const norm = (v) => String(parseInt(String(v), 10));
    const lineup = team === "A" ? lineupA : lineupB;
    const roster = team === "A" ? rosterARef.current : rosterBRef.current;
    const outEntry = (lineup ?? []).find(
      (p) => String(p.position) === String(position),
    );

    const undoable = findUndoableSub(team, outShirtRaw);
    const effectiveOut = undoable ? undoable.outNum : norm(outShirtRaw);
    const inNum = norm(inShirtRaw);
    const isRestore = !!undoable && inNum === undoable.outNum;

    // Resolve the incoming player object (for a restore we may need to look the
    // original player up from the roster).
    let inSource = inPlayerRaw;
    if (!inSource) {
      inSource = (roster ?? []).find(
        (p) =>
          norm(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? "") === inNum,
      );
    }
    const inNorm = inSource ? normalizePlayerShape(inSource) : null;
    const inEntry = {
      ...(inNorm ?? {}),
      shirtNumber: inShirtRaw,
      ShirtNumber: inShirtRaw,
      position: outEntry?.position ?? position,
      currentPosition:
        outEntry?.currentPosition ?? outEntry?.position ?? position,
    };
    const newLineup = (lineup ?? []).map((p) =>
      String(p.position) === String(position) ? inEntry : p,
    );

    pushHistory();

    // Start from the set's events, dropping the sub we're correcting (if any).
    let events = currentSet?.events ?? [];
    if (undoable) events = events.filter((e) => e.id !== undoable.id);

    // Only create a new "C" event when this isn't a pure restore.
    if (!isRestore) {
      const rawOut = norm(effectiveOut).padStart(2, "0");
      const rawIn = norm(inShirtRaw).padStart(2, "0");
      const rawCode = `${teamChar}c${rawOut}:${rawIn}`;
      const ev = {
        id: generateUUID(),
        EventId: generateUUID(),
        raw: rawCode,
        dvString: rawCode,
        team,
        shirtNumber: rawOut,
        skill: "C",
        skillLabel: "Sub",
        type: null,
        evaluation: null,
        evaluationLabel: null,
        zoneFrom: null,
        subzoneFrom: null,
        zoneTo: null,
        subzoneTo: null,
        timestamp: getTimestamp(),
        TimeStamp: new Date(),
        eventTypeOverride: 250,
        eventStringOverride: "Substitution ",
        EventType: 250,
        EventString: "Substitution ",
        EventGrade: null,
        DVGrade: null,
        SubEvent: null,
        SubEvent2: null,
        ErrorType: 0,
        BallStartString: "",
        BallEndString: "",
        ExtraCode1: "",
        ExtraCode2: "",
        ExtraCode3: "",
        setterCall: "",
        settersCall: "",
        attackCombo: "",
        NumberOfBlocks: "",
        homePlayer: team === "A",
        passingGrade: 0,
        substitution: rawCode,
        setter: null,
      };
      const cs = sets[currentSetIdx];
      hydrateEventForReports(
        ev,
        cs,
        team === "A" ? newLineup : lineupA,
        team === "B" ? newLineup : lineupB,
        Date.now(),
        0,
        undefined,
        undefined,
        rotationA,
        rotationB,
      );
      events = [...events, ev];
    }

    if (team === "A") setLineupA(newLineup);
    else setLineupB(newLineup);
    setSets((prev) =>
      prev.map((s, i) => (i === currentSetIdx ? { ...s, events } : s)),
    );
    const lastEv = events[events.length - 1];
    setSelectedEventId(lastEv ? lastEv.id : null);
    setReviewingEvent(false);

    setSubPairs((prev) =>
      prev.map((s, i) => {
        if (i !== currentSetIdx) return s;
        let teamPairs = s[team] ?? [];
        if (undoable)
          teamPairs = removeSubPairFrom(
            teamPairs,
            undoable.outNum,
            undoable.inNum,
          );
        if (!isRestore)
          teamPairs = applySubPair(effectiveOut, inShirtRaw, teamPairs);
        return { ...s, [team]: teamPairs };
      }),
    );

    // A roster player pulled in from outside the current match squad joins it
    // (respecting the squad cap). Not for a pure restore.
    if (!isRestore) {
      const squad = team === "A" ? matchSquadA : matchSquadB;
      if (squad && !squad.has(inNum) && squad.size < MAX_MATCH_SQUAD) {
        (team === "A" ? setMatchSquadA : setMatchSquadB)((prev) => {
          const next = new Set(prev ?? []);
          next.add(inNum);
          return next;
        });
      }
    }

    // Setter designation follows the outgoing player off / the incoming on.
    const setterVal = team === "A" ? setterA : setterB;
    if (
      setterVal != null &&
      String(parseInt(String(setterVal), 10)) === norm(outShirtRaw)
    ) {
      (team === "A" ? setSetterA : setSetterB)(inNum);
    }

    setSubPopup(null);
    setSubPopupShowAll(false);
  };

  // Tap handler for a player number in the lineups panel.
  const openSubPopup = (team, position, anchorEl) => {
    const lineup = team === "A" ? lineupA : lineupB;
    const outEntry = (lineup ?? []).find(
      (p) => String(p.position) === String(position),
    );
    if (
      !outEntry ||
      outEntry.shirtNumber == null ||
      outEntry.shirtNumber === ""
    )
      return;
    // Substitutions are a mid-set action — before the first rally, the starting
    // lineup is arranged via "Edit Lineup".
    if ((currentSet?.events?.length ?? 0) === 0) return;
    // A just-made sub (no rally since) can always be corrected, even at the cap.
    const undoable = findUndoableSub(team, outEntry.shirtNumber);
    // Re-substitution: a sub going back out for the starter they replaced is
    // applied automatically (unless free substitutions are enabled).
    if (!freeSubstitutions) {
      const partner = autoSwapPartner(team, outEntry);
      if (partner != null) {
        if (!undoable && subsUsedForTeam(team) >= effMaxSubs) {
          toast.info(t("Maximum substitutions reached"));
          return;
        }
        const roster = team === "A" ? rosterARef.current : rosterBRef.current;
        const inRaw = (roster ?? []).find(
          (p) =>
            String(
              parseInt(
                String(p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? ""),
                10,
              ),
            ) === String(parseInt(String(partner), 10)),
        );
        applyInlineSub(
          team,
          position,
          outEntry.shirtNumber,
          inRaw?.shirtNumber ?? partner,
          inRaw,
        );
        return;
      }
    }
    // New substitution: enforce the cap unless we're correcting an uncommitted
    // (no-rally-since) sub.
    if (!undoable && !freeSubstitutions && subsUsedForTeam(team) >= effMaxSubs) {
      toast.info(t("Maximum substitutions reached"));
      return;
    }
    const rect = anchorEl?.getBoundingClientRect?.();
    setSubPopupShowAll(false);
    setSubPopup({
      team,
      position: String(position),
      shirt: String(outEntry.shirtNumber),
      x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
      y: rect ? rect.bottom + 4 : window.innerHeight / 2,
    });
  };

  const renderLineup = (
    team,
    lineup,
    name,
    rotation,
    isServing,
    setter,
    libero,
    slotIsA,
  ) => {
    const isA = team === "A";
    // Use each team's OWN configured colour (not the theme's fixed
    // primary/secondary) for every team-identity accent in this panel.
    const teamColour = isA ? colourA : colourB;
    const teamContrast = contrastFor(teamColour);
    // slotIsA = the box occupies team-A's default physical slot (bottom in
    // vertical / left in horizontal). It controls front-row orientation and
    // libero placement so both teams' front rows face the net regardless of
    // which team is shown on which side (swap sides). Defaults to isA.
    if (slotIsA == null) slotIsA = isA;
    // In Draw & Code mode the lineup panels are purely a reference display
    // (the diagram itself is what's interacted with) — shrink them so more
    // vertical space goes to the court.
    const compact = drawMode;
    const baseOrder =
      orientation === "vertical" ? POS_A_VERTICAL : POS_A_HORIZONTAL;
    // The box NOT in team-A's slot is rotated 180° so its front row sits next
    // to the net.
    const order = slotIsA ? baseOrder : [...baseOrder].reverse();
    const gridCols = orientation === "vertical" ? "grid-cols-3" : "grid-cols-2";
    const accentStyle = isServing
      ? {
          borderColor: teamColour,
          boxShadow: `0 0 0 1px ${rgbaFromColour(teamColour, 0.3)}`,
        }
      : { borderColor: rgbaFromColour(teamColour, 0.4) };
    const labels = getRotationLabels();
    // Prefer deriving the rotation row from the setter's current court position
    // in the shown lineup, then map that row via user naming convention.
    const setterNum =
      setter != null && setter !== "" ? String(parseInt(setter, 10)) : null;
    const setterPos = setterNum
      ? parseInt(
          String(
            lineup?.find((p) => {
              const shirt = p?.shirtNumber ?? p?.playerNumber ?? p?.ShirtNumber;
              return String(parseInt(String(shirt ?? ""), 10)) === setterNum;
            })?.position ??
              lineup?.find((p) => {
                const shirt =
                  p?.shirtNumber ?? p?.playerNumber ?? p?.ShirtNumber;
                return String(parseInt(String(shirt ?? ""), 10)) === setterNum;
              })?.currentPosition ??
              "",
          ),
          10,
        )
      : NaN;
    const derivedRow = Number.isFinite(setterPos)
      ? kRotationSetterPositions.indexOf(setterPos) + 1
      : 0;
    const rowNo = derivedRow > 0 ? derivedRow : rotation;
    const rotationLabel =
      rowNo > 0 && rowNo <= labels.length
        ? labels[rowNo - 1]
        : `R${rowNo ?? ""}`;
    const liberoNums = normalizeLiberoList(libero);
    // "Subs n/m" chip shown on the libero row (opposite end to the liberos).
    // Tap to view this team's substitution pairs and the score at each.
    const subsChip = (
      <div className="relative shrink-0">
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (subHistoryPopup?.team === team) setSubHistoryPopup(null);
            else openSubHistory(team, e.currentTarget);
          }}
          className={`rounded bg-base-300/70 hover:bg-base-300 cursor-pointer font-semibold text-base-content/80 leading-tight whitespace-nowrap ${
            compact ? "px-1 py-0.5 text-[10px]" : "px-2 py-1 text-xs"
          }`}
          title={t("Substitutions made — tap to view")}
        >
          {t("Subs")} {subsUsedForTeam(team)}/{effMaxSubs}
        </button>
        {subHistoryPopup?.team === team && (
          <div
            data-subhistory="1"
            className="fixed z-50 w-52 max-h-64 overflow-auto overscroll-contain rounded-lg border border-base-300 bg-base-100 shadow-xl p-2 text-left"
            style={{
              left: subHistoryPopup.x,
              top: subHistoryPopup.y,
              transform: "translateX(-50%)",
              WebkitOverflowScrolling: "touch",
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1 pb-1 border-b border-base-300">
              <span className="text-[11px] font-semibold truncate">
                {t("Substitutions")} · {name}
              </span>
              <button
                className="btn btn-ghost btn-xs btn-square"
                title={t("Close")}
                onClick={() => setSubHistoryPopup(null)}
              >
                ✕
              </button>
            </div>
            {subHistoryForTeam(team).length === 0 ? (
              <div className="text-[11px] text-base-content/50 px-1 py-2 text-center">
                {t("No substitutions")}
              </div>
            ) : (
              <ul className="space-y-0.5">
                {subHistoryForTeam(team).map((r, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between text-xs gap-2"
                  >
                    <span className="whitespace-nowrap">
                      <span className="font-bold text-error">{r.out}</span>
                      {" → "}
                      <span className="font-bold text-success">{r.in}</span>
                    </span>
                    <span className="text-base-content/60 tabular-nums">
                      {r.score || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
    return (
      <div
        className={`bg-base-200 rounded ${compact ? "p-1" : "p-2"} flex-1 ${
          compact ? "min-w-[110px]" : "min-w-[160px]"
        } border-t-4`}
        style={accentStyle}
      >
        <div className="flex items-center mb-1 gap-1">
          {isServing && (
            <span
              title={
                (currentSet?.events?.length ?? 0) === 0
                  ? "Serving — click to swap"
                  : "Serving"
              }
              className={`inline-flex items-center gap-0.5 rounded-md font-bold shadow-sm ${
                compact ? "px-1 py-0 text-[12px]" : "px-2 py-0.5 text-[12px]"
              }${(currentSet?.events?.length ?? 0) === 0 ? " cursor-pointer hover:brightness-110" : ""}`}
              style={{ backgroundColor: teamColour, color: teamContrast }}
              onClick={
                (currentSet?.events?.length ?? 0) === 0
                  ? () => setServingTeam((prev) => (prev === "A" ? "B" : "A"))
                  : undefined
              }
            >
              🏐{compact ? "" : " SRV"}
            </span>
          )}
          <div
            className={`font-bold truncate flex-1 ${compact ? "text-sm" : ""}`}
          >
            <span style={{ color: teamColour }}>{isA ? "*" : "a"}</span> {name}
          </div>
          {drawMode && (
            <label
              className="flex items-center gap-0.5 text-[12px] text-base-content/60 cursor-pointer select-none shrink-0"
              title={t(
                "Opposition Passing — use the opposition's reception formation on the diagram",
              )}
            >
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={isA ? isOppPassingA : isOppPassingB}
                onChange={(e) =>
                  (isA ? setIsOppPassingA : setIsOppPassingB)(e.target.checked)
                }
              />
              {t("OPP REC")}
            </label>
          )}
          <span
            className={`text-base-content font-bold ml-1 ${compact ? "text-[12px]" : "text-[12px]"}`}
          >
            {rotationLabel}
          </span>
        </div>
        {lineup && lineup.length > 0 ? (
          <>
            {/* Libero indicator — above pos 5 for vertical top panel */}
            {orientation === "vertical" && !slotIsA && (
              <div
                className={`flex items-center justify-between mb-1 ${compact ? "gap-1" : "gap-1.5"}`}
              >
                <div className={`flex ${compact ? "gap-1" : "gap-1.5"}`}>
                  {liberoNums.map((n) => {
                  const rcvTeam = servingTeam === "A" ? "B" : "A";
                  const badgeHighlighted =
                    tcMode &&
                    ((tcStage === "receive" && team === rcvTeam) ||
                      tcStage === "attack" ||
                      tcStage === "eval");
                  return (
                    <div
                      key={n}
                      onClick={
                        tcMode ? () => onTCPlayerTap(team, n) : undefined
                      }
                      className={`text-center rounded bg-info/10 ring-1 ring-info/40 ring-dashed leading-tight ${
                        compact
                          ? "px-1 py-0.5 min-w-[1.5rem] text-[10px]"
                          : "px-3 py-1.5 min-w-[2.5rem] text-sm"
                      }${
                        badgeHighlighted
                          ? " ring-accent/80 ring-2 cursor-pointer active:scale-95 transition-transform brightness-110"
                          : tcMode
                            ? " cursor-pointer opacity-50"
                            : ""
                      }`}
                    >
                      <span className="font-semibold text-info">L{n}</span>
                    </div>
                  );
                  })}
                </div>
                {subsChip}
              </div>
            )}
            <div
              className={`grid ${gridCols} ${compact ? "gap-0.5" : "gap-1"}`}
            >
              {order.map((pos) => {
                const p = lineup.find((x) => String(x.position) === pos);
                const shirt =
                  p?.shirtNumber != null && p?.shirtNumber !== ""
                    ? String(parseInt(p.shirtNumber, 10))
                    : null;
                const setterNum =
                  setter != null && setter !== ""
                    ? String(parseInt(setter, 10))
                    : null;
                const liberoNumSet = new Set(liberoNums.map((n) => String(n)));
                const isSetter =
                  shirt != null && setterNum != null && shirt === setterNum;
                const isLibero = shirt != null && liberoNumSet.has(shirt);
                const isServer = isServing && pos === "1";
                // TC mode: highlight tappable cells based on rally stage.
                const receivingTeamForTC = servingTeam === "A" ? "B" : "A";
                const isTCTappable = tcMode && shirt != null;
                const isTCHighlighted =
                  isTCTappable &&
                  ((tcStage === "receive" && team === receivingTeamForTC) ||
                    tcStage === "attack" ||
                    tcStage === "eval");
                // Tap-to-substitute: enabled once the set is under way (a sub
                // is a mid-set action). TC mode keeps its own tap behaviour.
                const subClickable =
                  !tcMode &&
                  shirt != null &&
                  (currentSet?.events?.length ?? 0) > 0;
                const isSubPopupOpen =
                  subPopup?.team === team && subPopup?.position === pos;
                return (
                  <div
                    key={pos}
                    onClick={
                      isTCTappable
                        ? () => onTCPlayerTap(team, shirt)
                        : subClickable
                          ? (e) => openSubPopup(team, pos, e.currentTarget)
                          : undefined
                    }
                    className={`relative text-center rounded ${compact ? "px-0.5 py-0 text-[9px]" : "px-0.5 py-0.5 text-xs"} ${
                      isSetter
                        ? "bg-warning/15 ring-1 ring-warning/60"
                        : isLibero
                          ? "bg-info/15 ring-1 ring-info/60"
                          : !isServer
                            ? "bg-base-100"
                            : ""
                    } ${
                      isTCHighlighted
                        ? "ring-2 ring-accent/80 cursor-pointer active:scale-95 transition-transform brightness-110"
                        : isTCTappable
                          ? "cursor-pointer opacity-50"
                          : subClickable
                            ? "cursor-pointer hover:ring-1 hover:ring-primary/50"
                            : ""
                    }${isSubPopupOpen ? " ring-2 ring-primary" : ""}`}
                    style={
                      isServer && !isSetter && !isLibero
                        ? {
                            backgroundColor: rgbaFromColour(teamColour, 0.25),
                            boxShadow: `0 0 0 2px ${rgbaFromColour(teamColour, 0.7)}`,
                          }
                        : undefined
                    }
                  >
                    <div
                      className={`text-base-content/50 ${compact ? "text-[8px]" : "text-[10px]"}`}
                    >
                      {pos}
                      {isSetter
                        ? " ★"
                        : isLibero
                          ? " L"
                          : isServer
                            ? " 🏐"
                            : ""}
                    </div>
                    <div
                      className={`font-semibold ${compact ? "text-xs" : "text-sm"} ${
                        isSetter ? "text-warning" : isLibero ? "text-info" : ""
                      }`}
                    >
                      {p?.shirtNumber !== "" && p?.shirtNumber != null
                        ? p.shirtNumber
                        : "-"}
                    </div>
                    {isSubPopupOpen &&
                      (() => {
                        const cands = subCandidates(
                          team,
                          subPopup.shirt,
                          subPopupShowAll,
                        );
                        const squad =
                          team === "A" ? matchSquadA : matchSquadB;
                        const canAddNew =
                          (squad?.size ?? 0) < MAX_MATCH_SQUAD;
                        return (
                          <div
                            data-subpopup="1"
                            className="fixed z-50 w-44 max-h-56 overflow-auto overscroll-contain rounded-lg border border-base-300 bg-base-100 shadow-xl p-1 text-left"
                            style={{
                              left: subPopup.x,
                              top: subPopup.y,
                              transform: "translateX(-50%)",
                              WebkitOverflowScrolling: "touch",
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-between px-1 pb-1 mb-1 border-b border-base-300">
                              <span className="text-[11px] font-semibold">
                                {t("Sub for")} #{subPopup.shirt}
                              </span>
                              <button
                                className="btn btn-ghost btn-xs btn-square"
                                title={t("Close")}
                                onClick={() => {
                                  setSubPopup(null);
                                  setSubPopupShowAll(false);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                            {cands.length === 0 ? (
                              <div className="text-[11px] text-base-content/50 px-1 py-2 text-center">
                                {t("No players available")}
                              </div>
                            ) : (
                              cands.map((cp) => (
                                <button
                                  key={cp.shirtNumber}
                                  className="btn btn-ghost btn-xs w-full justify-start font-normal"
                                  onClick={() =>
                                    applyInlineSub(
                                      team,
                                      pos,
                                      subPopup.shirt,
                                      cp.shirtNumber,
                                      cp,
                                    )
                                  }
                                >
                                  <span className="font-bold w-6 text-right mr-1">
                                    {cp.shirtNumber}
                                  </span>
                                  <span className="truncate">
                                    {firstNonEmpty(
                                      cp.NickName,
                                      cp.LastName,
                                      cp.FirstName,
                                    )}
                                  </span>
                                </button>
                              ))
                            )}
                            <button
                              className="btn btn-xs btn-block btn-outline mt-1"
                              onClick={() => setSubPopupShowAll((v) => !v)}
                              disabled={!canAddNew && !subPopupShowAll}
                            >
                              {subPopupShowAll
                                ? t("Available only")
                                : t("Show full roster")}
                            </button>
                          </div>
                        );
                      })()}
                  </div>
                );
              })}
            </div>
            {/* Libero indicator — below pos 5 for vertical bottom panel,
                below pos 1 for horizontal left slot, below pos 5 for right slot */}
            {(orientation === "vertical" ? slotIsA : true) && (
              <div
                className={`flex items-center justify-between mt-1 ${compact ? "gap-1" : "gap-1.5"}`}
              >
                <div className={`flex ${compact ? "gap-1" : "gap-1.5"}`}>
                  {liberoNums.map((n) => {
                  const rcvTeam = servingTeam === "A" ? "B" : "A";
                  const badgeHighlighted =
                    tcMode &&
                    ((tcStage === "receive" && team === rcvTeam) ||
                      tcStage === "attack" ||
                      tcStage === "eval");
                  return (
                    <div
                      key={n}
                      onClick={
                        tcMode ? () => onTCPlayerTap(team, n) : undefined
                      }
                      className={`text-center rounded bg-info/10 ring-1 ring-info/40 ring-dashed leading-tight ${
                        compact
                          ? "px-1 py-0.5 min-w-[1.5rem] text-[10px]"
                          : "px-3 py-1.5 min-w-[2.5rem] text-sm"
                      }${
                        badgeHighlighted
                          ? " ring-accent/80 ring-2 cursor-pointer active:scale-95 transition-transform brightness-110"
                          : tcMode
                            ? " cursor-pointer opacity-50"
                            : ""
                      }`}
                    >
                      <span className="font-semibold text-info">L{n}</span>
                    </div>
                  );
                  })}
                </div>
                {subsChip}
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-base-content/50">no lineup</div>
        )}
      </div>
    );
  };

  const renderCourt = () => {
    // Render a team's lineup box for a given physical slot. slotIsA controls
    // front-row orientation / libero placement so front rows face the net no
    // matter which side a team is shown on.
    const renderTeamBox = (team, slotIsA) =>
      team === "A"
        ? renderLineup(
            "A",
            lineupA,
            teamAName,
            rotationA,
            servingTeam === "A",
            setterA,
            liberoA,
            slotIsA,
          )
        : renderLineup(
            "B",
            lineupB,
            teamBName,
            rotationB,
            servingTeam === "B",
            setterB,
            liberoB,
            slotIsA,
          );

    // The "net" sits between the two boxes (horizontal bar in vertical view,
    // vertical bar in horizontal view). In TC mode it hosts evaluation buttons.
    const TC_EVAL_CHARS = ["#", "+", "!", "-", "/", "="];
    const TC_EVAL_BTN = {
      "#": "bg-success/20 hover:bg-success/40 text-success border border-success/30",
      "+": "bg-success/10 hover:bg-success/30 text-success/80 border border-success/20",
      "!": "bg-base-content/10 hover:bg-base-content/20 text-base-content border border-base-content/20",
      "-": "bg-warning/20 hover:bg-warning/40 text-warning border border-warning/30",
      "/": "bg-warning/10 hover:bg-warning/30 text-warning/80 border border-warning/20",
      "=": "bg-error/20 hover:bg-error/40 text-error border border-error/30",
    };
    const TC_STAGE_HINT = {
      receive: "👆 Tap receiving player",
      attack: "👆 Tap attacker",
      eval: "👆 Tap eval or ↵ submit",
    };
    const netVertical =
      tcMode && !drawMode ? (
        <div className="my-0.5 rounded bg-base-300/60 px-1 py-1.5">
          <div className="flex justify-center gap-1">
            {TC_EVAL_CHARS.map((ev) => (
              <button
                key={ev}
                title={EVAL_LABELS[ev]}
                onClick={() => onTCEvalTap(ev)}
                disabled={!tcLastEval}
                className={`rounded px-3 py-2 text-base font-bold leading-none transition-all ${
                  TC_EVAL_BTN[ev]
                } ${
                  tcLastEval === ev
                    ? "ring-2 ring-offset-1 ring-accent scale-110 shadow"
                    : tcLastEval
                      ? ""
                      : "opacity-40"
                }`}
              >
                {ev}
              </button>
            ))}
          </div>
          {tcStage && (
            <div className="text-center text-[10px] text-base-content/50 mt-0.5 leading-tight">
              {TC_STAGE_HINT[tcStage] ?? ""}
            </div>
          )}
        </div>
      ) : (
        <div className="h-1 bg-base-content/40 my-1 rounded" title="net" />
      );
    const netHorizontal =
      tcMode && !drawMode ? (
        <div className="mx-0.5 rounded bg-base-300/60 px-1.5 py-1 flex flex-col justify-center gap-1">
          {TC_EVAL_CHARS.map((ev) => (
            <button
              key={ev}
              title={EVAL_LABELS[ev]}
              onClick={() => onTCEvalTap(ev)}
              disabled={!tcLastEval}
              className={`rounded px-2 py-1.5 text-sm font-bold leading-none transition-all ${
                TC_EVAL_BTN[ev]
              } ${
                tcLastEval === ev
                  ? "ring-2 ring-offset-1 ring-accent scale-110 shadow"
                  : tcLastEval
                    ? ""
                    : "opacity-40"
              }`}
            >
              {ev}
            </button>
          ))}
          {tcStage && (
            <div className="text-center text-[9px] text-base-content/50 mt-0.5 leading-tight rotate-90 origin-center w-4">
              {tcStage}
            </div>
          )}
        </div>
      ) : (
        <div className="w-1 bg-base-content/40 mx-1 rounded" title="net" />
      );

    if (orientation === "vertical") {
      // Default: B top, A bottom. Swapped: A top, B bottom.
      // The top slot always uses B-style orientation, bottom A-style.
      const topTeam = swapSides ? "A" : "B";
      const bottomTeam = swapSides ? "B" : "A";
      return (
        <div className="flex flex-col gap-0">
          {renderTeamBox(topTeam, false)}
          {netVertical}
          {renderTeamBox(bottomTeam, true)}
        </div>
      );
    }
    // Default: A left, B right. Swapped: B left, A right.
    // The left slot always uses A-style orientation, right B-style.
    const leftTeam = swapSides ? "B" : "A";
    const rightTeam = swapSides ? "A" : "B";
    return (
      <div className="flex flex-row gap-0 items-stretch">
        {renderTeamBox(leftTeam, true)}
        {netHorizontal}
        {renderTeamBox(rightTeam, false)}
      </div>
    );
  };

  // Recompute lineup/serving/rotation at a selected event by replaying match
  // flow up to that event. Prefer explicit LineupSnapshot when available.
  const computeStateAtEvent = (targetEventId) => {
    let found = false;
    let targetEv = null;
    let workingServing = "A";
    let workingRotA = initialRotationARef.current;
    let workingRotB = initialRotationBRef.current;
    let workingLineupA = safeClone(
      initialLineupsRef.current?.A ?? lineupA ?? [],
    );
    let workingLineupB = safeClone(
      initialLineupsRef.current?.B ?? lineupB ?? [],
    );

    // Seed the replay's INITIAL rotation from the FIRST event's scout line
    // (setter court position -> rotation number via `kRotationSetterPositions`).
    // At the match start NO substitution has happened yet, so the setter sits
    // at its true rotational slot and that inversion is exact. From here the
    // side-out count in the loop below tracks the PHYSICAL rotation correctly
    // EVEN ACROSS DOUBLE SUBS — a substitution never rotates the team, and the
    // side-out count is robust to the loss of the `compoundPaired` flag on a
    // DVW round-trip (consecutive events resolving to the SAME winner only
    // flip `workingServing` once, so they can't double-count a rotation). This
    // is why we must NOT read the rotation from the TARGET event's own setter
    // position (below): a double sub parks the setter on the diagonal, 3
    // rotations off. Only meaningful for reloaded events (full scout line);
    // live short "(draw)" codes parse to null and leave the seed untouched.
    const seedEv = sets?.[0]?.events?.[0];
    if (seedEv) {
      const seedScout = parseScoutStateFromDVCode(
        seedEv.dvString ?? seedEv.raw ?? "",
      );
      if (seedScout) {
        if (seedScout.setterPosA != null) {
          const rA = kRotationSetterPositions.indexOf(seedScout.setterPosA) + 1;
          if (rA > 0) workingRotA = rA;
        }
        if (seedScout.setterPosB != null) {
          const rB = kRotationSetterPositions.indexOf(seedScout.setterPosB) + 1;
          if (rB > 0) workingRotB = rB;
        }
      }
    }

    for (const setObj of sets) {
      for (const ev of setObj.events) {
        if (ev.skill === "S" && !ev.compoundPaired) {
          workingServing = ev.team;
        }
        const winner = ev.compoundPaired ? null : rallyWinnerFor(ev);
        if (winner && workingServing !== winner) {
          if (winner === "A") {
            const r = rotateTeamSafely(teamA, workingLineupA, workingRotA);
            workingLineupA = r.lineup;
            workingRotA = r.rotation;
          } else {
            const r = rotateTeamSafely(teamB, workingLineupB, workingRotB);
            workingLineupB = r.lineup;
            workingRotB = r.rotation;
          }
          workingServing = winner;
        }

        // Snapshot lineups (captured while coding/importing) are authoritative.
        if (
          Array.isArray(ev?.LineupSnapshot?.A) &&
          ev.LineupSnapshot.A.length === 6
        ) {
          workingLineupA = buildLineupFromNumbers(
            ev.LineupSnapshot.A,
            rosterARef.current ?? [],
            workingLineupA,
          );
        }
        if (
          Array.isArray(ev?.LineupSnapshot?.B) &&
          ev.LineupSnapshot.B.length === 6
        ) {
          workingLineupB = buildLineupFromNumbers(
            ev.LineupSnapshot.B,
            rosterBRef.current ?? [],
            workingLineupB,
          );
        }
        // Rotation counters captured ALONGSIDE LineupSnapshot are likewise
        // authoritative — restore them from the SAME moment as the lineup so
        // the (lineup positions, rotation counter) pair can never desync. The
        // rotateTeamSafely replay above is only a fallback for older events
        // that predate RotationSnapshot (it starts from a hardcoded serving
        // team and ignores substitutions, so on its own it can drift out of
        // sync with the authoritative LineupSnapshot positions — which is what
        // showed as "discs in the wrong slots after several undos"). Do NOT
        // try to DERIVE the rotation from the setter's position here as a
        // fallback: a double sub parks the setter on the diagonal, so that
        // derivation would come out 3 rotations wrong and re-create the exact
        // front/back "S/MB/OPP in the receiving spots" skew for older events.
        if (ev?.RotationSnapshot?.A != null)
          workingRotA = ev.RotationSnapshot.A;
        if (ev?.RotationSnapshot?.B != null)
          workingRotB = ev.RotationSnapshot.B;

        if (ev.id === targetEventId) {
          targetEv = ev;
          found = true;
          break;
        }
      }
      if (found) break;
    }

    if (!found) return null;
    const scoutState = parseScoutStateFromDVCode(
      targetEv?.dvString ?? targetEv?.raw ?? "",
    );
    if (scoutState) {
      // For selected events, scout-line fields are authoritative:
      // sm[14..25] lineup tails and sm[9]/sm[10] setter positions.
      workingLineupA = scoutState.lineupA;
      workingLineupB = scoutState.lineupB;
      // Intentionally do NOT derive the rotation from `scoutState.setterPosA/B`
      // here. The setter's court position is a valid rotation source ONLY when
      // the setter is at its normal rotational slot; a DOUBLE SUB parks the
      // setter on the diagonal, so that conversion would land 3 rotations off
      // and re-skew the formation. The physical rotation is taken instead from
      // the seeded side-out replay above (`workingRotA/B`), which is
      // substitution-independent and therefore correct across double subs.
    }
    return {
      lineupA: workingLineupA,
      lineupB: workingLineupB,
      serving: workingServing,
      rotationA: workingRotA,
      rotationB: workingRotB,
      // Only override the CURRENT setter when the historical scout-line data
      // actually resolves one for this point in time (true DVW imports with a
      // full 26+-field scout line). For typed/coded matches — where the raw
      // code is just a short compact token like "*05SQ+15" and
      // `parseScoutStateFromDVCode` returns null — omit these keys entirely
      // so callers' `hasOwnProperty` guards leave the team's ONE
      // currently-nominated setter untouched instead of resetting it to null
      // (which used to spuriously reopen the "nominate a setter" modal every
      // time an event row was clicked).
      ...(scoutState?.setterA != null ? { setterA: scoutState.setterA } : {}),
      ...(scoutState?.setterB != null ? { setterB: scoutState.setterB } : {}),
    };
  };

  const goToSet = (newIdx) => {
    setCurrentSetIdx(newIdx);
    setReviewingEvent(false);
    const firstEv = sets[newIdx]?.events?.[0];
    if (firstEv) {
      selectEventAndRestoreSnapshot(firstEv.id);
    } else {
      setSelectedEventId(null);
    }
  };

  // Selects an event and restores the live coding state (serving team,
  // lineups, rotations, setters) to whatever it was at that point in time.
  // Shared by row clicks, set navigation, and the "return to live" button.
  const selectEventAndRestoreSnapshot = (eventId) => {
    const snap = computeStateAtEvent(eventId);
    setSelectedEventId(eventId);
    if (snap) {
      setServingTeam(snap.serving);
      if (snap.lineupA) setLineupA(snap.lineupA);
      if (snap.lineupB) setLineupB(snap.lineupB);
      if (snap.rotationA != null) setRotationA(snap.rotationA);
      if (snap.rotationB != null) setRotationB(snap.rotationB);
      if (Object.prototype.hasOwnProperty.call(snap, "setterA"))
        setSetterA(snap.setterA);
      if (Object.prototype.hasOwnProperty.call(snap, "setterB"))
        setSetterB(snap.setterB);
    }
    return snap;
  };

  // "Return to live coding" — jumps back to the most recent event in the
  // current set, restoring its (i.e. the live) state.
  const goLive = () => {
    const events = currentSet?.events ?? [];
    const lastEv = events[events.length - 1];
    setReviewingEvent(false);
    if (lastEv) selectEventAndRestoreSnapshot(lastEv.id);
  };

  // True while the left column should show the LIVE drawing/coding surface
  // rather than the read-only-selected court diagram. Being AT the last
  // event is necessary but not sufficient — the user can also explicitly
  // click the last event's row (`reviewingEvent`) to inspect/edit its ball
  // path just like any other row, in which case it's no longer "live" even
  // though it's still the most recent event.
  const isViewingLiveEvent = (() => {
    const events = currentSet?.events ?? [];
    const lastEv = events[events.length - 1];
    if (!lastEv || !selectedEventId) return true;
    return selectedEventId === lastEv.id && !reviewingEvent;
  })();

  // Shared Events List header row (column labels), used by both the
  // video-mode and normal-mode layouts so they always stay in sync. Labels
  // are abbreviated when the panel is narrow to avoid truncation/wrapping.
  const renderEventsHeader = () => {
    const narrow = eventsPanelWidth < 320;
    const gridColsClass = drawMode
      ? narrow
        ? "grid-cols-10"
        : "grid-cols-11"
      : narrow
        ? "grid-cols-11"
        : "grid-cols-12";
    const playerCols = narrow ? "col-span-2" : "col-span-3";
    return (
      <div
        className={`px-2 py-1 text-xs font-bold bg-base-200 grid ${gridColsClass} gap-1`}
      >
        <div className={playerCols}>{narrow ? t("Pl") : t("Player")}</div>
        <div className="col-span-1 -ml-1.5">{narrow ? t("Sk") : t("Skill")}</div>
        <div className="col-span-1 -ml-1.5">{narrow ? t("Ty") : t("Type")}</div>
        <div className="col-span-1 -ml-1.5">{narrow ? t("Rs") : t("Result")}</div>
        <div className="col-span-2 -ml-1.5">{t("Zn")}</div>
        <div className="col-span-1 -ml-1.5">{narrow ? t("Ad") : t("Advanced")}</div>
        <div className="col-span-1 -ml-1.5">{narrow ? t("Ex") : t("Extended")}</div>
        {!drawMode && (
          <div className="col-span-1 -ml-1.5">{narrow ? t("Code") : t("DV Code")}</div>
        )}
        <div className="col-span-1 -ml-1.5 text-center">
          {narrow ? t("Sco") : t("Scores")}
        </div>
      </div>
    );
  };

  const renderEventRow = (ev, idx, narrow = false) => {
    const selected = selectedEventId === ev.id;
    const rawToken =
      String(ev?.dvString ?? ev?.raw ?? "")
        .split(";")[0]
        ?.trim() ?? "";
    const rawSkill =
      rawToken.length >= 4 ? (rawToken[3] ?? "").toUpperCase() : "";
    const skillFromEvent =
      typeof ev?.skill === "string" ? ev.skill.toUpperCase() : "";
    const skillForRow = SKILL_LABELS[rawSkill] ? rawSkill : skillFromEvent;
    const p = ev.Player ?? ev.player ?? {};
    const playerNumber =
      p.shirtNumber ?? p.ShirtNumber ?? ev.shirtNumber ?? "--";
    const playerName = firstNonEmpty(
      p.NickName,
      p.nickName,
      p.FirstName,
      p.firstName,
      p.playerFirstName,
      p.LastName,
      p.lastName,
      p.playerLastName,
      ev.PlayerName,
    );
    const gridColsClass = drawMode
      ? narrow
        ? "grid-cols-10"
        : "grid-cols-11"
      : narrow
        ? "grid-cols-11"
        : "grid-cols-12";
    const playerCols = narrow ? "col-span-2" : "col-span-3";
    return (
      <li
        key={ev.id}
        ref={selected ? selectedRowRef : null}
        onClick={() => {
          setReviewingEvent(true);
          selectEventAndRestoreSnapshot(ev.id);
          // Seek the video player to this event's recorded position.
          if (showVideo && playerRef.current && playerReadyRef.current) {
            const vp = computeEventVideoPosition(ev);
            if (typeof vp === "number" && vp >= 0) {
              playerRef.current.seekTo(vp, "seconds");
              // YouTube renders a black frame when seeked while paused; play so
              // the seeked frame is shown (local <video> is unaffected).
              setVideoPlaying(true);
            }
          }
        }}
        className={`grid ${gridColsClass} gap-1 items-center py-0.5 px-2 text-xs cursor-pointer ${
          selected ? "bg-primary/20" : idx % 2 === 0 ? "bg-base-200/40" : ""
        }`}
      >
        <span className={`${playerCols} flex items-center gap-1 truncate`}>
          <span
            className="inline-flex items-center justify-center rounded font-mono font-bold shrink-0 text-center"
            style={{
              width: "1.75rem",
              backgroundColor: ev.team === "A" ? colourA : colourB,
              color: contrastFor(ev.team === "A" ? colourA : colourB),
            }}
            title={`${playerNumber} ${playerName}`.trim()}
          >
            {playerNumber}
          </span>
          {!narrow && playerName ? (
            <span className="truncate text-base-content/80">
              {playerName.toUpperCase()}
            </span>
          ) : null}
        </span>
        <span className="col-span-1 font-bold text-primary/80 truncate">
          {narrow
            ? (skillForRow ?? "")
            : (SKILL_LABELS[skillForRow] ?? ev.skillLabel ?? skillForRow ?? "")}
        </span>
        <span className="col-span-1 text-base-content/60">
          {ev.type ?? ev.SubEvent ?? ""}
        </span>
        <span className={`col-span-1 font-bold ${EVAL_COLORS[ev.evaluation]}`}>
          {ev.evaluation}
        </span>
        <span className="col-span-2 text-base-content/60">
          {ev.zoneFrom ?? "-"}
          {ev.subzoneFrom ?? ""}
          {ev.zoneTo ? `→${ev.zoneTo}${ev.subzoneTo ?? ""}` : ""}
        </span>
        <span
          className="col-span-1 text-base-content/50 truncate font-mono"
          title={t("Attack Combo / Setter Call")}
        >
          {ev.attackCombo || ev.setterCall || ev.settersCall || ""}
        </span>
        <span className="col-span-1 text-base-content/50 truncate font-mono">
          {[ev.ExtraCode1, ev.ExtraCode2, ev.ExtraCode3]
            .filter((c) => c && c !== "~")
            .join("")}
        </span>
        {!drawMode && (
          <span className="col-span-1 font-mono text-base-content/60 truncate">
            {ev.raw}
          </span>
        )}
        <span
          className="col-span-1 font-mono tabular-nums text-base-content/50 text-center"
          title="Score A : Score B"
        >
          {ev.scoreA != null ? `${ev.scoreA}:${ev.scoreB ?? 0}` : ""}
        </span>
      </li>
    );
  };

  // ---------- Court diagram with optional ball-path overlay ----------
  // Standard DV zone layout viewed from a team's own perspective:
  //   front row : 4 3 2
  //   middle    : 7 8 9
  //   back row  : 5 6 1
  // We map a zone to a (col, row) pair within a team half (3x3).
  const ZONE_GRID = {
    4: [0, 0],
    3: [1, 0],
    2: [2, 0],
    7: [0, 1],
    8: [1, 1],
    9: [2, 1],
    5: [0, 2],
    6: [1, 2],
    1: [2, 2],
  };
  // Inverse map: "col,row" → zone digit
  const ZONE_GRID_INVERSE = Object.fromEntries(
    Object.entries(ZONE_GRID).map(([z, [c, r]]) => [`${c},${r}`, z]),
  );

  /**
   * Convert a zone digit on the given team's half into (x, y) in court SVG.
   * Court is drawn vertically: team B on top, team A on bottom, net = horizontal centerline.
   */
  const zonePoint = (team, zone, court) => {
    if (!zone || !ZONE_GRID[zone]) return null;
    const [col, row] = ZONE_GRID[zone];
    const halfH = court.height / 2;
    const cellW = court.width / 3;
    const cellH = halfH / 3;
    const x = col * cellW + cellW / 2;
    if (team === "A") {
      return { x, y: halfH + row * cellH + cellH / 2 };
    }
    // Team B: mirror columns and invert rows so front row faces the net
    const mirroredCol = 2 - col;
    return {
      x: mirroredCol * cellW + cellW / 2,
      y: halfH - (row * cellH + cellH / 2),
    };
  };

  /**
   * Like zonePoint but offset by the DV sub-zone quadrant (A/B/C/D).
   *   A = front-left, B = front-right, C = back-right, D = back-left
   * (own-team perspective; axes flip for team B in screen space)
   */
  const subzonePoint = (team, zone, subzone, court) => {
    const base = zonePoint(team, zone, court);
    if (!base || !subzone) return base;
    const halfH = court.height / 2;
    const cellW = court.width / 3;
    const cellH = halfH / 3;
    const dx = cellW / 4;
    const dy = cellH / 4;
    const teamFx = subzone === "a" || subzone === "d" ? -1 : 1;
    const teamFy = subzone === "a" || subzone === "b" ? -1 : 1;
    if (team === "A") {
      return { x: base.x + teamFx * dx, y: base.y + teamFy * dy };
    }
    // Away half axes are flipped in screen space
    return { x: base.x - teamFx * dx, y: base.y - teamFy * dy };
  };

  /**
   * Compute ball path points for a given event.
   * Returns { from, mid, to } or { block, from, to } or null.
   *
   * ALWAYS prefers the event's stored `ballStartPct`/`ballMidPct`/`ballEndPct`
   * (percent coordinates, 0-100, in the SAME canonical internal 240×360
   * vertical frame that `screenToInternal` produces — orientation/flip-
   * independent, exactly like zoneFrom/zoneTo) when present: these capture
   * exactly where the user dragged, so redraws are pixel-accurate instead of
   * snapping to the centre of whichever DV zone+subzone quadrant the drag
   * landed in. Zone-based interpolation (`subzonePoint`) is only used as a
   * fallback for events that predate this (typed DV codes, DVW imports) or
   * only have one side's precise coordinate set.
   */
  const pctToCourtPoint = (pct, court) =>
    pct
      ? { x: (pct.x / 100) * court.width, y: (pct.y / 100) * court.height }
      : null;

  const eventPath = (ev, court) => {
    if (!ev) return null;
    const own = ev.team;
    const opp = own === "A" ? "B" : "A";
    const z1 = ev.zoneFrom;
    const z2 = ev.zoneTo;
    const s1 = ev.subzoneFrom;
    const s2 = ev.subzoneTo;
    if (!z1 && !z2 && !ev.ballStartPct && !ev.ballEndPct) return null;

    // Convention: zoneFrom is always in the team's own half,
    //             zoneTo  is always in the opposition's half.
    const fromTeam = own;
    const toTeam = opp;
    // Mid waypoint (e.g. a paused touch recorded mid-drag) — coordinate-only,
    // there's no DV-zone equivalent for it, so it's simply omitted whenever
    // the event doesn't carry one.
    const mid = ev.ballMidPct ? pctToCourtPoint(ev.ballMidPct, court) : null;
    if (ev.skill === "B") {
      const p = ev.ballStartPct
        ? pctToCourtPoint(ev.ballStartPct, court)
        : z1
          ? subzonePoint(own, z1, s1, court)
          : null;
      if (!p) return null;
      // A block can still carry a destination (zoneTo/ballEndPct) when the
      // user has dragged on the diagram to edit it — always draw that path
      // too (in addition to the BLK marker) instead of only ever showing a
      // single point, so ball path display isn't special-cased away for
      // this event type.
      const to = ev.ballEndPct
        ? pctToCourtPoint(ev.ballEndPct, court)
        : z2
          ? subzonePoint(toTeam, z2, s2, court)
          : null;
      return { block: { x: p.x, y: court.height / 2 }, from: p, mid, to };
    }

    const from = ev.ballStartPct
      ? pctToCourtPoint(ev.ballStartPct, court)
      : z1
        ? subzonePoint(fromTeam, z1, s1, court)
        : null;
    const to = ev.ballEndPct
      ? pctToCourtPoint(ev.ballEndPct, court)
      : z2
        ? subzonePoint(toTeam, z2, s2, court)
        : null;
    if (!from && !to) return null;
    return { from, mid, to };
  };

  const renderCourtDiagram = () => {
    const W = 240;
    const H = 360;
    const isHoriz = orientation === "horizontal";
    // When sides are swapped, mirror the diagram vertically (in canonical
    // space) so Team A occupies the top half and Team B the bottom half.
    // Geometry is flipped via an SVG transform; text labels and drag input are
    // adjusted separately so they stay upright / consistent.
    const flip = swapSides;
    const court = { width: W, height: H };
    const selected =
      currentSet?.events.find((e) => e.id === selectedEventId) ?? null;
    const path = selected ? eventPath(selected, court) : null;

    const cellW = W / 3;
    const cellH = H / 6;
    const lines = [];
    for (let i = 1; i < 3; i++) {
      lines.push(
        <line
          key={`v${i}`}
          x1={i * cellW}
          y1={0}
          x2={i * cellW}
          y2={H}
          stroke="currentColor"
          strokeOpacity={0.15}
        />,
      );
    }
    for (let i = 1; i < 6; i++) {
      if (i === 3) continue;
      lines.push(
        <line
          key={`h${i}`}
          x1={0}
          y1={i * cellH}
          x2={W}
          y2={i * cellH}
          stroke="currentColor"
          strokeOpacity={0.1}
        />,
      );
    }

    const zoneLabels = [];
    const subzoneLabels = [];
    for (const [zone, [col, row]] of Object.entries(ZONE_GRID)) {
      zoneLabels.push(
        <text
          key={`A${zone}`}
          x={col * cellW + 4}
          y={flip ? H / 2 - row * cellH - 3 : H / 2 + row * cellH + 11}
          fontSize={9}
          fill="currentColor"
          opacity={0.35}
        >
          {zone}
        </text>,
      );
      const mCol = 2 - col;
      zoneLabels.push(
        <text
          key={`B${zone}`}
          x={mCol * cellW + 4}
          y={flip ? H / 2 + row * cellH + 11 : H / 2 - row * cellH - 3}
          fontSize={9}
          fill="currentColor"
          opacity={0.35}
        >
          {zone}
        </text>,
      );
      // Subzone quadrant labels (debug)
      const qW = cellW / 4;
      const qH = cellH / 4;
      // Quadrant [dx,dy] from cell origin for [a,b,c,d]
      const aofsA = !flip
        ? [
            [qW, qH],
            [3 * qW, qH],
            [3 * qW, 3 * qH],
            [qW, 3 * qH],
          ] // front=top, left=left
        : [
            [qW, 3 * qH],
            [3 * qW, 3 * qH],
            [3 * qW, qH],
            [qW, qH],
          ]; // flipped: front=bottom
      const origAx = col * cellW;
      const origAy = flip ? H / 2 - (row + 1) * cellH : H / 2 + row * cellH;
      ["a", "b", "c", "d"].forEach((sz, i) =>
        subzoneLabels.push(
          <text
            key={`A${zone}${sz}`}
            x={origAx + aofsA[i][0]}
            y={origAy + aofsA[i][1]}
            fontSize={7}
            fill="currentColor"
            opacity={0.2}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {sz}
          </text>,
        ),
      );
      const aofsB = !flip
        ? [
            [3 * qW, 3 * qH],
            [qW, 3 * qH],
            [qW, qH],
            [3 * qW, qH],
          ] // front=bottom, left=right-screen
        : [
            [3 * qW, qH],
            [qW, qH],
            [qW, 3 * qH],
            [3 * qW, 3 * qH],
          ]; // flipped: front=top, left=right-screen
      const origBx = mCol * cellW;
      const origBy = flip ? H / 2 + row * cellH : H / 2 - (row + 1) * cellH;
      ["a", "b", "c", "d"].forEach((sz, i) =>
        subzoneLabels.push(
          <text
            key={`B${zone}${sz}`}
            x={origBx + aofsB[i][0]}
            y={origBy + aofsB[i][1]}
            fontSize={7}
            fill="currentColor"
            opacity={0.2}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {sz}
          </text>,
        ),
      );
    }

    // ── Drag-to-draw helpers ─────────────────────────────────────────────────
    // Convert a browser clientX/Y to internal SVG coordinates (always 240×360,
    // vertical layout) regardless of the current display orientation.
    const screenToInternal = (clientX, clientY) => {
      const svg = courtSvgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      const nx = (clientX - rect.left) / rect.width;
      const ny = (clientY - rect.top) / rect.height;
      if (isHoriz) {
        // viewBox is 0 0 H W (360×240).  Un-rotate: ix = W − dvy, iy = dvx
        const dvx = nx * H;
        const dvy = ny * W;
        const iy = Math.max(0, Math.min(H, dvx));
        return { x: Math.max(0, Math.min(W, W - dvy)), y: flip ? H - iy : iy };
      }
      const iy = Math.max(0, Math.min(H, ny * H));
      return { x: Math.max(0, Math.min(W, nx * W)), y: flip ? H - iy : iy };
    };

    const internalToZone = (ix, iy) => {
      const halfH = H / 2;
      const cellW = W / 3;
      const cellH = halfH / 3;
      if (iy < halfH) {
        // Team B half: columns mirrored, rows inverted (front row nearest net)
        const mirroredCol = Math.min(2, Math.floor(ix / cellW));
        const col = 2 - mirroredCol;
        const row = Math.min(2, Math.floor((halfH - iy) / cellH));
        const zone = ZONE_GRID_INVERSE[`${col},${row}`] ?? null;
        // Subzone: front=closer to net=larger (halfH-iy) offset within cell;
        //          left=team-B-perspective-left=larger screen-x within mirrored cell
        const lx = ix - mirroredCol * cellW;
        const ly = halfH - iy - row * cellH;
        const near = ly >= cellH / 2;
        const left = lx >= cellW / 2;
        const subzone =
          near && left ? "a" : near && !left ? "b" : !near && !left ? "c" : "d";
        return { team: "B", zone, subzone };
      }
      // Team A half
      const col = Math.min(2, Math.floor(ix / cellW));
      const row = Math.min(2, Math.floor((iy - halfH) / cellH));
      const zone = ZONE_GRID_INVERSE[`${col},${row}`] ?? null;
      // Subzone: front=closer to net=smaller (iy-halfH) offset within cell;
      //          left=smaller screen-x within cell
      const lx = ix - col * cellW;
      const ly = iy - halfH - row * cellH;
      const near = ly < cellH / 2;
      const left = lx < cellW / 2;
      const subzone =
        near && left ? "a" : near && !left ? "b" : !near && !left ? "c" : "d";
      return { team: "A", zone, subzone };
    };

    // Helper: parse the last token of the input bar and return the event if it
    // is an attack (skill === "A"), otherwise null.
    const inputBarAttack = (() => {
      const trimmed = codeInput.trim();
      if (!trimmed) return null;
      const lastTok = trimmed.split(".").pop() ?? "";
      const r = parseDVCode(lastTok);
      return r.ok && r.event.skill === "A" ? r.event : null;
    })();

    const handleDragStart = (clientX, clientY) => {
      if (!selectedEventId && !inputBarAttack) return;
      const pt = screenToInternal(clientX, clientY);
      if (!pt) return;
      dragStartRef.current = pt;
      setDragLine({ x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y });
    };
    const handleDragMove = (clientX, clientY) => {
      if (!dragStartRef.current) return;
      const pt = screenToInternal(clientX, clientY);
      if (!pt) return;
      const s = dragStartRef.current;
      setDragLine({ x1: s.x, y1: s.y, x2: pt.x, y2: pt.y });
    };
    const handleDragEnd = (clientX, clientY) => {
      if (!dragStartRef.current) return;
      const startPt = dragStartRef.current;
      const endPt = screenToInternal(clientX, clientY) ?? startPt;
      dragStartRef.current = null;
      setDragLine(null);
      const sz = internalToZone(startPt.x, startPt.y);
      const ez = internalToZone(endPt.x, endPt.y);

      // Priority 1: assign zones to the attack in the input bar
      if (inputBarAttack) {
        const own = inputBarAttack.team;
        if (sz.team !== own && ez.team === own) return; // reject wrong direction
        let fromZone, toZone, toSubzone;
        [sz, ez].forEach(({ team, zone, subzone }) => {
          if (team === own) fromZone = zone;
          else {
            toZone = zone;
            toSubzone = subzone;
          }
        });
        const teamMark = own === "A" ? "*" : "a";
        const iev = inputBarAttack;
        const newTok =
          `${teamMark}${iev.shirtNumber}${iev.skill}${iev.type ?? ""}${iev.evaluation}` +
          `${fromZone ?? iev.zoneFrom ?? ""}${toZone ?? iev.zoneTo ?? ""}${(toZone ?? iev.zoneTo) ? (toSubzone ?? iev.subzoneTo ?? "") : ""}`;
        const inputTokens = codeInput.trim().split(".");
        inputTokens[inputTokens.length - 1] = newTok;
        setCodeInput(inputTokens.join("."));
        return;
      }

      // Priority 2: patch the currently selected (already-submitted) event
      const ev = currentSet?.events.find((e2) => e2.id === selectedEventId);
      if (!ev) return;
      const own = ev.team;
      // Reject drags drawn against the event's natural direction (opp → own)
      if (sz.team !== own && ez.team === own) return;
      let fromZone, toZone, toSubzone;
      [sz, ez].forEach(({ team, zone, subzone }) => {
        if (team === own)
          fromZone = zone; // source: zone only
        else {
          toZone = zone;
          toSubzone = subzone;
        } // destination: zone + subzone
      });
      // Also capture the exact drag points as percent coordinates (0-100,
      // in the canonical internal 240×360 frame — see `eventPath`'s comment)
      // so redraws use the precise dragged position instead of snapping to
      // the centre of a DV zone+subzone quadrant.
      const asPct = (pt) => ({ x: (pt.x / W) * 100, y: (pt.y / H) * 100 });
      let ballStartPct, ballEndPct;
      [
        { pt: startPt, team: sz.team },
        { pt: endPt, team: ez.team },
      ].forEach(({ pt, team }) => {
        if (team === own) ballStartPct = asPct(pt);
        else ballEndPct = asPct(pt);
      });
      const ballMidPct =
        ballStartPct && ballEndPct
          ? {
              x: (ballStartPct.x + ballEndPct.x) / 2,
              y: (ballStartPct.y + ballEndPct.y) / 2,
            }
          : undefined;
      patchSelectedEvent({
        ...(fromZone !== undefined && {
          zoneFrom: fromZone,
          subzoneFrom: null,
        }),
        ...(toZone !== undefined && {
          zoneTo: toZone,
          subzoneTo: toSubzone ?? null,
        }),
        ...(ballStartPct && { ballStartPct }),
        ...(ballEndPct && { ballEndPct }),
        ...(ballMidPct && { ballMidPct }),
      });
    };

    return (
      <div className="flex flex-col items-center gap-1 bg-base-200 rounded p-2 h-full min-h-0">
        <div className="text-xs text-base-content/60 self-stretch flex justify-between items-center shrink-0">
          <span>{t("Court")}</span>
          <div className="flex items-center gap-2">
            {/* In Draw & Code mode the raw DV code is meaningless to the
                user (they never type codes), so it's hidden here — the
                LIVE button (below) is always shown regardless of mode. */}
            {selected && !drawMode && (
              <span className="font-mono">{selected.raw}</span>
            )}
            <button
              className="btn btn-xs btn-accent"
              onClick={goLive}
              title={t("Return to live coding")}
            >
              {t("LIVE")}
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 w-full flex items-center justify-center">
          <svg
            ref={courtSvgRef}
            viewBox={`0 0 ${isHoriz ? H : W} ${isHoriz ? W : H}`}
            style={{
              height: "100%",
              width: "auto",
              maxWidth: "100%",
              maxHeight: "100%",
              aspectRatio: `${isHoriz ? H : W} / ${isHoriz ? W : H}`,
              cursor: selectedEventId ? "crosshair" : "default",
              touchAction: "none",
            }}
            className="text-base-content"
            onMouseDown={(e) => {
              e.preventDefault();
              handleDragStart(e.clientX, e.clientY);
            }}
            onMouseMove={(e) => handleDragMove(e.clientX, e.clientY)}
            onMouseUp={(e) => handleDragEnd(e.clientX, e.clientY)}
            onMouseLeave={(e) => handleDragEnd(e.clientX, e.clientY)}
            onTouchStart={(e) => {
              e.preventDefault();
              const t = e.touches[0];
              handleDragStart(t.clientX, t.clientY);
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              const t = e.touches[0];
              handleDragMove(t.clientX, t.clientY);
            }}
            onTouchEnd={(e) => {
              const t = e.changedTouches[0];
              handleDragEnd(t.clientX, t.clientY);
            }}
          >
            <g
              transform={isHoriz ? `translate(0,${W}) rotate(-90)` : undefined}
            >
              <defs>
                <marker
                  id="dv-arrowhead"
                  markerWidth="8"
                  markerHeight="8"
                  refX="6"
                  refY="4"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0,0 L8,4 L0,8 Z" fill="currentColor" />
                </marker>
              </defs>
              <g transform={flip ? `translate(0,${H}) scale(1,-1)` : undefined}>
                <rect
                  x={0}
                  y={0}
                  width={W}
                  height={H / 2}
                  fill="currentColor"
                  fillOpacity={0.04}
                />
                <rect
                  x={0}
                  y={H / 2}
                  width={W}
                  height={H / 2}
                  fill="currentColor"
                  fillOpacity={0.07}
                />
                <rect
                  x={0.5}
                  y={0.5}
                  width={W - 1}
                  height={H - 1}
                  fill="none"
                  stroke="currentColor"
                  strokeOpacity={0.5}
                />
                <line
                  x1={0}
                  y1={H / 2}
                  x2={W}
                  y2={H / 2}
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeOpacity={0.7}
                />
                {lines}
                {/* faint direction arrow — own-half centre → opp-half centre, always shown when event is selected */}
                {selected &&
                  (() => {
                    const isA = selected.team === "A";
                    const sw = W / 3; // shaft stroke-width (1/3 of court width)
                    const cx = W / 2; // horizontal centre
                    const fromY = isA ? (H * 3) / 4 : H / 4; // centre of own half
                    const toY = isA ? H / 4 : (H * 3) / 4; // centre of opp half
                    const ahH = sw; // arrowhead height
                    const ahHW = sw; // arrowhead half-base width (total = 2/3 court)
                    const tipBaseY = isA ? toY + ahH : toY - ahH;
                    return (
                      <g
                        opacity={0.2}
                        pointerEvents="none"
                        style={{ color: isA ? colourA : colourB }}
                      >
                        <line
                          x1={cx}
                          y1={fromY}
                          x2={cx}
                          y2={tipBaseY}
                          stroke="currentColor"
                          strokeWidth={sw}
                          strokeLinecap="round"
                        />
                        <polygon
                          points={`${cx - ahHW},${tipBaseY} ${cx + ahHW},${tipBaseY} ${cx},${toY}`}
                          fill="currentColor"
                        />
                      </g>
                    );
                  })()}
                {path?.from && path?.to && (
                  <g
                    style={{
                      color: selected?.team === "A" ? colourA : colourB,
                    }}
                  >
                    {/* When a mid waypoint was recorded, draw the path as two
                  segments through it (using its exact coordinates) instead
                  of a single straight from→to line, so an edit that passed
                  through an intermediate touch point is reflected faithfully. */}
                    {path.mid ? (
                      <>
                        <line
                          x1={path.from.x}
                          y1={path.from.y}
                          x2={path.mid.x}
                          y2={path.mid.y}
                          stroke="currentColor"
                          strokeWidth={2.2}
                        />
                        <line
                          x1={path.mid.x}
                          y1={path.mid.y}
                          x2={path.to.x}
                          y2={path.to.y}
                          stroke="currentColor"
                          strokeWidth={2.2}
                          markerEnd="url(#dv-arrowhead)"
                        />
                        <circle
                          cx={path.mid.x}
                          cy={path.mid.y}
                          r={3}
                          fill="currentColor"
                        />
                      </>
                    ) : (
                      <line
                        x1={path.from.x}
                        y1={path.from.y}
                        x2={path.to.x}
                        y2={path.to.y}
                        stroke="currentColor"
                        strokeWidth={2.2}
                        markerEnd="url(#dv-arrowhead)"
                      />
                    )}
                    <circle
                      cx={path.from.x}
                      cy={path.from.y}
                      r={4}
                      fill="currentColor"
                    />
                  </g>
                )}
                {path?.from && !path?.to && (
                  <circle
                    cx={path.from.x}
                    cy={path.from.y}
                    r={5}
                    fill="currentColor"
                    style={{
                      color: selected?.team === "A" ? colourA : colourB,
                    }}
                  />
                )}
                {path?.to && !path?.from && (
                  <circle
                    cx={path.to.x}
                    cy={path.to.y}
                    r={5}
                    fill="currentColor"
                    style={{
                      color: selected?.team === "A" ? colourA : colourB,
                    }}
                  />
                )}
                {dragLine && (
                  <line
                    x1={dragLine.x1}
                    y1={dragLine.y1}
                    x2={dragLine.x2}
                    y2={dragLine.y2}
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeDasharray="6 3"
                    className="text-accent"
                    opacity={0.85}
                    pointerEvents="none"
                  />
                )}
              </g>
              {zoneLabels}
              {subzoneLabels}
              <text
                x={4}
                y={flip ? H - 4 : 12}
                fontSize={10}
                fill="currentColor"
                opacity={0.6}
              >
                a {teamBName}
              </text>
              <text
                x={4}
                y={flip ? 12 : H - 4}
                fontSize={10}
                fill="currentColor"
                opacity={0.6}
              >
                * {teamAName}
              </text>
              {path?.block && (
                <g className="text-warning">
                  <circle
                    cx={path.block.x}
                    cy={path.block.y}
                    r={6}
                    fill="currentColor"
                    fillOpacity={0.85}
                    stroke="currentColor"
                  />
                  <text
                    x={path.block.x + 8}
                    y={path.block.y - 6}
                    fontSize={9}
                    fill="currentColor"
                  >
                    BLK
                  </text>
                </g>
              )}
            </g>
          </svg>
        </div>
        <div className="text-[10px] text-base-content/50 text-center shrink-0">
          {selected
            ? t("Drag on court to set ball path")
            : t("Select an event then drag to draw ball path")}
        </div>
      </div>
    );
  };

  // ---------- left column: lineups + court ----------
  const leftColumn = (
    <div className="flex flex-col gap-2 h-full overflow-hidden pr-2">
      <div className="flex flex-wrap items-center gap-3 shrink-0">
        <div className="join">
          <button
            className={`btn btn-xs join-item ${
              orientation === "vertical" ? "btn-primary" : ""
            }`}
            onClick={() => setOrientation("vertical")}
            title={t("Court vertical (net horizontal)")}
          >
            VERT
          </button>
          <button
            className={`btn btn-xs join-item ${
              orientation === "horizontal" ? "btn-primary" : ""
            }`}
            onClick={() => setOrientation("horizontal")}
            title={t("Court horizontal (net vertical)")}
          >
            HORZ
          </button>
        </div>
        <button
          className="btn btn-xs btn-square btn-ghost"
          onClick={() => setSwapSides((v) => !v)}
          title={t("Swap sides")}
        >
          {orientation === "vertical" ? <ArrowsUpDownIcon className="size-6" /> : <ArrowsRightLeftIcon className="size-6" />}
        </button>
        <button
          className={`btn btn-xs ${drawMode ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setDrawMode((v) => !v)}
          title={t(
            "Draw & Code — draw ball paths on the court to code the rally",
          )}
        >
          ✏️ {t("Draw")}
        </button>
        {(currentSet?.events.length ?? 0) === 0 && (
          <button
            className="btn btn-xs btn-outline ml-auto"
            onClick={() => {
              setIsSubs(false);
              matchSetupDialogRef.current?.showModal();
            }}
            title={t(
              "Edit Lineup — freely rearrange the starting lineup before the set begins",
            )}
          >
            {t("Edit Lineup")}
          </button>
        )}
        <button
          className={`btn btn-xs btn-outline ${(currentSet?.events.length ?? 0) === 0 ? "" : "ml-auto"}`}
          disabled={(currentSet?.events.length ?? 0) === 0}
          onClick={() => {
            setIsSubs(true);
            matchSetupDialogRef.current?.showModal();
          }}
          title={
            (currentSet?.events.length ?? 0) === 0
              ? t("Record at least one event before making substitutions")
              : t("Substitutions")
          }
        >
          {t("Subs")}
        </button>
      </div>
      <div className="shrink-0">{renderCourt()}</div>
      <div className="flex-1 min-h-0">
        {drawMode && isViewingLiveEvent ? (
          <DrawAndCodeCourt
            orientation={orientation}
            primaryTeamKey={
              orientation === "vertical"
                ? swapSides
                  ? "A"
                  : "B"
                : swapSides
                  ? "B"
                  : "A"
            }
            secondaryTeamKey={
              orientation === "vertical"
                ? swapSides
                  ? "B"
                  : "A"
                : swapSides
                  ? "A"
                  : "B"
            }
            teamA={{
              name: teamAName,
              colour: colourA,
              lineup: lineupA,
              startingLineup: startingLineupsRef.current.A,
              setter: setterA,
              libero: liberoA,
              isOppPassing: isOppPassingA,
              rotation: rotationA,
            }}
            teamB={{
              name: teamBName,
              colour: colourB,
              lineup: lineupB,
              startingLineup: startingLineupsRef.current.B,
              setter: setterB,
              libero: liberoB,
              isOppPassing: isOppPassingB,
              rotation: rotationB,
            }}
            servingTeam={servingTeam}
            isStartOfRally={isStartOfRally(currentSet)}
            events={currentSet?.events ?? []}
            onEvents={handleDrawCodeEvents}
            onGoLive={goLive}
          />
        ) : (
          renderCourtDiagram()
        )}
      </div>
    </div>
  );

  // ---------- right column: input + events ----------
  const rightColumn = (
    <div className="flex flex-col gap-2 h-full min-w-0 pl-2">
      {/* Top toolbar: compact scoreboard sharing a row with the action buttons
          (video, defaults, undo, redo, save, discard, back). */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-base-300 rounded px-2 py-1 shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className="font-bold truncate text-sm max-w-[96px]"
            title={teamAName}
          >
            {teamAName}
          </span>
          <span className="text-xl font-bold tabular-nums">
            {isViewingLiveEvent
              ? (currentSet?.pointsA ?? 0)
              : (selectedEvent?.scoreA ?? currentSet?.pointsA ?? 0)}
          </span>
          <span className="text-base-content/40">-</span>
          <span className="text-xl font-bold tabular-nums">
            {isViewingLiveEvent
              ? (currentSet?.pointsB ?? 0)
              : (selectedEvent?.scoreB ?? currentSet?.pointsB ?? 0)}
          </span>
          <span
            className="font-bold truncate text-sm max-w-[96px]"
            title={teamBName}
          >
            {teamBName}
          </span>
          <span className="text-[14px] text-base-content/60 whitespace-nowrap ml-1">
            {/* {setsWonA}-{setsWonB} · {t("Game")} {currentSet?.setNumber} */}
            {setsWonA}-{setsWonB}
            {isTiebreak ? " (TB)" : ""}
          </span>
          <span className="text-[11px] text-base-content/50 whitespace-nowrap hidden lg:inline">
            {sets
              .filter((s) => s.winner)
              .map((s) => `${s.pointsA}-${s.pointsB}`)
              .join("  ")}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1 ml-auto">
          <button
            className="btn btn-xs btn-ghost"
            title={t(
              "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule",
            )}
            disabled={matchOver || !currentSet || currentSet.pointsA === currentSet.pointsB}
            onClick={closeCurrentSet}
          >
            {t("Close Set")}
          </button>
          <button
            className={`btn btn-xs btn-square ${showVideo ? "btn-active" : "btn-ghost"}`}
            title={t("Toggle video player")}
            onClick={() => {
              const next = !showVideo;
              if (next && !videoUrl) {
                const matchUrl = initialState.match?.videoOnlineUrl;
                if (
                  matchUrl &&
                  matchUrl !== "?" &&
                  matchUrl !== "-1" &&
                  matchUrl !== -1
                ) {
                  playerReadyRef.current = false;
                  setVideoUrl(matchUrl);
                }
              }
              setShowVideo(next);
            }}
          >
            <VideoCameraIcon className="size-4" />
          </button>
          <button
            className="btn btn-xs btn-square btn-ghost"
            title={t("Coding defaults & shortcuts")}
            onClick={() => settingsDialogRef.current?.showModal()}
          >
            <Cog6ToothIcon className="size-4" />
          </button>
          <button
            className="btn btn-xs btn-square"
            onClick={undo}
            disabled={history.length === 0}
            title={`${t("Undo")}  ${formatShortcut(activeShortcuts.undoLast)}`}
          >
            <ArrowUturnLeftIcon className="size-4" />
          </button>
          <button
            className="btn btn-xs btn-square"
            onClick={redo}
            disabled={redoStack.length === 0}
            title={`${t("Redo")}  ${formatShortcut(activeShortcuts.redoLast)}`}
          >
            <ArrowUturnRightIcon className="size-4" />
          </button>
          <button
            className="btn btn-xs btn-square btn-primary"
            onClick={() => saveMatchToDatabase(false)}
            disabled={saving}
            title={saving ? t("Saving…") : t("Save")}
          >
            {saving ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <CloudArrowUpIcon className="size-4" />
            )}
          </button>
          <button
            className="btn btn-xs btn-square btn-ghost text-error"
            onClick={discardChanges}
            title={t("Discard all changes and revert to the starting point?")}
          >
            <ArrowPathIcon className="size-4" />
          </button>
          <button
            className="btn btn-xs btn-square btn-ghost"
            title={t("Back")}
            onClick={async () => {
              // Brand-new match (arrived via New Match -> setup-modal flow):
              // persist it and go STRAIGHT to the Matches list (NOT back to the
              // New Match screen), asking the list to re-fetch so the freshly
              // coded match shows up.
              if (initialState.showSetupModal) {
                try {
                  await saveMatchToDatabase(true, {
                    allowEmpty: true,
                    silent: true,
                    markSaved: false,
                  });
                } catch (_) {
                  /* best-effort — never block navigation on a save failure */
                }
                navigate("/matcheslist", { state: { refresh: true } });
              } else {
                navigate(-1);
              }
            }}
          >
            <ArrowLeftIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Video + events (when visible, sits between scoreboard and coding panel) */}
      {showVideo && (
        <div ref={videoRowRef} className="flex flex-row flex-1 min-h-0">
          {/* Video panel */}
          <div className="flex-1 min-w-0 bg-base-100 rounded border border-base-300 flex flex-col gap-1 p-2">
            <div className="flex gap-1 items-center">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => {
                  playerReadyRef.current = false;
                  setVideoUrl(e.target.value);
                }}
                placeholder="Video URL…"
                className="input input-bordered input-xs font-mono flex-1"
              />
              <label
                className="btn btn-xs btn-ghost cursor-pointer"
                title="Open local file"
              >
                📁
                <input
                  type="file"
                  className="hidden"
                  accept="video/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      playerReadyRef.current = false;
                      setVideoUrl(URL.createObjectURL(f));
                    }
                  }}
                />
              </label>
            </div>
            {videoUrl && (
              <div
                ref={videoWrapRef}
                className="flex-1 min-h-0 relative bg-black"
              >
                <ReactPlayer
                  ref={playerRef}
                  url={videoUrl}
                  controls={false}
                  playing={videoPlaying}
                  playbackRate={videoRate}
                  muted={videoMuted}
                  progressInterval={200}
                  onPlay={() => setVideoPlaying(true)}
                  onPause={() => setVideoPlaying(false)}
                  onDuration={(d) => setVideoDuration(d)}
                  onProgress={({ playedSeconds }) => {
                    if (!isSeekBarDragging) setVideoCurrentTime(playedSeconds);
                  }}
                  width="100%"
                  height="100%"
                  onReady={() => {
                    playerReadyRef.current = true;
                    // Seek to the currently-selected event ONCE per loaded
                    // video (fixes black-screen-on-first-load) — never on
                    // subsequent onReady re-fires, or the player would keep
                    // snapping back to the last-coded event and ignore the
                    // user's own wheel/drag/click seeks.
                    if (initialVideoSeekDoneRef.current) return;
                    initialVideoSeekDoneRef.current = true;
                    const ev = currentSet?.events.find(
                      (e) => e.id === selectedEventId,
                    );
                    const vp = computeEventVideoPosition(ev);
                    if (typeof vp === "number" && vp >= 0) {
                      playerRef.current?.seekTo(vp, "seconds");
                    }
                  }}
                />
                {/* Transparent capture layer — a YouTube/Vimeo/etc. embed is a
                    cross-origin IFRAME, so a wheel/mouse listener on an
                    ancestor never sees events fired while the pointer is over
                    the iframe itself (they never bubble out of it at all).
                    This overlay sits ON TOP so it is the actual hit-test
                    target for wheel/drag/tap, which is what makes mousewheel
                    scrubbing (and drag-panning) work for every provider, not
                    just plain <video> files. Native player controls are
                    disabled above since they'd be unreachable under this. */}
                <div
                  className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing"
                  onPointerDown={onVideoPointerDown}
                  onPointerMove={onVideoPointerMove}
                  onPointerUp={onVideoPointerUp}
                  onPointerLeave={onVideoPointerUp}
                  {...videoSwipeHandlers}
                />
              </div>
            )}
            {/* Seek bar — current time / scrub range / duration */}
            {videoUrl && (
              <div className="flex items-center gap-2 px-1 shrink-0">
                <span className="text-[11px] tabular-nums text-base-content/60 w-10 text-right">
                  {formatVideoTime(videoCurrentTime)}
                </span>
                <input
                  type="range"
                  className="range range-xs flex-1"
                  min={0}
                  max={videoDuration > 0 ? videoDuration : 0.001}
                  step={0.1}
                  value={Math.min(videoCurrentTime, videoDuration || 0)}
                  onMouseDown={() => setIsSeekBarDragging(true)}
                  onTouchStart={() => setIsSeekBarDragging(true)}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setVideoCurrentTime(v);
                    playerRef.current?.seekTo(v, "seconds");
                  }}
                  onMouseUp={() => setIsSeekBarDragging(false)}
                  onTouchEnd={() => setIsSeekBarDragging(false)}
                />
                <span className="text-[11px] tabular-nums text-base-content/60 w-10">
                  {formatVideoTime(videoDuration)}
                </span>
              </div>
            )}
            {/* Video toolbar — seek / play / speed (mousewheel, drag & tap also scrub/toggle) */}
            {videoUrl && (
              <div className="flex gap-1 items-center p-1 bg-base-300 rounded shrink-0">
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="-10s"
                  onClick={() => {
                    setVideoPlaying(false);
                    seekVideo(-10);
                  }}
                >
                  <ChevronDoubleLeftIcon className="size-5" />
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="-1s"
                  onClick={() => {
                    setVideoPlaying(false);
                    seekVideo(-1);
                  }}
                >
                  <ChevronLeftIcon className="size-5" />
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title={videoPlaying ? t("Pause") : t("Play")}
                  onClick={() => setVideoPlaying((p) => !p)}
                >
                  {videoPlaying ? (
                    <PauseIcon className="size-5" />
                  ) : (
                    <PlayIcon className="size-5" />
                  )}
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="+1s"
                  onClick={() => {
                    setVideoPlaying(false);
                    seekVideo(1);
                  }}
                >
                  <ChevronRightIcon className="size-5" />
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="+10s"
                  onClick={() => {
                    setVideoPlaying(false);
                    seekVideo(10);
                  }}
                >
                  <ChevronDoubleRightIcon className="size-5" />
                </button>
                <select
                  className="select select-xs select-bordered ml-1"
                  value={videoRate}
                  onChange={(e) => setVideoRate(parseFloat(e.target.value))}
                >
                  <option value="0.25">0.25x</option>
                  <option value="0.5">0.5x</option>
                  <option value="1">1x</option>
                  <option value="1.5">1.5x</option>
                  <option value="2">2x</option>
                  <option value="3">3x</option>
                  <option value="4">4x</option>
                </select>
                <span className="text-[11px] text-base-content/40 ml-2 truncate hidden md:inline">
                  {t("Scroll, drag or swipe to scrub")}
                </span>
                <button
                  className="btn btn-xs btn-ghost px-1 ml-auto"
                  title={videoMuted ? t("Unmute") : t("Mute")}
                  onClick={() => setVideoMuted((m) => !m)}
                >
                  {videoMuted ? (
                    <SpeakerXMarkIcon className="size-5" />
                  ) : (
                    <SpeakerWaveIcon className="size-5" />
                  )}
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title={
                    isVideoFullscreen ? t("Exit fullscreen") : t("Fullscreen")
                  }
                  onClick={toggleVideoFullscreen}
                >
                  {isVideoFullscreen ? (
                    <ArrowsPointingInIcon className="size-5" />
                  ) : (
                    <ArrowsPointingOutIcon className="size-5" />
                  )}
                </button>
              </div>
            )}
          </div>
          {/* Drag handle */}
          <div
            onMouseDown={startEventsResize}
            onTouchStart={startEventsResize}
            className="w-1 cursor-col-resize bg-base-300 hover:bg-primary/60 transition-colors mx-1 rounded"
            title="Drag to resize events list"
          />
          {/* Events list alongside video */}
          <div
            ref={eventsPanelRef}
            style={{ width: eventsWidth, minWidth: 240, flex: "0 0 auto" }}
            className="bg-base-100 rounded border border-base-300 flex flex-col min-h-0"
          >
            <div className="px-2 py-1 text-xs bg-base-200 flex items-center justify-between border-b border-base-300">
              <button
                className="btn btn-xs btn-ghost"
                disabled={currentSetIdx === 0}
                onClick={() => goToSet(Math.max(0, currentSetIdx - 1))}
              >
                ⤴ {t("Prev set")}
              </button>
              <span className="font-semibold">
                {t("Set")} {currentSet?.setNumber ?? currentSetIdx + 1}
              </span>
              <button
                className="btn btn-xs btn-ghost"
                disabled={currentSetIdx >= sets.length - 1}
                onClick={() =>
                  goToSet(Math.min(sets.length - 1, currentSetIdx + 1))
                }
              >
                {t("Next set")} ⤵
              </button>
            </div>
            {renderEventsHeader()}
            <ul className="overflow-auto flex-1 min-h-[3.75rem]">
              {currentSet?.events.length === 0 && (
                <li className="text-xs text-base-content/50 px-2 py-4 text-center">
                  {t("No events yet – enter a DV code above and press Enter.")}
                </li>
              )}
              {currentSet?.events.map((ev, idx) =>
                renderEventRow(ev, idx, eventsPanelWidth < 320),
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Code input — hidden in Draw mode (the court IS the input surface
          there, so the DV code box, manual-point buttons and code reference
          are all suppressed). */}
      {!drawMode && (
        <div className="bg-base-200 rounded p-2 flex flex-col gap-2 shrink-0">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              ref={inputRef}
              type="text"
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value);
                setParseError(null);
              }}
              onKeyDown={onInputKeyDown}
              placeholder="DV code – e.g. *1S#  a7r+15  *11aq#42"
              className="input input-bordered input-sm font-mono flex-1 min-w-[220px]"
              autoFocus
              spellCheck={false}
            />
            <button className="btn btn-sm btn-primary" onClick={submitCode}>
              {t("Enter")}
            </button>
          </div>
          {/* Manual rally winner buttons */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-base-content/60">Rally over:</span>
            <button
              className="btn btn-xs btn-outline"
              style={{ borderColor: colourA, color: colourA }}
              onClick={() => awardPoint("A")}
              title={`Point to ${teamAName}`}
            >
              ▲ {t("Point")} {teamAName}
              <kbd className="kbd kbd-xs ml-1 opacity-60">
                {formatShortcut(activeShortcuts.pointHome)}
              </kbd>
            </button>
            <button
              className="btn btn-xs btn-outline"
              style={{ borderColor: colourB, color: colourB }}
              onClick={() => awardPoint("B")}
              title={`Point to ${teamBName}`}
            >
              ▲ {t("Point")} {teamBName}
              <kbd className="kbd kbd-xs ml-1 opacity-60">
                {formatShortcut(activeShortcuts.pointAway)}
              </kbd>
            </button>
          </div>
          {parseError && <div className="text-error text-xs">{parseError}</div>}
          <details className="text-xs text-base-content/70">
            <summary className="cursor-pointer">
              {t("DataVolley code reference")}
            </summary>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="font-bold">{t("Format")}</div>
                <code className="font-mono">
                  {t(
                    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]",
                  )}
                </code>
                <div className="mt-1">
                  {t("team:")} <b>*</b>=A, <b>a</b>=B &nbsp;(
                  {t("omit → defaults to A")})
                </div>
                <div>{t("NN: 1 or 2 digit jersey, e.g. 1, 01, 12")}</div>
                <div>
                  {t(
                    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a",
                  )}
                </div>
              </div>
              <div>
                <div className="font-bold">{t("Skills")}</div>
                <div>
                  {t(
                    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball",
                  )}
                </div>
                <div className="font-bold mt-1">{t("Evaluation")}</div>
                <div>
                  {t(
                    "# kill/ace · + positive · ! ok · - negative · / overpass · = error",
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2">
              <div className="font-bold">{t("Skill / type omission")}</div>
              <div>
                {t(
                  "Skill and/or type may be omitted; defaults fill in the rest.",
                )}{" "}
                {t("E.g.")} <code className="font-mono">*01+5</code> →{" "}
                {t("serve with skill=A (default), type=H, eval=+, zone=5.")}
                &nbsp;<code className="font-mono">a06Q-</code> →{" "}
                {t("type-only (no skill marker), defaults skill from context.")}
              </div>
              <div className="font-bold mt-1">
                {t("Dot-coding (S→R and A→B pairs)")}
              </div>
              <div>
                <code className="font-mono">*01S+5.a06r#</code> –{" "}
                {t("serve coded, reception auto-linked.")}{" "}
                <code className="font-mono">*11AH#51.a03BH+</code> –{" "}
                {t("attack coded, block auto-linked.")}{" "}
                {t(
                  "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.",
                )}
              </div>
              <div className="font-bold mt-1">{t("Chaining & aliases")}</div>
              <div>
                <code className="font-mono">*01SH+a06RH#</code> –{" "}
                {t("two events in one string.")}{" "}
                <code className="font-mono">pa</code> /{" "}
                <code className="font-mono">pb</code> – {t("manual point A/B.")}{" "}
                <code className="font-mono">sa</code> /{" "}
                <code className="font-mono">se</code> – {t("serve ace/error.")}{" "}
                <code className="font-mono">s+ s! s- s/</code> –{" "}
                {t("serve with eval.")}
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Events list (shown when video is hidden) */}
      {!showVideo && (
        <div
          ref={eventsPanelRef}
          className="bg-base-100 rounded border border-base-300 flex-1 flex flex-col min-h-0"
        >
          <div className="px-2 py-1 text-xs bg-base-200 flex items-center justify-between border-b border-base-300">
            <button
              className="btn btn-xs btn-ghost"
              disabled={currentSetIdx === 0}
              onClick={() => goToSet(Math.max(0, currentSetIdx - 1))}
            >
              ⤴ {t("Prev set")}
            </button>
            <span className="font-semibold">
              {t("Game")} {currentSet?.setNumber ?? currentSetIdx + 1}
            </span>
            <button
              className="btn btn-xs btn-ghost"
              disabled={currentSetIdx >= sets.length - 1}
              onClick={() =>
                goToSet(Math.min(sets.length - 1, currentSetIdx + 1))
              }
            >
              {t("Next set")} ⤵
            </button>
          </div>
          {renderEventsHeader()}
          <ul className="overflow-auto flex-1 min-h-[3.75rem]">
            {currentSet?.events.length === 0 && (
              <li className="text-xs text-base-content/50 px-2 py-4 text-center">
                {t("No events yet – enter a DV code above and press Enter.")}
              </li>
            )}
            {currentSet?.events.map((ev, idx) =>
              renderEventRow(ev, idx, eventsPanelWidth < 320),
            )}
          </ul>
        </div>
      )}

      {/* Event editor — always at the BOTTOM of the right column (both video
          and no-video modes), full width, so it never crowds the events list
          or the video panel. */}
      {selectedEvent && (
        <div className="bg-base-100 rounded border border-base-300 p-2 shrink-0 overflow-y-auto max-h-[45%]">
          <div
            ref={editorContentRef}
            style={editorMinHeight ? { minHeight: editorMinHeight } : undefined}
          >
            <EventEditor
              isPortrait={false}
              event={selectedEventEditorValue}
              isCurrentEvent={true}
              team={selectedEventEditorTeam}
              isDVW={true}
              narrow={eventsPanelWidth < 320}
              onPlayerChanged={onSelectedEventPlayerChanged}
              onSkillChanged={onSelectedEventSkillChanged}
              onSubskillChanged={onSelectedEventSubskillChanged}
              onSetterCallsChanged={onSelectedEventSetterCallChanged}
              setterCalls={initialState.match?.setterCalls ?? []}
              onAttackCombosChanged={onSelectedEventAttackComboChanged}
              onTypeChanged={onSelectedEventTypeChanged}
              onBlocksChanged={onSelectedEventBlocksChanged}
              onGradeChanged={onSelectedEventGradeChanged}
              onExtCode1Changed={onSelectedEventExtCode1Changed}
              onExtCode2Changed={onSelectedEventExtCode2Changed}
              onExtCode3Changed={onSelectedEventExtCode3Changed}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="coding-viewport flex flex-row w-full p-2 bg-base-100"
    >
      <div
        style={{ width: leftWidth, flex: "0 0 auto" }}
        className="h-full overflow-hidden"
      >
        {leftColumn}
      </div>
      <div
        onMouseDown={startResize}
        onTouchStart={startResize}
        className="w-1 cursor-col-resize bg-base-300 hover:bg-primary/60 transition-colors mx-1 rounded"
        title="Drag to resize"
      />
      <div className="flex-1 min-w-0 h-full overflow-hidden">{rightColumn}</div>
      <ScoutingDefaultsModal
        dialogRef={settingsDialogRef}
        defaults={activeDefaults}
        onSave={setScoutingDefaults}
        shortcuts={activeShortcuts}
        onSaveShortcuts={setShortcutDefs}
      />
      <LineupEditorModal
        dialogRef={lineupDialogRef}
        teamName={lineupEditTeam === "A" ? teamAName : teamBName}
        teamChar={lineupEditTeam === "A" ? "*" : "a"}
        teamColour={lineupEditTeam === "A" ? colourA : colourB}
        lineup={lineupEditTeam === "A" ? lineupA : lineupB}
        roster={
          lineupEditTeam === "A" ? rosterARef.current : rosterBRef.current
        }
        setter={lineupEditTeam === "A" ? setterA : setterB}
        libero={lineupEditTeam === "A" ? liberoA : liberoB}
        setHasStarted={currentSet.events.length > 0}
        subPairs={subPairs[currentSetIdx]?.[lineupEditTeam] ?? []}
        maxSubs={effMaxSubs}
        onSave={(newLineup, newSetter, newLibero, newPairs) => {
          if (lineupEditTeam === "A") {
            setLineupA(newLineup);
            setSetterA(newSetter);
            setLiberoA(normalizeLiberoList(newLibero));
          } else {
            setLineupB(newLineup);
            setSetterB(newSetter);
            setLiberoB(normalizeLiberoList(newLibero));
          }
          setSubPairs((prev) =>
            prev.map((s, i) =>
              i === currentSetIdx ? { ...s, [lineupEditTeam]: newPairs } : s,
            ),
          );
        }}
        prevSetLineup={
          currentSetIdx > 0
            ? setStartLineupsRef.current[currentSetIdx - 1]?.[lineupEditTeam]
            : null
        }
      />
      <NominateSetterModal
        dialogRef={nominateSetterDialogRef}
        teamName={nominateSetterTeam === "A" ? teamAName : teamBName}
        teamChar={nominateSetterTeam === "A" ? "*" : "a"}
        roster={
          nominateSetterTeam === "A" ? rosterARef.current : rosterBRef.current
        }
        onSave={(shirt) => {
          if (nominateSetterTeam === "A") setSetterA(shirt);
          else if (nominateSetterTeam === "B") setSetterB(shirt);
          setNominateSetterTeam(null);
          nominateSetterDialogRef.current?.close();
        }}
      />
      <MatchSetupModal
        dialogRef={matchSetupDialogRef}
        teamAName={teamAName}
        teamBName={teamBName}
        teamAColour={colourA}
        teamBColour={colourB}
        lineupA={lineupA}
        rosterA={rosterARef.current}
        setterA={setterA}
        liberoA={liberoA}
        lineupB={lineupB}
        rosterB={rosterBRef.current}
        setterB={setterB}
        liberoB={liberoB}
        servingTeam={servingTeam}
        isSubs={isSubs}
        videoUrl={videoUrl}
        onVideoUrlChange={(url) => {
          playerReadyRef.current = false;
          setVideoUrl(url);
        }}
        onSave={(
          newLA,
          newSA,
          newLLA,
          newLB,
          newSB,
          newLLB,
          newSrv,
          newColourA,
          newColourB,
          newSquadA,
          newSquadB,
        ) => {
          // If in subs mode, detect which players changed and insert sub codes.
          if (isSubs) {
            const ts = getTimestamp();
            const subEvents = [];
            const buildSubEvents = (oldLu, newLu, teamChar, teamKey) => {
              const oldMap = {};
              const newMap = {};
              (oldLu ?? []).forEach((p) => {
                oldMap[p.position] = String(p.shirtNumber);
              });
              (newLu ?? []).forEach((p) => {
                newMap[p.position] = String(p.shirtNumber);
              });
              ["4", "3", "2", "5", "6", "1"].forEach((pos) => {
                const out = oldMap[pos];
                const inn = newMap[pos];
                if (out && inn && out !== inn) {
                  const rawOut = out.padStart(2, "0");
                  const rawIn = inn.padStart(2, "0");
                  const rawCode = `${teamChar}c${rawOut}:${rawIn}`;
                  subEvents.push({
                    id: generateUUID(),
                    EventId: generateUUID(),
                    raw: rawCode,
                    dvString: rawCode,
                    team: teamKey,
                    shirtNumber: rawOut,
                    skill: "C",
                    skillLabel: "Sub",
                    type: null,
                    evaluation: null,
                    evaluationLabel: null,
                    zoneFrom: null,
                    subzoneFrom: null,
                    zoneTo: null,
                    subzoneTo: null,
                    timestamp: ts,
                    TimeStamp: new Date(),
                    eventTypeOverride: 250,
                    eventStringOverride: "Substitution ",
                    EventType: 250,
                    EventString: "Substitution ",
                    EventGrade: null,
                    DVGrade: null,
                    SubEvent: null,
                    SubEvent2: null,
                    ErrorType: 0,
                    BallStartString: "",
                    BallEndString: "",
                    ExtraCode1: "",
                    ExtraCode2: "",
                    ExtraCode3: "",
                    setterCall: "",
                    settersCall: "",
                    attackCombo: "",
                    NumberOfBlocks: "",
                    homePlayer: teamKey === "A",
                    passingGrade: 0,
                    substitution: rawCode,
                    setter: null,
                  });
                }
              });
            };
            buildSubEvents(lineupA, newLA, "", "A");
            buildSubEvents(lineupB, newLB, "a", "B");
            if (subEvents.length > 0) {
              pushHistory();
              const currentSet = sets[currentSetIdx];
              const subWallTime = Date.now();
              subEvents.forEach((ev, idx) => {
                hydrateEventForReports(
                  ev,
                  currentSet,
                  newLA,
                  newLB,
                  subWallTime,
                  (subEvents.length - 1 - idx) * 50,
                  undefined,
                  undefined,
                  rotationA,
                  rotationB,
                );
              });
              setSets((prev) =>
                prev.map((s, i) =>
                  i === currentSetIdx
                    ? { ...s, events: [...s.events, ...subEvents] }
                    : s,
                ),
              );
              // Return to LIVE coding after the sub: the newly-appended sub
              // events are now the set's last events, so leave selection
              // pinned to the last one (and clear review mode) to keep
              // `isViewingLiveEvent` true — otherwise `selectedEventId` still
              // points at the pre-sub event and the left column drops out of
              // the Draw & Code surface into the read-only diagram. Combined
              // with `isStartOfRally` ignoring trailing "C" events, coding
              // resumes at the same rally stage (e.g. the serving phase).
              setSelectedEventId(subEvents[subEvents.length - 1].id);
              setReviewingEvent(false);
            }
          }
          setLineupA(newLA);
          setSetterA(newSA);
          setLiberoA(normalizeLiberoList(newLLA));
          setLineupB(newLB);
          setSetterB(newSB);
          setLiberoB(normalizeLiberoList(newLLB));
          setServingTeam(newSrv);
          setColourA(newColourA);
          setColourB(newColourB);
          // Keep rotationA/B (the counter DrawAndCodeCourt's formationFor trusts
          // directly to pick which row of the on-court formation tables to use)
          // in sync with wherever the setter ACTUALLY landed. This only matters
          // when a fresh starting lineup is being established — NOT during a
          // genuine mid-set substitution (isSubs), which swaps a player without
          // changing the team's rotation sequence. Without this, rotationA/B
          // default to 1 regardless of the real starting setter position (see
          // the `rotationA`/`rotationB` useState init — `teamA.rotation`/
          // `teamB.rotation` are never actually populated by NewMatch.jsx), so
          // any match/set that DOESN'T start with the setter in position 1 got
          // an on-court formation derived from the wrong array row — this is
          // exactly what "positioning is skewed" looks like even though the
          // lineup/role data itself is correct.
          if (!isSubs) {
            const newRotA = rotationForSetterInLineup(newLA, newSA);
            const newRotB = rotationForSetterInLineup(newLB, newSB);
            if (newRotA != null) setRotationA(newRotA);
            if (newRotB != null) setRotationB(newRotB);
            // Re-establishing the STARTING lineup (match start or "Edit
            // Lineup" at the start of a set) must also refresh the stable
            // starting-lineup snapshot that Draw & Code derives player ROLES
            // (S/OH1/MB1/…) — and therefore libero substitution — from.
            // Otherwise `startingLineupsRef` keeps the FIRST-ever arrangement
            // (it's captured once and never updated), so after rearranging
            // the lineup in the popup the roles stay based on the old setter
            // position: the libero can sub into the wrong back-row slot and
            // discs appear in the wrong places (only "sometimes", i.e. when
            // the re-edit actually changes the relative arrangement or a
            // libero is in play). Refresh it here so roles track the new
            // arrangement.
            startingLineupsRef.current = {
              A: safeClone(newLA),
              B: safeClone(newLB),
            };
            startingLineupsCapturedRef.current = true;
            // Persist each team's starting lineup (keyed by team name) so the
            // next set or a future match with the same team can reuse it.
            saveTeamStartingLineup(teamAName, newLA, newSA, newLLA);
            saveTeamStartingLineup(teamBName, newLB, newSB, newLLB);
          }
          // Capture the playing squad chosen in the setup modal so the sub
          // popover only offers match-selected players.
          if (newSquadA)
            setMatchSquadA(
              new Set(
                [...newSquadA].map((v) => String(parseInt(String(v), 10))),
              ),
            );
          if (newSquadB)
            setMatchSquadB(
              new Set(
                [...newSquadB].map((v) => String(parseInt(String(v), 10))),
              ),
            );
        }}
      />
    </div>
  );
}

// ─── LineupEditorModal ────────────────────────────────────────────────────────
// Click-based lineup editor + substitution tracker.
//
// Court positions (own-team perspective, net at top):
//   Front row : P4  P3  P2
//   Back row  : P5  P6  P1  ← P1 = server position
//
// Interactions:
//   Click occupied court position → select it (highlighted).
//   Court selected + click another court position → swap positions.
//   Court selected + click ★ Setter → designate as setter.
//   Court selected + click bench player → swap to bench / bring from bench.
//   Bench selected + click court position → same as above.
//   If the set has already started, court↔bench swaps are substitutions and
//   FIVB sub-pair rules are enforced.

const LINEUP_COURT_ORDER = ["4", "3", "2", "5", "6", "1"];
const LINEUP_ROT_CW = { 1: "6", 2: "1", 3: "2", 4: "3", 5: "4", 6: "5" };
const LINEUP_ROT_CCW = { 6: "1", 1: "2", 2: "3", 3: "4", 4: "5", 5: "6" };
const KB_ENTRY_ORDER = ["1", "2", "3", "4", "5", "6"];

function courtFromLineup(lu) {
  const c = {};
  LINEUP_COURT_ORDER.forEach((pos) => {
    c[pos] = null;
  });
  (lu ?? []).forEach((p) => {
    if (p.position) c[String(p.position)] = { ...p };
  });
  return c;
}

// ── Per-team starting-lineup persistence ────────────────────────────────────
// Remember each team's last-entered STARTING lineup (court positions + setter +
// liberos), keyed by team name, so the NEXT set or a FUTURE match with the same
// team can reuse it instead of re-entering every position. Stored minimally
// (position + shirt number) so it round-trips across matches whose roster
// player objects differ — it's re-matched against the current roster on load.
function teamLineupStorageKey(teamName) {
  const slug = String(teamName ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug ? `VBLiveTeamLineup_${slug}` : null;
}

function saveTeamStartingLineup(teamName, lineup, setter, libero) {
  const key = teamLineupStorageKey(teamName);
  if (!key) return;
  const slots = (lineup ?? [])
    .filter(
      (p) =>
        p &&
        p.position != null &&
        p.shirtNumber != null &&
        String(p.shirtNumber) !== "",
    )
    .map((p) => ({
      position: String(p.position),
      shirtNumber: String(p.shirtNumber),
    }));
  if (slots.length === 0) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        lineup: slots,
        setter: setter != null ? String(setter) : null,
        libero: normalizeLiberoList(libero),
      }),
    );
  } catch (e) {
    console.log("Failed to persist team lineup", e);
  }
}

function loadTeamStartingLineup(teamName) {
  const key = teamLineupStorageKey(teamName);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.lineup) || parsed.lineup.length === 0)
      return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

// Rebuild a modal court map ({position: player}) from a persisted lineup,
// re-matching each stored shirt number to a fresh player object in the current
// roster (falls back to a bare placeholder when the shirt isn't in the roster).
function courtFromSavedLineup(saved, roster) {
  const c = {};
  LINEUP_COURT_ORDER.forEach((pos) => {
    c[pos] = null;
  });
  (saved?.lineup ?? []).forEach((entry) => {
    const pos = String(entry.position);
    if (!LINEUP_COURT_ORDER.includes(pos)) return;
    const shirt = String(entry.shirtNumber);
    const rp = (roster ?? []).find((p) => String(p.shirtNumber) === shirt);
    c[pos] = rp
      ? { ...rp, position: pos, currentPosition: pos }
      : {
          shirtNumber: entry.shirtNumber,
          ShirtNumber: entry.shirtNumber,
          playerNumber: entry.shirtNumber,
          position: pos,
          currentPosition: pos,
        };
  });
  return c;
}

// A court slot only counts as "filled" once it holds a player with a real
// shirt number (an empty-numbered placeholder is still considered empty).
function isCourtSlotFilled(player) {
  return (
    player != null &&
    player.shirtNumber != null &&
    String(player.shirtNumber) !== ""
  );
}

// Next position to focus during keyboard entry: scan the current team forward
// from the just-filled slot, then the other team, then any earlier gaps in the
// current team. Returns null when every position on both teams is filled, so
// focus is removed instead of cycling back to the first team.
function nextEmptyFocus(courtA, courtB, fromTeam, fromPos) {
  const courts = { A: courtA, B: courtB };
  const other = fromTeam === "A" ? "B" : "A";
  const startIdx = KB_ENTRY_ORDER.indexOf(fromPos);
  const scan = [];
  for (let i = startIdx + 1; i < KB_ENTRY_ORDER.length; i++)
    scan.push([fromTeam, KB_ENTRY_ORDER[i]]);
  for (let i = 0; i < KB_ENTRY_ORDER.length; i++)
    scan.push([other, KB_ENTRY_ORDER[i]]);
  for (let i = 0; i <= startIdx; i++) scan.push([fromTeam, KB_ENTRY_ORDER[i]]);
  for (const [team, pos] of scan) {
    if (!isCourtSlotFilled(courts[team][pos])) return { team, pos };
  }
  return null;
}

// ── Sub-pair helpers ──────────────────────────────────────────────────────────
// pairs: [{p1, p2, count}]  count = how many times this pair has exchanged (max 2)
function checkSub(goingOut, comingIn, pairs, maxSubs) {
  const used = pairs.reduce((s, p) => s + p.count, 0);
  if (used >= maxSubs)
    return { ok: false, reason: `Max ${maxSubs} substitutions reached` };
  const pForOut = pairs.find((p) => p.p1 === goingOut || p.p2 === goingOut);
  const pForIn = pairs.find((p) => p.p1 === comingIn || p.p2 === comingIn);
  if (!pForOut && !pForIn) return { ok: true };
  if (pForOut && pForIn) {
    if (pForOut !== pForIn)
      return {
        ok: false,
        reason: "Players are already in different substitution pairs",
      };
    if (pForOut.count >= 2)
      return {
        ok: false,
        reason: "Substitution pair has already exchanged twice",
      };
    return { ok: true };
  }
  const existing = pForOut ?? pForIn;
  const fixed = pForOut ? goingOut : comingIn;
  const partner = existing.p1 === fixed ? existing.p2 : existing.p1;
  const other = pForOut ? comingIn : goingOut;
  if (partner !== other)
    return {
      ok: false,
      reason: `#${fixed} is already paired with #${partner}`,
    };
  if (existing.count >= 2)
    return {
      ok: false,
      reason: "Substitution pair has already exchanged twice",
    };
  return { ok: true };
}

function applySubPair(goingOut, comingIn, pairs) {
  const idx = pairs.findIndex(
    (p) =>
      (p.p1 === goingOut && p.p2 === comingIn) ||
      (p.p1 === comingIn && p.p2 === goingOut),
  );
  if (idx >= 0) {
    const next = [...pairs];
    next[idx] = { ...next[idx], count: next[idx].count + 1 };
    return next;
  }
  return [...pairs, { p1: goingOut, p2: comingIn, count: 1 }];
}

function LineupEditorModal({
  dialogRef,
  teamName,
  teamChar,
  teamColour,
  lineup,
  roster,
  setter,
  libero,
  setHasStarted,
  subPairs,
  maxSubs,
  onSave,
  prevSetLineup,
}) {
  const { t } = useLanguage();
  const [court, setCourt] = useState(() => courtFromLineup(lineup));
  const [localSetter, setLocalSetter] = useState(setter ?? null);
  const [localLibero, setLocalLibero] = useState(normalizeLiberoList(libero));
  // {type: "court"|"bench", key: pos|playerNum} or null
  const [selected, setSelected] = useState(null);
  const [localPairs, setLocalPairs] = useState(() =>
    JSON.parse(
      JSON.stringify(
        (subPairs ?? []).map((sp) =>
          sp ? { A: [...(sp.A ?? [])], B: [...(sp.B ?? [])] } : sp,
        ),
      ),
    ),
  );
  const [subError, setSubError] = useState(null);

  // Sync when parent data changes (team switch, external rotation, dialog re-open).
  useEffect(() => {
    setCourt(courtFromLineup(lineup));
    setLocalSetter(setter ?? null);
    setLocalLibero(normalizeLiberoList(libero));
    setLocalPairs(
      JSON.parse(
        JSON.stringify(
          (subPairs ?? []).map((sp) =>
            sp ? { A: [...(sp.A ?? [])], B: [...(sp.B ?? [])] } : sp,
          ),
        ),
      ),
    );
    setSelected(null);
    setSubError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineup, setter, libero, subPairs]);

  const courtNums = new Set(
    Object.values(court)
      .filter(Boolean)
      .flatMap((cp) =>
        [cp.shirtNumber, cp.shirtNumber]
          .filter((v) => v != null && v !== "")
          .map(String),
      ),
  );
  // allPlayers = full roster sorted by jersey; fall back to lineup players if no separate roster.
  const allPlayers = (roster ?? [])
    .filter((p) => p.shirtNumber)
    .sort(
      (a, b) => parseInt(a.shirtNumber || 0) - parseInt(b.shirtNumber || 0),
    );
  const totalSubsUsed = localPairs.reduce((s, p) => s + p.count, 0);

  const doSwap = (courtPos, benchNum) => {
    const goingOut = court[courtPos];
    const comingIn = (roster ?? []).find((p) => p.shirtNumber === benchNum);
    if (!goingOut || !comingIn) {
      setSelected(null);
      return;
    }
    if (setHasStarted) {
      const chk = checkSub(
        goingOut.shirtNumber,
        comingIn.shirtNumber,
        localPairs,
        maxSubs,
      );
      if (!chk.ok) {
        setSubError(chk.reason);
        setSelected(null);
        return;
      }
      setLocalPairs((prev) =>
        applySubPair(goingOut.shirtNumber, comingIn.shirtNumber, prev),
      );
    }
    setCourt((prev) => ({
      ...prev,
      [courtPos]: {
        ...comingIn,
        position: courtPos,
        currentPosition: courtPos,
      },
    }));
    setSelected(null);
  };

  const clickCourt = (pos) => {
    setSubError(null);
    const player = court[pos];
    if (!selected) {
      if (player) setSelected({ type: "court", key: pos });
      return;
    }
    if (selected.type === "court") {
      if (selected.key === pos) {
        setSelected(null);
        return;
      }
      // Swap two court positions (pure lineup edit, no sub rules).
      setCourt((prev) => {
        const n = { ...prev };
        const tmp = n[pos];
        n[pos] = n[selected.key];
        n[selected.key] = tmp;
        return n;
      });
      setSelected(null);
    } else {
      doSwap(pos, selected.key);
    }
  };

  const clickBench = (playerNum) => {
    setSubError(null);
    if (selected?.type === "court") {
      // Is the clicked player already on court? → court-to-court swap
      const theirPos = Object.keys(court).find(
        (p) => court[p]?.shirtNumber === playerNum,
      );
      if (theirPos) {
        if (theirPos !== selected.key) {
          setCourt((prev) => {
            const n = { ...prev };
            const tmp = n[theirPos];
            n[theirPos] = n[selected.key];
            n[selected.key] = tmp;
            return n;
          });
        }
        setSelected(null);
        return;
      }
      // Not on court → sub
      doSwap(selected.key, playerNum);
      return;
    }
    if (!selected) {
      setSelected({ type: "bench", key: playerNum });
      return;
    }
    if (selected.type === "bench") {
      setSelected(
        selected.key === playerNum ? null : { type: "bench", key: playerNum },
      );
      return;
    }
  };

  const rotate = (dir) => {
    const map = dir === "cw" ? LINEUP_ROT_CW : LINEUP_ROT_CCW;
    setCourt((prev) => {
      const next = {};
      Object.entries(prev).forEach(([pos, p]) => {
        const np = map[pos] ?? pos;
        next[np] = p ? { ...p, position: np, currentPosition: np } : null;
      });
      return next;
    });
  };

  const handleSave = () => {
    const newLineup = LINEUP_COURT_ORDER.filter((pos) => court[pos]).map(
      (pos) => ({ ...court[pos], position: pos, currentPosition: pos }),
    );
    onSave(
      newLineup,
      localSetter,
      normalizeLiberoList(localLibero),
      localPairs,
    );
    dialogRef.current?.close();
  };

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-sm">
        <h3 className="font-bold text-lg mb-2 flex items-center gap-1 min-w-0">
          <span className="opacity-50 font-mono shrink-0">{teamChar}</span>
          <span className="truncate min-w-0">{teamName}</span>
          <span className="shrink-0">— Lineup</span>
        </h3>

        {/* Sub count (only when set has started) */}
        {setHasStarted && (
          <div className="text-xs mb-2">
            <span className="text-base-content/60">
              Substitutions: {totalSubsUsed} / {maxSubs}
            </span>
            {subError && <span className="text-error ml-2">⚠ {subError}</span>}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap gap-1 mb-2">
          <button
            className="btn btn-xs btn-outline"
            onClick={() => rotate("ccw")}
            title="Rotate counter-clockwise"
          >
            ↺ ←
          </button>
          <button
            className="btn btn-xs btn-outline"
            onClick={() => rotate("cw")}
            title="Rotate clockwise (side-out direction)"
          >
            ↻ →
          </button>
          <button
            className={`btn btn-xs ${selected?.type === "court" ? "btn-warning" : "btn-outline opacity-40"}`}
            disabled={selected?.type !== "court"}
            onClick={() => {
              if (selected?.type === "court") {
                const p = court[selected.key];
                if (p) setLocalSetter(p.shirtNumber);
              }
              setSelected(null);
            }}
            title="Designate selected player as setter"
          >
            ★ Setter
          </button>
          <button
            className={`btn btn-xs btn-info ${
              selected?.type === "court" || selected?.type === "bench"
                ? ""
                : "opacity-40"
            }`}
            disabled={!selected}
            onClick={() => {
              if (selected?.type === "court") {
                const p = court[selected.key];
                if (p)
                  setLocalLibero(
                    toggleLiberoNumber(localLibero, p.shirtNumber),
                  );
              } else if (selected?.type === "bench") {
                setLocalLibero(toggleLiberoNumber(localLibero, selected.key));
              }
              setSelected(null);
            }}
            title="Toggle selected player as libero"
          >
            L Libero
          </button>
          {prevSetLineup && (
            <button
              className="btn btn-xs btn-outline btn-info"
              onClick={() => {
                setCourt(courtFromLineup(prevSetLineup));
                setSelected(null);
              }}
              title="Load lineup from previous set"
            >
              ⤴ {t("Prev set")}
            </button>
          )}
          <button
            className="btn btn-xs btn-ghost ml-auto"
            onClick={() => {
              setCourt(
                LINEUP_COURT_ORDER.reduce((o, p) => ({ ...o, [p]: null }), {}),
              );
              setSelected(null);
            }}
          >
            {t("Clear")}
          </button>
        </div>

        {/* Court */}
        <div className="text-[10px] text-base-content/40 mb-0.5">
          ← net · front row
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-1.5">
          {["4", "3", "2"].map((pos) => (
            <CourtCell
              key={pos}
              pos={pos}
              player={court[pos]}
              setter={localSetter}
              libero={localLibero}
              teamColour={teamColour}
              isSelected={selected?.type === "court" && selected.key === pos}
              onClick={() => clickCourt(pos)}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-0.5">
          {["5", "6", "1"].map((pos) => (
            <CourtCell
              key={pos}
              pos={pos}
              player={court[pos]}
              setter={localSetter}
              libero={localLibero}
              teamColour={teamColour}
              isSelected={selected?.type === "court" && selected.key === pos}
              onClick={() => clickCourt(pos)}
            />
          ))}
        </div>
        <div className="text-[10px] text-base-content/40 mb-3">
          back row · P1 = server
        </div>

        {/* Player list — all roster players; on-court ones are dimmed/disabled */}
        {allPlayers.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] text-base-content/40 mb-1">
              — players
              {selected?.type === "court" && (
                <span className="ml-1 text-primary">
                  {" "}
                  · select any player to swap
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {allPlayers.map((p) => {
                const isOnCourt = courtNums.has(String(p.shirtNumber));
                const isSel =
                  selected?.type === "bench" && selected.key === p.shirtNumber;
                const isLiberoPlayer = isLiberoNumber(
                  localLibero,
                  p.shirtNumber,
                );
                const courtSelected = selected?.type === "court";
                const isClickable = !isOnCourt || courtSelected;
                const fullName =
                  [p.FirstName, p.LastName].filter(Boolean).join(" ") ||
                  p.NickName ||
                  "";
                return (
                  <button
                    key={p.shirtNumber}
                    className={`btn btn-xs font-mono ${
                      isOnCourt && !courtSelected
                        ? "btn-ghost opacity-30 cursor-not-allowed"
                        : isOnCourt && courtSelected
                          ? "btn-warning"
                          : isSel
                            ? ""
                            : isLiberoPlayer
                              ? "btn-info btn-outline"
                              : "btn-outline"
                    }`}
                    style={
                      isSel && !(isOnCourt && courtSelected)
                        ? {
                            backgroundColor: teamColour,
                            color: contrastFor(teamColour),
                            borderColor: teamColour,
                          }
                        : undefined
                    }
                    disabled={!isClickable}
                    onClick={() => isClickable && clickBench(p.shirtNumber)}
                    title={
                      isOnCourt && !courtSelected
                        ? `${fullName || p.shirtNumber} — on court`
                        : isLiberoPlayer
                          ? `${fullName || "#" + p.shirtNumber} (Libero)`
                          : fullName || `#${p.shirtNumber}`
                    }
                  >
                    {p.shirtNumber}
                    {isLiberoPlayer && !isOnCourt && (
                      <span className="text-[8px] ml-0.5">L</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="modal-action mt-0">
          <button
            className="btn btn-sm"
            style={{
              backgroundColor: teamColour,
              color: contrastFor(teamColour),
              borderColor: teamColour,
            }}
            onClick={handleSave}
          >
            Save
          </button>
          <button
            className="btn btn-sm"
            onClick={() => {
              setSelected(null);
              dialogRef.current?.close();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
}

// ─── NominateSetterModal ────────────────────────────────────────────────────
// Forced prompt shown whenever a configured team's setter is null (see the
// effect above `setNominateSetterTeam` — team.setter should never be null).
// Lets the user pick which roster player is that team's setter; can't be
// dismissed without choosing one (no cancel button, and native Escape/
// backdrop dismissal is blocked below).
function NominateSetterModal({
  dialogRef,
  teamName,
  teamChar,
  roster,
  onSave,
}) {
  const { t } = useLanguage();
  const players = (roster ?? [])
    .filter((p) => p.shirtNumber)
    .sort(
      (a, b) =>
        parseInt(a.shirtNumber || 0, 10) - parseInt(b.shirtNumber || 0, 10),
    );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onCancel = (e) => e.preventDefault(); // setter is mandatory — block Escape-close
    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [dialogRef]);

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-sm">
        <h3 className="font-bold text-lg mb-2 flex items-center gap-1 min-w-0">
          <span className="opacity-50 font-mono shrink-0">{teamChar}</span>
          <span className="truncate min-w-0">{teamName}</span>
          <span className="shrink-0">— {t("Nominate Setter")}</span>
        </h3>
        <p className="text-sm text-base-content/60 mb-3">
          {t(
            "This team has no designated setter. Pick the player who is setting.",
          )}
        </p>
        {players.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {players.map((p) => {
              const fullName =
                [p.FirstName, p.LastName].filter(Boolean).join(" ") ||
                p.NickName ||
                "";
              return (
                <button
                  key={p.shirtNumber}
                  className="btn btn-outline btn-sm font-mono"
                  onClick={() => onSave(p.shirtNumber)}
                  title={fullName || `#${p.shirtNumber}`}
                >
                  {p.shirtNumber}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-warning">
            {t("No roster players available.")}
          </div>
        )}
      </div>
    </dialog>
  );
}

// The Opposite always sits diagonally from the Setter (offset +3 of 6 court
// positions — see `roleForPosition`/`ROLE_BY_OFFSET` in DrawAndCodeCourt.jsx,
// which this mirrors) — used by the Double Substitution flow to find which
// court slot currently holds the Opposite once the Setter's slot is known.
function oppositeCourtPosition(setterPos) {
  const n = parseInt(setterPos, 10);
  if (!Number.isFinite(n)) return null;
  return String(((((n - 1 + 3) % 6) + 6) % 6) + 1);
}

// Finds which court slot (position key, e.g. "1") currently holds the given
// shirt number, or null if that shirt isn't on court.
function positionOfShirtInCourt(court, shirt) {
  if (shirt == null) return null;
  const key = String(shirt);
  const found = Object.entries(court ?? {}).find(
    ([, p]) => p && String(p.shirtNumber) === key,
  );
  return found ? found[0] : null;
}

// ─── MatchSetupModal ──────────────────────────────────────────────────────────
// Two-tab lineup setup dialog shown at match start (navigated from NewMatch).
// Allows configuring both team's starting lineups, setters, liberos,
// and the initial serving team — all in one place before coding begins.
function MatchSetupModal({
  dialogRef,
  teamAName,
  teamBName,
  teamAColour,
  teamBColour,
  lineupA,
  rosterA,
  setterA,
  liberoA,
  lineupB,
  rosterB,
  setterB,
  liberoB,
  servingTeam,
  isSubs,
  videoUrl,
  onVideoUrlChange,
  onSave,
}) {
  const { t } = useLanguage();
  const modalPlayerRef = useRef(null);
  const videoWrapRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoRate, setVideoRate] = useState(1);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [isSeekBarDragging, setIsSeekBarDragging] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const dragRef = useRef({ active: false, lastX: 0 });

  // Seek the modal video by `delta` seconds (mirrors CodingPage's seek/swipe).
  const seekVideo = (delta) => {
    const p = modalPlayerRef.current;
    if (!p || typeof p.getCurrentTime !== "function") return;
    const cur = p.getCurrentTime() ?? 0;
    const dur = videoDuration || Infinity;
    const next = Math.max(0, Math.min(dur, cur + delta));
    p.seekTo(next, "seconds");
    setVideoCurrentTime(next);
  };

  // Mousewheel scrubbing — a non-passive listener so we can preventDefault and
  // stop the modal from scrolling. Scoped to the video wrapper.
  useEffect(() => {
    const el = videoWrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!modalPlayerRef.current) return;
      e.preventDefault();
      setVideoPlaying(false);
      // Finer-grained than the drag/toolbar seeks — one wheel notch only
      // nudges a fraction of a second.
      seekVideo(-e.deltaY / 300);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fullscreen toggle + keep isVideoFullscreen in sync with ESC/browser exits.
  const toggleVideoFullscreen = () => {
    const el = videoWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  useEffect(() => {
    const onFsChange = () =>
      setIsVideoFullscreen(
        !!document.fullscreenElement &&
          document.fullscreenElement === videoWrapRef.current,
      );
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Drag-to-pan scrubbing (mouse + touch) over the video surface.
  const onVideoPointerDown = (e) => {
    const p = modalPlayerRef.current;
    const cur =
      p && typeof p.getCurrentTime === "function" ? (p.getCurrentTime() ?? 0) : 0;
    dragRef.current = {
      active: true,
      lastX: e.clientX,
      moved: 0,
      target: cur,
      lastSeek: 0,
    };
  };
  const onVideoPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.lastX;
    d.moved = (d.moved || 0) + Math.abs(dx);
    if (Math.abs(dx) < 2) return;
    d.lastX = e.clientX;
    // Accumulate a TARGET time and only seek the player ~10×/sec — seeking on
    // every pointermove re-buffers an online source and makes iPad scrubbing
    // jerky. The scrub bar still updates every move for smooth feedback.
    const dur = videoDuration || Infinity;
    d.target = Math.max(0, Math.min(dur, (d.target ?? 0) + dx / 40));
    setVideoPlaying(false);
    setVideoCurrentTime(d.target);
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - (d.lastSeek || 0) >= 100) {
      d.lastSeek = now;
      const p = modalPlayerRef.current;
      if (p && typeof p.seekTo === "function") p.seekTo(d.target, "seconds");
    }
  };
  const onVideoPointerUp = () => {
    const d = dragRef.current;
    // A tap (negligible movement) toggles play/pause — native controls are
    // disabled (unreachable under the capture overlay), so this restores
    // basic click-to-play/pause.
    if (d.active && (d.moved || 0) < 6) {
      setVideoPlaying((p) => !p);
    } else if (d.active) {
      const p = modalPlayerRef.current;
      if (p && typeof p.seekTo === "function") p.seekTo(d.target ?? 0, "seconds");
    }
    d.active = false;
  };

  // Swipe-to-scrub for touch devices (matches CodingPage's useSwipeable).
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => seekVideo(-2.5),
    onSwipedRight: () => seekVideo(2.5),
    trackMouse: false,
  });

  const [courtA, setCourtA] = useState(() => courtFromLineup(lineupA));
  const [courtB, setCourtB] = useState(() => courtFromLineup(lineupB));
  const [localSetterA, setLocalSetterA] = useState(setterA ?? null);
  const [localSetterB, setLocalSetterB] = useState(setterB ?? null);
  const [localLiberoA, setLocalLiberoA] = useState(
    normalizeLiberoList(liberoA),
  );
  const [localLiberoB, setLocalLiberoB] = useState(
    normalizeLiberoList(liberoB),
  );
  const [localServing, setLocalServing] = useState(servingTeam ?? "A");
  const [localColourA, setLocalColourA] = useState(teamAColour ?? "lime");
  const [localColourB, setLocalColourB] = useState(teamBColour ?? "magenta");
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);
  // Double-substitution flow: { side, step: "setter"|"opposite", setterPos, oppPos }
  // or null when no double-sub is in progress — see the "Double Sub" button
  // and `makeActions`'s `clickBench` below.
  const [doubleSub, setDoubleSub] = useState(null);

  // Playing squad (which roster players are actually part of today's match) —
  // separate from ON-COURT POSITION assignment below. Reuses NewMatch.jsx's
  // own <SquadPicker> toggle-on/off component (shown in a "show/hide" dropdown
  // just below each team's player list). Defaults to the whole roster when it
  // has 14 or fewer players (FIVB match-sheet limit); larger rosters default
  // to just the first 14 (by shirt number) so the modal doesn't start with an
  // over-sized squad. Only squad members are offered for court-position
  // assignment (bench buttons and keyboard shirt-number entry). Squad
  // selection never adds anyone to the lineup by itself — it only REMOVES a
  // player from their on-court slot (and clears them as setter/libero) the
  // moment they're taken out of the squad, since a benched player can no
  // longer be on court.
  const MAX_SQUAD = 14; // FIVB match sheet player limit per team
  const rosterShirts = (roster) => {
    const withShirt = (roster ?? [])
      .filter((p) => p.shirtNumber)
      .sort(
        (a, b) => parseInt(a.shirtNumber || 0) - parseInt(b.shirtNumber || 0),
      );
    // Default the squad to the WHOLE roster when it fits under the match-sheet
    // limit; if it's over the limit, start EMPTY (the user must pick who dresses
    // for this match).
    const defaultSquad = withShirt.length > MAX_SQUAD ? [] : withShirt;
    return new Set(defaultSquad.map((p) => p.shirtNumber));
  };
  const [squadA, setSquadA] = useState(() => rosterShirts(rosterA));
  const [squadB, setSquadB] = useState(() => rosterShirts(rosterB));
  const [showSquadPickerA, setShowSquadPickerA] = useState(false);
  const [showSquadPickerB, setShowSquadPickerB] = useState(false);

  useEffect(() => {
    setSquadA(rosterShirts(rosterA));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterA]);

  useEffect(() => {
    setSquadB(rosterShirts(rosterB));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterB]);

  const squadRosterA = (rosterA ?? []).filter((p) => squadA.has(p.shirtNumber));
  const squadRosterB = (rosterB ?? []).filter((p) => squadB.has(p.shirtNumber));

  // A player is a libero by role tag (from the roster / New Match selection).
  const isLiberoRolePlayer = (p) => {
    const r = String(p?.role1 ?? p?.Role1 ?? p?.role ?? "").toUpperCase();
    return r === "L" || r === "LIB" || r === "LIBERO" || hasLiberoPosition(p);
  };
  // Merge any squad member carrying the libero role into a libero list so that
  // adding such a player (manually via the squad picker, or automatically as
  // part of the default squad) auto-designates them as a libero. Capped at 2 by
  // `normalizeLiberoList`.
  const mergeRoleLiberos = (baseList, roster, squadSet) => {
    const merged = [...normalizeLiberoList(baseList)];
    (roster ?? [])
      .filter((p) => squadSet.has(p.shirtNumber) && isLiberoRolePlayer(p))
      .forEach((p) => {
        const n = parseInt(String(p.shirtNumber), 10);
        if (Number.isFinite(n) && !merged.includes(n)) merged.push(n);
      });
    return normalizeLiberoList(merged);
  };

  // Apply a persisted starting lineup (saved when a previous set/match's lineup
  // was confirmed) to `side`, re-matching shirt numbers to the current roster
  // and ensuring those players are in the match squad (otherwise the squad-strip
  // effect below would immediately clear them). Returns true if a saved lineup
  // existed and was applied.
  const applySavedLineup = (side) => {
    const roster = side === "A" ? rosterA : rosterB;
    const teamName = side === "A" ? teamAName : teamBName;
    const saved = loadTeamStartingLineup(teamName);
    if (!saved) return false;
    const resolveShirt = (v) => {
      const rp = (roster ?? []).find(
        (p) => String(p.shirtNumber) === String(v),
      );
      return rp ? rp.shirtNumber : v;
    };
    const shirts = new Set();
    (saved.lineup ?? []).forEach((e) => shirts.add(resolveShirt(e.shirtNumber)));
    (saved.libero ?? []).forEach((n) => shirts.add(resolveShirt(n)));
    (side === "A" ? setSquadA : setSquadB)((prev) => {
      const next = new Set(prev);
      shirts.forEach((s) => next.add(s));
      return next;
    });
    const court = courtFromSavedLineup(saved, roster);
    const setterPlayer =
      saved.setter != null
        ? (roster ?? []).find(
            (p) => String(p.shirtNumber) === String(saved.setter),
          )
        : null;
    const setter = setterPlayer
      ? setterPlayer.shirtNumber
      : (saved.setter ?? null);
    const libero = mergeRoleLiberos(saved.libero ?? [], roster, shirts);
    (side === "A" ? setCourtA : setCourtB)(court);
    (side === "A" ? setLocalSetterA : setLocalSetterB)(setter);
    (side === "A" ? setLocalLiberoA : setLocalLiberoB)(libero);
    return true;
  };

  // If a player currently ON COURT (or the designated setter/libero) is taken
  // out of the squad, free their slot / clear the role — they can't keep
  // playing once benched. Squad selection otherwise never touches the court.
  useEffect(() => {
    setCourtA((prev) => {
      let changed = false;
      const next = { ...prev };
      LINEUP_COURT_ORDER.forEach((pos) => {
        if (next[pos] && !squadA.has(next[pos].shirtNumber)) {
          next[pos] = null;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setLocalSetterA((prev) =>
      prev != null && !squadA.has(prev) ? null : prev,
    );
    setLocalLiberoA((prev) =>
      mergeRoleLiberos(
        (prev ?? []).filter((shirt) => squadA.has(shirt)),
        rosterA,
        squadA,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadA]);

  useEffect(() => {
    setCourtB((prev) => {
      let changed = false;
      const next = { ...prev };
      LINEUP_COURT_ORDER.forEach((pos) => {
        if (next[pos] && !squadB.has(next[pos].shirtNumber)) {
          next[pos] = null;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setLocalSetterB((prev) =>
      prev != null && !squadB.has(prev) ? null : prev,
    );
    setLocalLiberoB((prev) =>
      mergeRoleLiberos(
        (prev ?? []).filter((shirt) => squadB.has(shirt)),
        rosterB,
        squadB,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadB]);

  useEffect(() => {
    setLocalColourA(teamAColour ?? "lime");
  }, [teamAColour]);

  useEffect(() => {
    setLocalColourB(teamBColour ?? "magenta");
  }, [teamBColour]);

  useEffect(() => {
    const baseCourt = courtFromLineup(lineupA);
    const hasPlayers = LINEUP_COURT_ORDER.some((pos) =>
      isCourtSlotFilled(baseCourt[pos]),
    );
    // For a fresh lineup (new match/set with no players yet), reuse the team's
    // last-saved starting lineup instead of an empty court.
    if (!isSubs && !hasPlayers && applySavedLineup("A")) {
      setSelectedA(null);
      setDoubleSub((prev) => (prev?.side === "A" ? null : prev));
      return;
    }
    setCourtA(baseCourt);
    setLocalSetterA(setterA ?? null);
    setLocalLiberoA(mergeRoleLiberos(liberoA, rosterA, squadA));
    setSelectedA(null);
    setDoubleSub((prev) => (prev?.side === "A" ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineupA, setterA, liberoA]);

  useEffect(() => {
    const baseCourt = courtFromLineup(lineupB);
    const hasPlayers = LINEUP_COURT_ORDER.some((pos) =>
      isCourtSlotFilled(baseCourt[pos]),
    );
    if (!isSubs && !hasPlayers && applySavedLineup("B")) {
      setSelectedB(null);
      setDoubleSub((prev) => (prev?.side === "B" ? null : prev));
      return;
    }
    setCourtB(baseCourt);
    setLocalSetterB(setterB ?? null);
    setLocalLiberoB(mergeRoleLiberos(liberoB, rosterB, squadB));
    setSelectedB(null);
    setDoubleSub((prev) => (prev?.side === "B" ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineupB, setterB, liberoB]);

  // ── Keyboard-driven lineup entry ─────────────────────────────────────────
  const [focusedPos, setFocusedPos] = useState({ team: "A", pos: "1" });
  const [inputBuffer, setInputBuffer] = useState("");
  const [entryError, setEntryError] = useState(false);

  // Reset focus when dialog is closed
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => {
      setFocusedPos({ team: "A", pos: "1" });
      setInputBuffer("");
      setEntryError(false);
      setDoubleSub(null);
    };
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolves a typed jersey number against this team's FULL roster ("squad")
  // and today's match-day picks ("selection"/squadA/squadB) — used by the
  // Enter/s-key handlers below to decide whether a typed number can be
  // placed on court:
  //   - team has no roster loaded at all      -> "ok" (free entry, matches
  //     the existing "No roster loaded — edit court positions directly" state)
  //   - not found in the roster at all        -> "unknown"
  //   - already in the selection              -> "ok"
  //   - in the roster but not yet selected,
  //     and there's room in the selection     -> "add" (auto-add + place)
  //   - in the roster but the selection is
  //     already at MAX_SQUAD                  -> "full"
  const resolveJerseyEntry = (team, shirt) => {
    const fullRoster = team === "A" ? rosterA : rosterB;
    const squadSet = team === "A" ? squadA : squadB;
    if (!fullRoster || fullRoster.length === 0)
      return { status: "ok", player: null };
    const found = fullRoster.find((p) => String(p.shirtNumber) === shirt);
    if (!found) return { status: "unknown" };
    if (squadSet.has(found.shirtNumber)) return { status: "ok", player: found };
    if (squadSet.size < MAX_SQUAD) return { status: "add", player: found };
    return { status: "full" };
  };

  // Keyboard handler — placed on the <dialog> element so it receives all
  // bubbled events from inside the modal with fresh state in the closure.
  const handleModalKeyDown = (e) => {
    const tag = e.target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    if (!focusedPos) return;

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      if (entryError) {
        setEntryError(false);
        setInputBuffer(e.key);
      } else {
        setInputBuffer((prev) => (prev + e.key).slice(-2));
      }
    } else if (e.key === "Backspace") {
      e.preventDefault();
      setEntryError(false);
      setInputBuffer((prev) => prev.slice(0, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (!inputBuffer) return;
      const { team, pos } = focusedPos;
      const court = team === "A" ? courtA : courtB;
      const setCourt = team === "A" ? setCourtA : setCourtB;
      const setSquadSet = team === "A" ? setSquadA : setSquadB;
      const resolved = resolveJerseyEntry(team, inputBuffer);
      if (resolved.status === "unknown") {
        setEntryError(true);
        toast.warn(`#${inputBuffer} ${t("is not on this team's roster.")}`);
        return;
      }
      if (resolved.status === "full") {
        setEntryError(true);
        toast.warn(
          t(
            "Playing squad is full. Remove a player from the squad before adding another.",
          ),
        );
        return;
      }
      if (
        Object.entries(court).some(
          ([p, pl]) => p !== pos && String(pl?.shirtNumber) === inputBuffer,
        )
      ) {
        setEntryError(true);
        return;
      }
      setEntryError(false);
      if (resolved.status === "add") {
        setSquadSet((prev) => new Set(prev).add(resolved.player.shirtNumber));
      }
      const player = resolved.player ?? { shirtNumber: inputBuffer };
      const newCourt = {
        ...court,
        [pos]: { ...player, position: pos, currentPosition: pos },
      };
      setCourt(newCourt);
      setInputBuffer("");
      const courtANow = team === "A" ? newCourt : courtA;
      const courtBNow = team === "B" ? newCourt : courtB;
      // Move to the next empty slot; clear focus once both teams are complete
      // (rather than cycling back to the first team).
      setFocusedPos(nextEmptyFocus(courtANow, courtBNow, team, pos));
    } else if (e.key === "s" || e.key === "S") {
      e.preventDefault();
      const { team, pos } = focusedPos;
      const court = team === "A" ? courtA : courtB;
      const setCourt = team === "A" ? setCourtA : setCourtB;
      const setLocalSetter = team === "A" ? setLocalSetterA : setLocalSetterB;

      // If inputBuffer has content, treat as "number+s" (e.g., "4s")
      if (inputBuffer) {
        const setSquadSet = team === "A" ? setSquadA : setSquadB;
        const resolved = resolveJerseyEntry(team, inputBuffer);
        if (resolved.status === "unknown") {
          setEntryError(true);
          toast.warn(`#${inputBuffer} ${t("is not on this team's roster.")}`);
          return;
        }
        if (resolved.status === "full") {
          setEntryError(true);
          toast.warn(
            t(
              "Playing squad is full. Remove a player from the squad before adding another.",
            ),
          );
          return;
        }
        if (
          Object.entries(court).some(
            ([p, pl]) => p !== pos && String(pl?.shirtNumber) === inputBuffer,
          )
        ) {
          setEntryError(true);
          return;
        }
        setEntryError(false);
        if (resolved.status === "add") {
          setSquadSet((prev) => new Set(prev).add(resolved.player.shirtNumber));
        }
        const player = resolved.player ?? { shirtNumber: inputBuffer };
        const newCourt = {
          ...court,
          [pos]: { ...player, position: pos, currentPosition: pos },
        };
        setCourt(newCourt);
        setLocalSetter(player.shirtNumber);
        setInputBuffer("");
        const courtANow = team === "A" ? newCourt : courtA;
        const courtBNow = team === "B" ? newCourt : courtB;
        setFocusedPos(nextEmptyFocus(courtANow, courtBNow, team, pos));
      } else {
        // No inputBuffer: mark the already-focused player as setter
        const player = court[pos];
        if (isCourtSlotFilled(player)) {
          setLocalSetter(player.shirtNumber);
        }
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      setEntryError(false);
      setInputBuffer("");
      const { team, pos } = focusedPos;
      const idx = KB_ENTRY_ORDER.indexOf(pos);
      if (idx < KB_ENTRY_ORDER.length - 1) {
        setFocusedPos({ team, pos: KB_ENTRY_ORDER[idx + 1] });
      } else if (team === "A") {
        setFocusedPos({ team: "B", pos: KB_ENTRY_ORDER[0] });
      } else {
        // End of team B: stop here instead of cycling back to team A.
        setFocusedPos(null);
      }
    }
  };

  // Per-side action helpers
  const makeActions = (side) => {
    const court = side === "A" ? courtA : courtB;
    const setCourt = side === "A" ? setCourtA : setCourtB;
    const roster = side === "A" ? squadRosterA : squadRosterB;
    const selected = side === "A" ? selectedA : selectedB;
    const setSelected = side === "A" ? setSelectedA : setSelectedB;
    const localSetter = side === "A" ? localSetterA : localSetterB;
    const setLocalSetter = side === "A" ? setLocalSetterA : setLocalSetterB;

    const clickCourt = (pos) => {
      // While a double-substitution is in progress for this side, court
      // clicks don't do the normal select/swap thing — bench clicks (below)
      // feed straight into the flashing setter/opposite slot. Clicking the
      // flashing slot itself cancels the double-sub.
      if (doubleSub?.side === side) {
        if (pos === doubleSub.setterPos || pos === doubleSub.oppPos)
          setDoubleSub(null);
        return;
      }
      const focusChanging =
        focusedPos?.team !== side || focusedPos?.pos !== pos;
      if (focusChanging) {
        setInputBuffer("");
        setEntryError(false);
      }
      setFocusedPos({ team: side, pos });
      if (!selected) {
        setSelected({ type: "court", key: pos });
        return;
      }
      if (selected.type === "court") {
        if (selected.key === pos) {
          setSelected(null);
          return;
        }
        setCourt((prev) => {
          const n = { ...prev };
          const t = n[pos];
          n[pos] = n[selected.key];
          n[selected.key] = t;
          return n;
        });
        setSelected(null);
      } else {
        const c = (roster ?? []).find((p) => p.shirtNumber === selected.key);
        if (!c) {
          setSelected(null);
          return;
        }
        // A bench player replacing whoever currently holds the SETTER'S own
        // shirt on court is a normal (single) substitution for the setter —
        // the incoming player automatically takes over the setter role
        // (unless a Double Substitution is in progress, handled above).
        const outgoing = court[pos];
        if (
          outgoing &&
          localSetter != null &&
          String(outgoing.shirtNumber) === String(localSetter)
        ) {
          setLocalSetter(c.shirtNumber);
        }
        setCourt((prev) => ({
          ...prev,
          [pos]: { ...c, position: pos, currentPosition: pos },
        }));
        setSelected(null);
      }
    };
    const clickBench = (num) => {
      // Double-substitution flow: the two bench picks (in order) go straight
      // into the flashing setter slot, then the flashing opposite slot. The
      // player who ends up in the OPPOSITE'S slot becomes the new setter —
      // the other incoming player (now in the old setter's slot) becomes the
      // new opposite automatically, since roles are always computed as a
      // fixed offset from whoever the setter is.
      if (doubleSub?.side === side) {
        const c = (roster ?? []).find((p) => p.shirtNumber === num);
        if (!c) return;
        if (doubleSub.step === "setter") {
          setCourt((prev) => ({
            ...prev,
            [doubleSub.setterPos]: {
              ...c,
              position: doubleSub.setterPos,
              currentPosition: doubleSub.setterPos,
            },
          }));
          setDoubleSub({ ...doubleSub, step: "opposite" });
        } else {
          setCourt((prev) => ({
            ...prev,
            [doubleSub.oppPos]: {
              ...c,
              position: doubleSub.oppPos,
              currentPosition: doubleSub.oppPos,
            },
          }));
          setLocalSetter(num);
          setDoubleSub(null);
        }
        setSelected(null);
        return;
      }
      if (!selected) {
        setSelected({ type: "bench", key: num });
        return;
      }
      if (selected.type === "bench") {
        setSelected(selected.key === num ? null : { type: "bench", key: num });
        return;
      }
      // Only place bench player on court if we're still in focus mode (focusedPos not null).
      // Once all positions are filled and focusedPos is null, bench players can only be used for Lib selection.
      if (!focusedPos) {
        setSelected({ type: "bench", key: num });
        return;
      }
      const c = (roster ?? []).find((p) => p.shirtNumber === num);
      if (!c) {
        setSelected(null);
        return;
      }
      // Same auto setter-takeover rule as clickCourt's bench-onto-court path.
      const outgoing = court[selected.key];
      if (
        outgoing &&
        localSetter != null &&
        String(outgoing.shirtNumber) === String(localSetter)
      ) {
        setLocalSetter(c.shirtNumber);
      }
      setCourt((prev) => ({
        ...prev,
        [selected.key]: {
          ...c,
          position: selected.key,
          currentPosition: selected.key,
        },
      }));
      setSelected(null);
    };
    const rotate = (dir) => {
      const map = dir === "cw" ? LINEUP_ROT_CW : LINEUP_ROT_CCW;
      setCourt((prev) => {
        const next = {};
        Object.entries(prev).forEach(([pos, p]) => {
          const np = map[pos] ?? pos;
          next[np] = p ? { ...p, position: np, currentPosition: np } : null;
        });
        return next;
      });
    };
    // Kicks off the Double Substitution flow: flashes the setter's slot
    // first (the user picks a bench replacement for it), then flashes the
    // opposite's slot — see `clickBench` above for how the two picks resolve.
    const startDoubleSub = () => {
      const setterPos = positionOfShirtInCourt(court, localSetter);
      if (!setterPos) return;
      const oppPos = oppositeCourtPosition(setterPos);
      setSelected(null);
      setDoubleSub({ side, step: "setter", setterPos, oppPos });
    };
    return {
      clickCourt,
      clickBench,
      rotate,
      startDoubleSub,
      selected,
      setSelected,
      court,
    };
  };

  const handleStart = () => {
    const toLineup = (c) =>
      LINEUP_COURT_ORDER.filter((pos) => c[pos]).map((pos) => ({
        ...c[pos],
        position: pos,
        currentPosition: pos,
      }));
    onSave(
      toLineup(courtA),
      localSetterA,
      normalizeLiberoList(localLiberoA),
      toLineup(courtB),
      localSetterB,
      normalizeLiberoList(localLiberoB),
      localServing,
      localColourA,
      localColourB,
      squadA,
      squadB,
    );
    dialogRef.current?.close();
  };

  const renderTeamPanel = (side) => {
    const {
      clickCourt,
      clickBench,
      rotate,
      startDoubleSub,
      selected,
      setSelected,
      court,
    } = makeActions(side);
    const shirtKey = (value) => {
      const n = Number.parseInt(String(value ?? ""), 10);
      return Number.isFinite(n) ? String(n) : String(value ?? "");
    };
    const isTeamA = side === "A";
    const flipped = side === "B"; // B's front col faces left (toward net / team A)
    const teamColour = isTeamA
      ? (localColourA ?? "#888")
      : (localColourB ?? "#888");
    const setLocalColour = isTeamA ? setLocalColourA : setLocalColourB;
    // `roster` (squad-filtered) drives court-position assignment (bench
    // buttons, the dropdown, keyboard shirt-number entry); `fullRoster` (the
    // WHOLE team roster) drives the "Playing squad" toggle list below, since
    // a player must be visible there in order to be added to the squad.
    const roster = isTeamA ? squadRosterA : squadRosterB;
    const fullRoster = isTeamA ? rosterA : rosterB;
    const squadSet = isTeamA ? squadA : squadB;
    const setSquadSet = isTeamA ? setSquadA : setSquadB;
    const showSquadPicker = isTeamA ? showSquadPickerA : showSquadPickerB;
    const setShowSquadPicker = isTeamA
      ? setShowSquadPickerA
      : setShowSquadPickerB;
    const localSetter = isTeamA ? localSetterA : localSetterB;
    const setLocalSetter = isTeamA ? setLocalSetterA : setLocalSetterB;
    const localLibero = isTeamA ? localLiberoA : localLiberoB;
    const setLocalLibero = isTeamA ? setLocalLiberoA : setLocalLiberoB;
    const teamName = isTeamA ? teamAName : teamBName;
    const teamChar = isTeamA ? "*" : "a";
    const courtNums = new Set(
      Object.values(court)
        .filter(Boolean)
        .map((p) => shirtKey(p.shirtNumber)),
    );
    const allPlayers = (roster ?? [])
      .filter((p) => p.shirtNumber)
      .sort(
        (a, b) => parseInt(a.shirtNumber || 0) - parseInt(b.shirtNumber || 0),
      );
    const allRosterPlayers = (fullRoster ?? [])
      .filter((p) => p.shirtNumber)
      .sort(
        (a, b) => parseInt(a.shirtNumber || 0) - parseInt(b.shirtNumber || 0),
      );

    return (
      <div
        className="flex flex-col border-t-2 pt-2"
        style={{ borderColor: rgbaFromColour(teamColour, 0.3) }}
      >
        {/* Header */}
        <div
          className="font-semibold text-sm mb-2 flex items-center gap-2 min-w-0"
          style={{ color: teamColour }}
        >
          <span className="font-mono shrink-0">{teamChar}</span>
          <span className="truncate min-w-0">{teamName}</span>
          <input
            type="color"
            className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer ml-auto shrink-0"
            value={toHex(teamColour)}
            onChange={(e) => setLocalColour(e.target.value)}
            title={t("Team colour")}
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-1 mb-2">
          <button
            className={`btn btn-xs btn-outline ${isSubs ? "opacity-40" : ""}`}
            disabled={isSubs}
            onClick={() => rotate("ccw")}
            title={
              isSubs
                ? t("Rotation is not permitted while making substitutions")
                : "Rotate CCW"
            }
          >
            ↺
          </button>
          <button
            className={`btn btn-xs btn-outline ${isSubs ? "opacity-40" : ""}`}
            disabled={isSubs}
            onClick={() => rotate("cw")}
            title={
              isSubs
                ? t("Rotation is not permitted while making substitutions")
                : "Rotate CW"
            }
          >
            ↻
          </button>
          <button
            className={`btn btn-xs btn-warning ${selected?.type === "court" ? "" : "opacity-40"}`}
            disabled={selected?.type !== "court"}
            onClick={() => {
              if (selected?.type === "court") {
                const p = court[selected.key];
                if (isCourtSlotFilled(p)) setLocalSetter(p.shirtNumber);
              }
              setSelected(null);
            }}
          >
            ★ S
          </button>
          <button
            className={`btn btn-xs btn-info ${selected?.type === "bench" && !courtNums.has(shirtKey(selected.key)) ? "" : "opacity-40"}`}
            disabled={
              selected?.type !== "bench" ||
              courtNums.has(shirtKey(selected.key))
            }
            onClick={() => {
              if (
                selected?.type === "bench" &&
                !courtNums.has(shirtKey(selected.key))
              ) {
                setLocalLibero(toggleLiberoNumber(localLibero, selected.key));
              }
              setSelected(null);
            }}
          >
            L Lib
          </button>
          {!isSubs && loadTeamStartingLineup(teamName) && (
            <button
              className="btn btn-xs btn-outline"
              title={t(
                "Load this team's last-saved starting lineup (positions, setter and liberos)",
              )}
              onClick={() => {
                applySavedLineup(side);
                setSelected(null);
              }}
            >
              ⤓ {t("Saved lineup")}
            </button>
          )}
          {isSubs && (
            <button
              className={`btn btn-xs ${doubleSub?.side === side ? "btn-accent" : "btn-outline"}`}
              disabled={localSetter == null}
              title={t(
                "Double Substitution — swap the Setter and Opposite for two incoming players",
              )}
              onClick={() => {
                if (doubleSub?.side === side) {
                  setDoubleSub(null);
                  return;
                }
                startDoubleSub();
              }}
            >
              ⇄⇄ {t("Double Sub")}
            </button>
          )}
          <button
            className="btn btn-xs btn-ghost ml-auto"
            onClick={() => {
              (isTeamA ? setCourtA : setCourtB)(
                LINEUP_COURT_ORDER.reduce((o, p) => ({ ...o, [p]: null }), {}),
              );
              setSelected(null);
            }}
          >
            ✕
          </button>
        </div>

        {doubleSub?.side === side && (
          <div className="text-[10px] text-accent mb-1 animate-pulse">
            {doubleSub.step === "setter"
              ? t(
                  "Double Sub: pick a bench player for the flashing SETTER slot…",
                )
              : t(
                  "Double Sub: now pick a bench player for the flashing OPPOSITE slot…",
                )}
          </div>
        )}

        {/* Court grid — 2 cols × 3 rows, front col faces the net (center of modal) */}
        <div
          className={`mb-2 flex ${isTeamA ? "justify-end" : "justify-start"}`}
        >
          <div className="grid grid-rows-3 grid-flow-col gap-1">
            {(flipped
              ? ["2", "3", "4", "1", "6", "5"] // Team B: P2/P1 top, P3/P6 middle, P4/P5 bottom
              : ["5", "6", "1", "4", "3", "2"]
            ) // Team A: back col left, front col right (near net)
              .map((pos) => (
                <CourtCell
                  key={pos}
                  pos={pos}
                  player={court[pos]}
                  setter={localSetter}
                  libero={localLibero}
                  isSelected={
                    selected?.type === "court" && selected.key === pos
                  }
                  onClick={() => clickCourt(pos)}
                  teamColour={teamColour}
                  isFocused={
                    focusedPos?.team === side && focusedPos?.pos === pos
                  }
                  inputHint={
                    focusedPos?.team === side && focusedPos?.pos === pos
                      ? inputBuffer
                      : ""
                  }
                  isError={
                    focusedPos?.team === side &&
                    focusedPos?.pos === pos &&
                    entryError
                  }
                  isDoubleSubTarget={
                    doubleSub?.side === side &&
                    pos ===
                      (doubleSub.step === "setter"
                        ? doubleSub.setterPos
                        : doubleSub.oppPos)
                  }
                />
              ))}
          </div>
        </div>

        {/* Player list — bench buttons for assigning a squad member to a
            court position (click a bench button, then a court slot). */}
        <div>
          <div className="text-[9px] text-base-content/40 mb-1">players</div>
          {allPlayers.length === 0 ? (
            <div className="text-[10px] text-base-content/40 italic">
              No roster loaded — edit court positions directly
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {allPlayers.map((p) => {
                const isOnCourt = courtNums.has(shirtKey(p.shirtNumber));
                const isSel =
                  selected?.type === "bench" && selected.key === p.shirtNumber;
                const isLiberoPlayer = isLiberoNumber(
                  localLibero,
                  p.shirtNumber,
                );
                const fullName =
                  [p.FirstName, p.LastName].filter(Boolean).join(" ") ||
                  p.NickName ||
                  "";
                const contrastText = contrastFor(teamColour);
                const btnStyle = {
                  backgroundColor: teamColour,
                  color: contrastText,
                  ...(isSel
                    ? {
                        outline: `3px solid ${contrastText}`,
                        outlineOffset: "-2px",
                      }
                    : {}),
                  ...(isLiberoPlayer && !isSel
                    ? {
                        outline: `2px dashed ${contrastText}`,
                        outlineOffset: "-2px",
                      }
                    : {}),
                };
                return (
                  <button
                    key={p.shirtNumber}
                    className={`btn btn-xs font-mono border-0 ${isOnCourt ? "opacity-40 cursor-not-allowed" : ""}`}
                    style={btnStyle}
                    disabled={isOnCourt}
                    onClick={() => !isOnCourt && clickBench(p.shirtNumber)}
                    title={
                      isOnCourt
                        ? `${fullName || p.shirtNumber} — on court`
                        : isLiberoPlayer
                          ? `${fullName || "#" + p.shirtNumber} (Libero)`
                          : fullName || `#${p.shirtNumber}`
                    }
                  >
                    {p.shirtNumber}
                    {isLiberoPlayer && !isOnCourt && (
                      <span className="text-[8px] ml-0.5">L</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Playing squad — who is actually part of today's match, in a
            collapsible dropdown below the player list. Reuses NewMatch.jsx's
            own <SquadPicker> (toggle on/off). Selections here only affect the
            "Player list" panel above (who is eligible to be assigned a court
            position) — they never ADD anyone to the lineup, but DO remove a
            player from the lineup/setter/libero the moment they're taken out
            of the squad (see the squadA/squadB effects near the top of
            MatchSetupModal). */}
        {allRosterPlayers.length > 0 && (
          <div className="mt-2">
            <button
              className="btn btn-xs btn-ghost w-full justify-between"
              onClick={() => setShowSquadPicker((v) => !v)}
            >
              <span>
                {t("Playing squad")}{" "}
                <span className="font-mono">
                  {squadSet.size}/{allRosterPlayers.length}
                </span>
              </span>
              <span>{showSquadPicker ? "▲" : "▼"}</span>
            </button>
            {showSquadPicker && (
              <SquadPicker
                players={allRosterPlayers}
                picks={squadSet}
                onChange={setSquadSet}
                onClose={() => setShowSquadPicker(false)}
                teamColour={teamColour}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const canStart =
    LINEUP_COURT_ORDER.every((p) => isCourtSlotFilled(courtA[p])) &&
    LINEUP_COURT_ORDER.every((p) => isCourtSlotFilled(courtB[p])) &&
    localSetterA != null &&
    localSetterB != null &&
    normalizeLiberoList(localLiberoA).length > 0 &&
    normalizeLiberoList(localLiberoB).length > 0;

  return (
    <dialog ref={dialogRef} className="modal" onKeyDown={handleModalKeyDown}>
      <div className="modal-box max-w-none w-[98vw] h-[96vh] flex flex-col p-4">
        <h3 className="font-bold text-lg mb-2 shrink-0">{t("Match Setup")}</h3>

        <div className="flex flex-1 min-h-0 gap-3">
          {/* Video panel — large so the coder can see the match clearly */}
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <div className="flex gap-1 items-center shrink-0">
              <input
                type="text"
                value={videoUrl ?? ""}
                onChange={(e) => onVideoUrlChange?.(e.target.value)}
                placeholder={t("Video URL…")}
                className="input input-bordered input-xs font-mono flex-1"
              />
              <label
                className="btn btn-xs btn-ghost cursor-pointer"
                title={t("Open local file")}
              >
                📁
                <input
                  type="file"
                  className="hidden"
                  accept="video/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onVideoUrlChange?.(URL.createObjectURL(f));
                  }}
                />
              </label>
            </div>
            <div
              ref={videoWrapRef}
              className="flex-1 min-h-0 bg-black rounded overflow-hidden relative"
            >
              {videoUrl ? (
                <>
                  <ReactPlayer
                    ref={modalPlayerRef}
                    url={videoUrl}
                    controls={false}
                    playing={videoPlaying}
                    playbackRate={videoRate}
                    muted={videoMuted}
                    progressInterval={200}
                    onPlay={() => setVideoPlaying(true)}
                    onPause={() => setVideoPlaying(false)}
                    onDuration={(d) => setVideoDuration(d)}
                    onProgress={({ playedSeconds }) => {
                      if (!isSeekBarDragging)
                        setVideoCurrentTime(playedSeconds);
                    }}
                    width="100%"
                    height="100%"
                  />
                  {/* Transparent capture layer — see the same overlay note on
                      the main coding-screen video panel: a YouTube/Vimeo/etc.
                      embed is a cross-origin iframe whose wheel/mouse events
                      never bubble out to an ancestor listener, so this overlay
                      (same-origin, on top) is what actually receives the
                      mousewheel/drag/tap gestures. Native controls are
                      disabled above since they'd be unreachable under this. */}
                  <div
                    className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing"
                    onPointerDown={onVideoPointerDown}
                    onPointerMove={onVideoPointerMove}
                    onPointerUp={onVideoPointerUp}
                    onPointerLeave={onVideoPointerUp}
                    {...swipeHandlers}
                  />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-base-content/40 text-sm">
                  {t("Select a match video to view while entering lineups.")}
                </div>
              )}
            </div>
            {/* Seek bar — current time / scrub range / duration */}
            {videoUrl && (
              <div className="flex items-center gap-2 px-1 shrink-0">
                <span className="text-[11px] tabular-nums text-base-content/60 w-10 text-right">
                  {formatVideoTime(videoCurrentTime)}
                </span>
                <input
                  type="range"
                  className="range range-xs flex-1"
                  min={0}
                  max={videoDuration > 0 ? videoDuration : 0.001}
                  step={0.1}
                  value={Math.min(videoCurrentTime, videoDuration || 0)}
                  onMouseDown={() => setIsSeekBarDragging(true)}
                  onTouchStart={() => setIsSeekBarDragging(true)}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setVideoCurrentTime(v);
                    modalPlayerRef.current?.seekTo(v, "seconds");
                  }}
                  onMouseUp={() => setIsSeekBarDragging(false)}
                  onTouchEnd={() => setIsSeekBarDragging(false)}
                />
                <span className="text-[11px] tabular-nums text-base-content/60 w-10">
                  {formatVideoTime(videoDuration)}
                </span>
              </div>
            )}
            {/* Video toolbar — seek / play / speed (same controls as Coding Page) */}
            {videoUrl && (
              <div className="flex gap-1 items-center p-1 bg-base-300 rounded shrink-0">
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="-10s"
                  onClick={() => seekVideo(-10)}
                >
                  <ChevronDoubleLeftIcon className="size-5" />
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="-1s"
                  onClick={() => seekVideo(-1)}
                >
                  <ChevronLeftIcon className="size-5" />
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title={videoPlaying ? t("Pause") : t("Play")}
                  onClick={() => setVideoPlaying((p) => !p)}
                >
                  {videoPlaying ? (
                    <PauseIcon className="size-5" />
                  ) : (
                    <PlayIcon className="size-5" />
                  )}
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="+1s"
                  onClick={() => seekVideo(1)}
                >
                  <ChevronRightIcon className="size-5" />
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title="+10s"
                  onClick={() => seekVideo(10)}
                >
                  <ChevronDoubleRightIcon className="size-5" />
                </button>
                <select
                  className="select select-xs select-bordered ml-1"
                  value={videoRate}
                  onChange={(e) => setVideoRate(parseFloat(e.target.value))}
                >
                  <option value="0.25">0.25x</option>
                  <option value="0.5">0.5x</option>
                  <option value="1">1x</option>
                  <option value="1.5">1.5x</option>
                  <option value="2">2x</option>
                  <option value="3">3x</option>
                  <option value="4">4x</option>
                </select>
                <span className="text-[11px] text-base-content/40 ml-2 truncate">
                  {t("Scroll, drag or swipe to scrub")}
                </span>
                <button
                  className="btn btn-xs btn-ghost px-1 ml-auto"
                  title={videoMuted ? t("Unmute") : t("Mute")}
                  onClick={() => setVideoMuted((m) => !m)}
                >
                  {videoMuted ? (
                    <SpeakerXMarkIcon className="size-5" />
                  ) : (
                    <SpeakerWaveIcon className="size-5" />
                  )}
                </button>
                <button
                  className="btn btn-xs btn-ghost px-1"
                  title={
                    isVideoFullscreen ? t("Exit fullscreen") : t("Fullscreen")
                  }
                  onClick={toggleVideoFullscreen}
                >
                  {isVideoFullscreen ? (
                    <ArrowsPointingInIcon className="size-5" />
                  ) : (
                    <ArrowsPointingOutIcon className="size-5" />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Setup controls — serving team + both lineups */}
          <div className="w-[520px] max-w-[45vw] shrink-0 overflow-y-auto pr-1">
            {/* Serving team */}
            <div className="flex items-center gap-3 mb-4 p-2 rounded bg-base-200">
              <span className="text-sm font-medium">{t("First serve")}:</span>
              <button
                className={`btn btn-sm max-w-[45%] ${localServing === "A" ? "" : "btn-ghost"}`}
                style={
                  localServing === "A"
                    ? {
                        backgroundColor: localColourA,
                        color: contrastFor(localColourA),
                        borderColor: localColourA,
                      }
                    : undefined
                }
                onClick={() => setLocalServing("A")}
              >
                <span className="truncate">{teamAName}</span>
              </button>
              <button
                className={`btn btn-sm max-w-[45%] ${localServing === "B" ? "" : "btn-ghost"}`}
                style={
                  localServing === "B"
                    ? {
                        backgroundColor: localColourB,
                        color: contrastFor(localColourB),
                        borderColor: localColourB,
                      }
                    : undefined
                }
                onClick={() => setLocalServing("B")}
              >
                <span className="truncate">{teamBName}</span>
              </button>
            </div>

            {/* Both team panels — courts face each other with net in the middle */}
            <div className="flex items-stretch gap-0">
              <div className="flex-1 min-w-0">{renderTeamPanel("A")}</div>
              <div className="w-px self-stretch bg-base-content/20 mx-2"></div>
              <div className="flex-1 min-w-0">{renderTeamPanel("B")}</div>
            </div>
          </div>
        </div>

        <div className="modal-action mt-3 shrink-0">
          <button
            className="btn btn-primary btn-sm"
            onClick={handleStart}
            disabled={!canStart}
            title={
              canStart
                ? ""
                : "Fill all 6 positions and assign a setter & libero for each team"
            }
          >
            {isSubs ? `${t("Resume Match")} ▶` : `${t("Start Match")} ▶`}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => dialogRef.current?.close()}
          >
            {t("Skip")}
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
}

// Formats a seconds value as "m:ss" (or "h:mm:ss" once past an hour) for the
// video seek bar's time labels.
function formatVideoTime(seconds) {
  const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

// Normalise any CSS colour (named or hex) to a "#rrggbb" hex string so it can
// be used as the value of a native <input type="color">.
function toHex(colour) {
  if (!colour) return "#000000";
  if (/^#[0-9a-f]{6}$/i.test(colour)) return colour.toLowerCase();
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.fillStyle = colour;
  return /^#[0-9a-f]{6}$/i.test(ctx.fillStyle) ? ctx.fillStyle : "#000000";
}

// Returns black or white depending on which has better contrast against `colour`.
function contrastFor(colour) {
  if (!colour) return "#ffffff";
  const c = colour.trim();
  const m = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const r = parseInt(h.slice(0, 2), 16),
      g = parseInt(h.slice(2, 4), 16),
      b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55
      ? "#000000"
      : "#ffffff";
  }
  const light = new Set([
    "white",
    "yellow",
    "lime",
    "cyan",
    "aqua",
    "silver",
    "ivory",
    "snow",
    "cornsilk",
    "linen",
    "oldlace",
    "floralwhite",
    "seashell",
    "lightyellow",
    "lightcyan",
    "lightgreen",
    "palegreen",
    "mintcream",
    "honeydew",
    "aliceblue",
    "lavender",
    "lightblue",
    "lightpink",
    "lightsalmon",
    "gold",
    "orange",
    "khaki",
    "wheat",
    "bisque",
    "moccasin",
    "peachpuff",
    "mistyrose",
    "greenyellow",
    "yellowgreen",
  ]);
  return light.has(c.toLowerCase()) ? "#000000" : "#ffffff";
}

// Renders any CSS colour as an "rgba(r, g, b, alpha)" string — lets us
// reproduce daisyUI's `bg-primary/25`, `ring-primary/70`-style translucent
// accents using each team's OWN configured colour instead of the theme's
// fixed primary/secondary colours.
function rgbaFromColour(colour, alpha) {
  const hex = toHex(colour);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function CourtCell({
  pos,
  player,
  setter,
  libero,
  isSelected,
  onClick,
  teamColour,
  isFocused,
  inputHint,
  isError,
  isDoubleSubTarget,
}) {
  const hasNumber =
    player != null && player.shirtNumber != null && player.shirtNumber !== "";
  const isSetter =
    hasNumber &&
    setter != null &&
    setter !== "" &&
    player.shirtNumber === setter;
  const isLibero = hasNumber && isLiberoNumber(libero, player.shirtNumber);
  const tc = teamColour ?? "#888";
  const contrast = contrastFor(tc);
  let cellStyle;
  let addedCls;
  // Filled slots always use the exact, fully-opaque team colour as the
  // background (no transparency) — selection/focus are communicated purely
  // via the outline so the team colour itself is never washed out.
  if (isDoubleSubTarget) {
    // Double Substitution flow: flash this slot (distinct fuchsia colour,
    // whole-cell pulse) to draw the eye to whichever slot — setter or
    // opposite — needs a bench replacement next.
    cellStyle = player
      ? {
          backgroundColor: tc,
          outline: "3px solid #d946ef",
          outlineOffset: "-2px",
        }
      : {
          backgroundColor: "rgba(217,70,239,0.15)",
          outline: "3px solid #d946ef",
          outlineOffset: "-2px",
        };
    addedCls = "cursor-pointer animate-pulse";
  } else if (isSelected) {
    cellStyle = player
      ? {
          backgroundColor: tc,
          outline: `3px solid ${contrast}`,
          outlineOffset: "-2px",
        }
      : {
          backgroundColor: "rgba(255,255,255,0.08)",
          outline: `3px solid ${tc}`,
          outlineOffset: "-2px",
        };
    addedCls = "cursor-pointer";
  } else if (isFocused) {
    const focusColour = isError ? "#ef4444" : "#facc15"; // red on error, amber to draw the eye to focus
    cellStyle = player
      ? {
          backgroundColor: tc,
          outline: `3px ${isError ? "solid" : "dashed"} ${focusColour}`,
          outlineOffset: "-2px",
        }
      : {
          backgroundColor: isError
            ? "rgba(239,68,68,0.15)"
            : "rgba(250,204,21,0.15)",
          outline: `3px ${isError ? "solid" : "dashed"} ${focusColour}`,
          outlineOffset: "-2px",
        };
    addedCls = "cursor-pointer";
  } else if (player) {
    cellStyle = { backgroundColor: tc };
    addedCls = "cursor-pointer hover:brightness-110";
  } else {
    cellStyle = { border: "1px dashed rgba(128,128,128,0.3)" };
    addedCls = "cursor-default";
  }
  // While typing a jersey number into a focused slot, make the digits
  // themselves flash (not just the trailing cursor) so it's unmistakable
  // which slot currently has keyboard focus.
  const displayNum = inputHint ? (
    isError ? (
      <span className="font-mono font-bold animate-pulse px-1 rounded bg-red-600 text-white">
        {inputHint}
      </span>
    ) : (
      <span
        className="font-mono font-bold animate-pulse px-1 rounded"
        style={{
          color: player ? contrast : "#facc15",
          backgroundColor: player ? "rgba(250,204,21,0.45)" : "transparent",
        }}
      >
        {inputHint}
        <span className="opacity-70">▌</span>
      </span>
    )
  ) : player ? (
    player.shirtNumber
  ) : (
    "—"
  );
  const numStyle = player ? { color: contrast } : {};
  const badgeStyle = {
    backgroundColor: contrast,
    color: tc,
    lineHeight: "1.3",
  };
  return (
    <div
      className={`rounded text-center select-none transition-all w-16 h-14 flex flex-col items-center justify-center ${addedCls}`}
      style={cellStyle}
      onClick={onClick}
    >
      <div className="text-[10px] text-base-content/40">
        P{pos}
        {pos === "1" ? " srv" : ""}
      </div>
      <div className="text-base font-semibold leading-none" style={numStyle}>
        {displayNum}
      </div>
      {isSetter && (
        <div
          className="text-[8px] font-bold mt-0.5 px-1 rounded"
          style={badgeStyle}
        >
          S
        </div>
      )}
      {isLibero && !isSetter && (
        <div
          className="text-[8px] font-bold mt-0.5 px-1 rounded"
          style={badgeStyle}
        >
          L
        </div>
      )}
    </div>
  );
}

// ─── ScoutingDefaultsModal ────────────────────────────────────────────────────
// Standalone modal (no Firestore) for overriding default skill, type, and eval
// per skill, and for re-binding the three keyboard shortcuts.
const SKILL_OPTS = ["A", "S", "R", "E", "B", "D", "F"];
const TYPE_OPTS = {
  A: ["H", "Q", "T", "P", "N", "O", ""],
  S: ["Q", "H", "M", "O", ""],
  R: ["H", "V", ""],
  E: ["V", ""],
  B: [""],
  D: [""],
  F: [""],
  P: [""],
};
const EVAL_OPTS = ["#", "+", "!", "-", "/", "="];

function ScoutingDefaultsModal({
  dialogRef,
  defaults,
  onSave,
  shortcuts,
  onSaveShortcuts,
}) {
  const [skill, setSkill] = useState(defaults.skill);
  const [typeMap, setTypeMap] = useState({ ...DEFAULT_TYPE, ...defaults.type });
  const [evalMap, setEvalMap] = useState({ ...DEFAULT_EVAL, ...defaults.eval });
  const [videoLeadTime, setVideoLeadTime] = useState(
    defaults.videoLeadTime ?? DEFAULT_VIDEO_LEAD_TIME,
  );
  const [maxSubs, setMaxSubs] = useState(defaults.maxSubs ?? DEFAULT_MAX_SUBS);
  const [freeSubstitutions, setFreeSubstitutions] = useState(
    !!defaults.freeSubstitutions,
  );
  const [setWinScore, setSetWinScore] = useState(
    defaults.setWinScore ?? DEFAULT_SET_WIN_SCORE,
  );
  const [finalSetWinScore, setFinalSetWinScore] = useState(
    defaults.finalSetWinScore ?? DEFAULT_FINAL_SET_WIN_SCORE,
  );
  const [winByTwo, setWinByTwo] = useState(
    defaults.winByTwo ?? DEFAULT_WIN_BY_TWO,
  );
  const [scMap, setScMap] = useState({ ...DEFAULT_SHORTCUTS, ...shortcuts });

  // Sync when parent changes (e.g. reset)
  useEffect(() => {
    setSkill(defaults.skill);
    setTypeMap({ ...DEFAULT_TYPE, ...defaults.type });
    setEvalMap({ ...DEFAULT_EVAL, ...defaults.eval });
    setVideoLeadTime(defaults.videoLeadTime ?? DEFAULT_VIDEO_LEAD_TIME);
    setMaxSubs(defaults.maxSubs ?? DEFAULT_MAX_SUBS);
    setFreeSubstitutions(!!defaults.freeSubstitutions);
    setSetWinScore(defaults.setWinScore ?? DEFAULT_SET_WIN_SCORE);
    setFinalSetWinScore(defaults.finalSetWinScore ?? DEFAULT_FINAL_SET_WIN_SCORE);
    setWinByTwo(defaults.winByTwo ?? DEFAULT_WIN_BY_TWO);
  }, [defaults]);
  useEffect(() => {
    setScMap({ ...DEFAULT_SHORTCUTS, ...shortcuts });
  }, [shortcuts]);

  const handleSave = () => {
    onSave({
      skill,
      type: typeMap,
      eval: evalMap,
      videoLeadTime,
      maxSubs,
      freeSubstitutions,
      setWinScore,
      finalSetWinScore,
      winByTwo,
    });
    onSaveShortcuts(scMap);
    dialogRef.current?.close();
  };

  const handleReset = () => {
    setSkill(DEFAULT_SKILL);
    setTypeMap({ ...DEFAULT_TYPE });
    setEvalMap({ ...DEFAULT_EVAL });
    setVideoLeadTime(DEFAULT_VIDEO_LEAD_TIME);
    setMaxSubs(DEFAULT_MAX_SUBS);
    setFreeSubstitutions(DEFAULT_FREE_SUBS);
    setSetWinScore(DEFAULT_SET_WIN_SCORE);
    setFinalSetWinScore(DEFAULT_FINAL_SET_WIN_SCORE);
    setWinByTwo(DEFAULT_WIN_BY_TWO);
    setScMap({ ...DEFAULT_SHORTCUTS });
  };

  const updateSc = (action, field, value) => {
    setScMap((prev) => ({
      ...prev,
      [action]: { ...prev[action], [field]: value },
    }));
  };

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-3">
          Coding defaults &amp; shortcuts
        </h3>

        {/* Default skill */}
        <div className="mb-3">
          <label className="label label-text font-semibold">
            Default skill (when omitted)
          </label>
          <div className="join">
            {SKILL_OPTS.map((s) => (
              <button
                key={s}
                className={`btn btn-xs join-item ${skill === s ? "btn-primary" : ""}`}
                onClick={() => setSkill(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Default type / eval per skill */}
        <div className="mb-3 overflow-x-auto">
          <table className="table table-xs w-full">
            <thead>
              <tr>
                <th>Skill</th>
                <th>Default type</th>
                <th>Default eval</th>
              </tr>
            </thead>
            <tbody>
              {SKILL_OPTS.map((s) => (
                <tr key={s}>
                  <td className="font-mono font-bold">{s}</td>
                  <td>
                    <select
                      className="select select-xs select-bordered"
                      value={typeMap[s] ?? ""}
                      onChange={(e) =>
                        setTypeMap((p) => ({ ...p, [s]: e.target.value }))
                      }
                    >
                      {(TYPE_OPTS[s] ?? [""]).map((t) => (
                        <option key={t} value={t}>
                          {t === "" ? "(none)" : t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="select select-xs select-bordered"
                      value={evalMap[s] ?? "+"}
                      onChange={(e) =>
                        setEvalMap((p) => ({ ...p, [s]: e.target.value }))
                      }
                    >
                      {EVAL_OPTS.map((ev) => (
                        <option key={ev} value={ev}>
                          {ev}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Video lead time */}
        <div className="mb-3">
          <label className="label label-text font-semibold">
            Video lead time (seconds before event)
          </label>
          <input
            type="number"
            min={0}
            max={60}
            step={0.5}
            className="input input-xs input-bordered w-24"
            value={videoLeadTime}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setVideoLeadTime(Number.isFinite(v) && v >= 0 ? v : 0);
            }}
          />
        </div>

        {/* Substitutions */}
        <div className="mb-3">
          <label className="label label-text font-semibold">
            Max substitutions per set
          </label>
          <input
            type="number"
            min={0}
            max={12}
            step={1}
            className="input input-xs input-bordered w-24"
            value={maxSubs}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setMaxSubs(Number.isFinite(v) && v >= 0 ? v : 0);
            }}
          />
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-xs"
              checked={freeSubstitutions}
              onChange={(e) => setFreeSubstitutions(e.target.checked)}
            />
            <span className="text-sm">
              Free substitutions (ignore FIVB pair / re-entry rules)
            </span>
          </label>
        </div>

        {/* Set scoring */}
        <div className="mb-3">
          <label className="label label-text font-semibold">
            Points to win a set (sets 1-4)
          </label>
          <input
            type="number"
            min={1}
            max={99}
            step={1}
            className="input input-xs input-bordered w-24"
            value={setWinScore}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setSetWinScore(Number.isFinite(v) && v > 0 ? v : DEFAULT_SET_WIN_SCORE);
            }}
          />
          <label className="label label-text font-semibold mt-2">
            Points to win the deciding set (5th)
          </label>
          <input
            type="number"
            min={1}
            max={99}
            step={1}
            className="input input-xs input-bordered w-24"
            value={finalSetWinScore}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setFinalSetWinScore(
                Number.isFinite(v) && v > 0 ? v : DEFAULT_FINAL_SET_WIN_SCORE,
              );
            }}
          />
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-xs"
              checked={winByTwo}
              onChange={(e) => setWinByTwo(e.target.checked)}
            />
            <span className="text-sm">
              Require a 2-point winning margin (uncheck for sudden-death / no-advantage)
            </span>
          </label>
          <p className="text-xs text-base-content/50 mt-1">
            A set is also always closed early with the "Close Set" button
            next to the scoreboard, regardless of these settings.
          </p>
        </div>

        {/* Keyboard shortcuts */}
        <div className="mb-3">
          <div className="font-semibold mb-1">Keyboard shortcuts</div>
          {Object.keys(SHORTCUT_LABELS).map((action) => {
            const def = scMap[action] ?? DEFAULT_SHORTCUTS[action];
            return (
              <div
                key={action}
                className="flex items-center gap-2 mb-1 text-sm"
              >
                <span className="w-32 text-base-content/70">
                  {SHORTCUT_LABELS[action]}
                </span>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs"
                    checked={!!def.ctrl}
                    onChange={(e) => updateSc(action, "ctrl", e.target.checked)}
                  />
                  Ctrl
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs"
                    checked={!!def.shift}
                    onChange={(e) =>
                      updateSc(action, "shift", e.target.checked)
                    }
                  />
                  Shift
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs"
                    checked={!!def.alt}
                    onChange={(e) => updateSc(action, "alt", e.target.checked)}
                  />
                  Alt
                </label>
                <input
                  type="text"
                  maxLength={1}
                  className="input input-xs input-bordered w-10 font-mono text-center"
                  value={def.key}
                  onChange={(e) => updateSc(action, "key", e.target.value)}
                />
                <span className="text-base-content/50 text-xs">
                  = <kbd className="kbd kbd-xs">{formatShortcut(def)}</kbd>
                </span>
              </div>
            );
          })}
        </div>

        <div className="modal-action">
          <button className="btn btn-xs btn-ghost" onClick={handleReset}>
            Reset to defaults
          </button>
          <button
            className="btn btn-sm"
            onClick={() => dialogRef.current?.close()}
          >
            Cancel
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
}

export default DVMatchCoding;
